/**
 * Checks if a lazy load image has alternate <source> elements and copies the
 * 'data-src' and 'data-srcset' attributes to 'src' and 'srcset' accordingly.
 * @param {Element} img - Image element.
 */
function setImageSources(img) {
  const setImageAttr = (el) => {
    if (el.dataset.src && !el.src) {
      el.src = el.dataset.src;
    }

    if (el.dataset.srcset && !el.srcset) {
      el.srcset = el.dataset.srcset;
    }
  };

  if (img.parentNode.tagName === "PICTURE") {
    Array.from(img.parentNode.children).forEach((el) => {
      setImageAttr(el);
    });
  } else {
    setImageAttr(img);
  }
}

/**
 * Initialises lazy load images.
 */
function initLazyImages() {
  if (
    "loading" in HTMLImageElement.prototype === false &&
    "IntersectionObserver" in window
  ) {
    // If native lazyload not supported but IntersectionObserver supported (Safari).
    const io = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const img = entry.target;
            setImageSources(img);
            observer.unobserve(img);
          }
        });
      },
      { rootMargin: "0px 0px 500px 0px" }
    );

    document.querySelectorAll('[loading="lazy"]').forEach((img) => {
      io.observe(img);
    });
  } else {
    // If native lazy load supported or IntersectionObserver not supported (legacy browsers).
    document.querySelectorAll('[loading="lazy"]').forEach((img) => {
      setImageSources(img);
    });
  }
}

/**
 * Adds an observer to initialise a script when an element is scrolled into view.
 * @param {Element} element - Element to observe.
 * @param {Function} callback - Function to call when element is scrolled into view.
 * @param {number} [threshold=500] - Distance from viewport (in pixels) to trigger init.
 */
function initLazyScript(element, callback, threshold = 500) {
  if ("IntersectionObserver" in window) {
    const io = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (typeof callback === "function") {
              callback();
              observer.unobserve(entry.target);
            }
          }
        });
      },
      { rootMargin: `0px 0px ${threshold}px 0px` }
    );

    io.observe(element);
  } else {
    callback();
  }
}

theme.throttleEvent = (callback, threshold) => {
  let debounceTimeoutId = -1;
  let tick = false;

  return () => {
    clearTimeout(debounceTimeoutId);
    debounceTimeoutId = setTimeout(callback, threshold);

    if (!tick) {
      callback.call();
      tick = true;
      setTimeout(() => {
        tick = false;
      }, threshold);
    }
  };
};

theme.stickyHeaderHeight = () => {
  const v = getComputedStyle(document.documentElement).getPropertyValue(
    "--theme-sticky-header-height"
  );
  if (v) {
    return parseInt(v, 10) || 0;
  }
  return 0;
};

theme.getOffsetTopFromDoc = (el) =>
  el.getBoundingClientRect().top + window.scrollY;

theme.getOffsetLeftFromDoc = (el) =>
  el.getBoundingClientRect().left + window.scrollX;

theme.getScrollParent = (node) => {
  const isElement = node instanceof HTMLElement;
  const overflowY = isElement && window.getComputedStyle(node).overflowY;
  const isScrollable = overflowY !== "visible" && overflowY !== "hidden";

  if (!node) {
    return null;
  }

  if (isScrollable && node.scrollHeight > node.clientHeight) {
    return node;
  }

  return (
    theme.getScrollParent(node.parentNode) ||
    document.scrollingElement ||
    window
  );
};

theme.scrollToRevealElement = (el) => {
  const scrollContainer = theme.getScrollParent(el);
  const scrollTop =
    scrollContainer === window ? window.scrollY : scrollContainer.scrollTop;
  const scrollVisibleHeight =
    scrollContainer === window
      ? window.innerHeight
      : scrollContainer.clientHeight;
  const elTop = theme.getOffsetTopFromDoc(el);
  const elBot = elTop + el.offsetHeight;
  const inViewTop = scrollTop + theme.stickyHeaderHeight();
  const inViewBot = scrollTop + scrollVisibleHeight - 50;

  if (elTop < inViewTop || elBot > inViewBot) {
    scrollContainer.scrollTo({
      top: elTop - 100 - theme.stickyHeaderHeight(),
      left: 0,
      behavior: "smooth",
    });
  }
};

theme.getEmptyOptionSelectors = (formContainer) => {
  const emptySections = [];

  formContainer
    .querySelectorAll('[data-selector-type="dropdown"].option-selector')
    .forEach((el) => {
      if (
        !el.querySelector(
          '[aria-selected="true"][data-value]:not([data-value=""])'
        )
      ) {
        emptySections.push(el);
      }
    });

  formContainer
    .querySelectorAll('[data-selector-type="listed"].option-selector')
    .forEach((el) => {
      if (!el.querySelector("input:checked")) {
        emptySections.push(el);
      }
    });

  return emptySections;
};

theme.suffixIds = (container, prefix) => {
  const suffixCandidates = ["id", "for", "aria-describedby", "aria-controls"];
  for (let i = 0; i < suffixCandidates.length; i += 1) {
    container
      .querySelectorAll(`[${suffixCandidates[i]}]`)
      .forEach((el) =>
        el.setAttribute(
          suffixCandidates[i],
          el.getAttribute(suffixCandidates[i]) + prefix
        )
      );
  }
};

theme.addDelegateEventListener = (
  element,
  eventName,
  selector,
  callback,
  addEventListenerParams = null
) => {
  const cb = (evt) => {
    const el = evt.target.closest(selector);
    if (!el) return;
    if (!element.contains(el)) return;
    callback.call(el, evt, el);
  };
  element.addEventListener(eventName, cb, addEventListenerParams);
  return cb;
};

theme.hideAndRemove = (el) => {
  // disable
  el.querySelectorAll("input").forEach((input) => {
    input.disabled = true;
  });

  // wrap
  const wrapper = document.createElement("div");
  wrapper.className = "merge-remove-wrapper";
  el.parentNode.insertBefore(wrapper, el);
  wrapper.appendChild(el);
  el.classList.add("merge-remove-item");
  wrapper.style.height = `${wrapper.clientHeight}px`;

  const cs = getComputedStyle(el);
  const fadeDuration =
    parseFloat(cs.getPropertyValue("--fade-duration")) * 1000;
  const slideDuration =
    parseFloat(cs.getPropertyValue("--slide-duration")) * 1000;

  setTimeout(() => {
    wrapper.classList.add("merge-remove-wrapper--fade");

    setTimeout(() => {
      wrapper.classList.add("merge-remove-wrapper--slide");

      setTimeout(() => wrapper.remove(), slideDuration);
    }, fadeDuration);
  }, 10);
};

theme.insertAndReveal = (el, target, iaeCmd, delay) => {
  const initialDelay = delay || 10;
  el.classList.add("merge-add-wrapper");
  target.insertAdjacentElement(iaeCmd, el);
  el.style.height = `${el.firstElementChild.clientHeight}px`;

  const cs = getComputedStyle(el);
  const fadeDuration =
    parseFloat(cs.getPropertyValue("--fade-duration")) * 1000;
  const slideDuration =
    parseFloat(cs.getPropertyValue("--slide-duration")) * 1000;

  setTimeout(() => {
    el.classList.add("merge-add-wrapper--slide");

    setTimeout(() => {
      el.classList.add("merge-add-wrapper--fade");

      setTimeout(() => {
        // tidy up
        el.style.height = "";
        el.classList.remove(
          "merge-add-wrapper",
          "merge-add-wrapper--slide",
          "merge-add-wrapper--fade"
        );
      }, fadeDuration);
    }, slideDuration);
  }, initialDelay);
};

theme.mergeNodes = (newContent, targetContainer) => {
  try {
    // merge: replace content if changed
    newContent.querySelectorAll("[data-merge]").forEach((newEl) => {
      const targetEl = targetContainer.querySelector(
        `[data-merge="${newEl.dataset.merge}"]`
      );
      if (
        !newEl.dataset.mergeCache ||
        !targetEl.dataset.mergeCache ||
        newEl.dataset.mergeCache !== targetEl.dataset.mergeCache
      ) {
        targetEl.innerHTML = newEl.innerHTML;
        if (newEl.dataset.mergeCache || targetEl.dataset.mergeCache) {
          targetEl.dataset.mergeCache = newEl.dataset.mergeCache;
        }
      }
    });
    // merge: attributes only
    newContent.querySelectorAll("[data-merge-attributes]").forEach((newEl) => {
      const targetEl = targetContainer.querySelector(
        `[data-merge-attributes="${newEl.dataset.mergeAttributes}"]`
      );
      const newElAttributeNames = newEl.getAttributeNames();
      for (let i = 0; i < newElAttributeNames.length; i += 1) {
        const attributeName = newElAttributeNames[i];
        targetEl.setAttribute(attributeName, newEl.getAttribute(attributeName));
      }
    });
    // merge: insert/remove/replace in list
    newContent.querySelectorAll("[data-merge-list]").forEach((newList) => {
      const targetList = targetContainer.querySelector(
        `[data-merge-list="${newList.dataset.mergeList}"]`
      );
      let targetListItems = Array.from(
        targetList.querySelectorAll("[data-merge-list-item]")
      );
      const newListItems = Array.from(
        newList.querySelectorAll("[data-merge-list-item]")
      );

      // Cart drawer: a discount or line-key change must not animate every
      // remaining product out and back in when only one product was removed.
      const isCartDrawerList = targetList.id === "mak-cart-body";
      const cartMatches = isCartDrawerList ? new Map() : null;
      if (cartMatches) {
        const unmatchedOld = [...targetListItems].filter(
          (item) => !item.classList.contains("merge-remove-item")
        );
        const match = (newItem, index) => {
          if (index !== -1) {
            cartMatches.set(newItem, unmatchedOld.splice(index, 1)[0]);
          }
        };
        // Prefer exact keys; the stable identity is only a fallback.
        newListItems.forEach((item) => {
          match(item, unmatchedOld.findIndex(
            (oldItem) => oldItem.dataset.mergeListItem === item.dataset.mergeListItem
          ));
        });
        newListItems.forEach((item) => {
          if (!cartMatches.has(item) && item.dataset.cartStableId) {
            match(item, unmatchedOld.findIndex(
              (oldItem) => oldItem.dataset.cartStableId === item.dataset.cartStableId
            ));
          }
        });
      }
      const keptCartItems = cartMatches ? new Set(cartMatches.values()) : null;

      // remove
      targetListItems.forEach((targetListItem) => {
        // eslint-disable-next-line max-len
        const matchedItem = newListItems.find(
          (item) =>
            item.dataset.mergeListItem === targetListItem.dataset.mergeListItem
        );
        if (keptCartItems ? !keptCartItems.has(targetListItem) : !matchedItem) {
          if (!targetListItem.classList.contains("merge-remove-item")) {
            theme.hideAndRemove(targetListItem);
          }
        }
      });

      // rebuild target list excluding removed items
      targetListItems = Array.from(
        targetList.querySelectorAll(
          "[data-merge-list-item]:not(.merge-remove-item)"
        )
      );

      for (let i = 0; i < newListItems.length; i += 1) {
        const newListItem = newListItems[i];
        // eslint-disable-next-line max-len
        const matchedItem = cartMatches
          ? cartMatches.get(newListItem)
          : targetListItems.find(
            (item) =>
              item.dataset.mergeListItem === newListItem.dataset.mergeListItem
          );
        if (matchedItem) {
          // Replace content only if changed, keeping the existing product image
          // mounted on cart refreshes so it does not flash or reload.
          if (
            matchedItem.dataset.mergeListItem !== newListItem.dataset.mergeListItem ||
            !newListItem.dataset.mergeCache ||
            !matchedItem.dataset.mergeCache ||
            newListItem.dataset.mergeCache !== matchedItem.dataset.mergeCache
          ) {
            if (cartMatches) {
              const oldImage = matchedItem.querySelector(".cart-item__image");
              const newImage = newListItem.querySelector(".cart-item__image");
              const oldLink = oldImage?.querySelector("a")?.getAttribute("href");
              const newLink = newImage?.querySelector("a")?.getAttribute("href");
              if (oldImage && newImage && oldLink && oldLink === newLink) {
                newImage.replaceWith(oldImage);
              }
              matchedItem.replaceChildren(...newListItem.childNodes);
            } else {
              matchedItem.innerHTML = newListItem.innerHTML;
            }
            if (newListItem.dataset.mergeCache) {
              matchedItem.dataset.mergeCache = newListItem.dataset.mergeCache;
            }
          }
          if (cartMatches) {
            matchedItem.dataset.mergeListItem = newListItem.dataset.mergeListItem;
            matchedItem.dataset.cartStableId = newListItem.dataset.cartStableId;
          }
        } else {
          // add
          if (i === 0) {
            // first place
            theme.insertAndReveal(newListItem, targetList, "afterbegin", 500);
          } else if (i >= targetListItems.length) {
            // at end
            theme.insertAndReveal(newListItem, targetList, "beforeend", 500);
          } else {
            // before element currently at that index
            theme.insertAndReveal(
              newListItem,
              targetListItems[i],
              "beforebegin",
              500
            );
          }
          // update target list
          targetListItems.splice(i, 0, newListItem);
        }
      }
    });
  } catch (ex) {
    window.location.reload();
  }
};

// Show a short-lived text popup above an element
theme.showQuickPopup = (message, origin) => {
  const offsetLeft = theme.getOffsetLeftFromDoc(origin);
  const offsetTop = theme.getOffsetTopFromDoc(origin);
  const originLeft = origin.getBoundingClientRect().left;
  const popup = document.createElement("div");
  popup.className = "simple-popup simple-popup--hidden";
  popup.innerHTML = message;
  popup.style.left = `${offsetLeft}px`;
  popup.style.top = `${offsetTop}px`;

  document.body.appendChild(popup);

  let marginLeft = -(popup.clientWidth - origin.clientWidth) / 2;
  if (originLeft + marginLeft < 0) {
    // Pull it away from the left edge of the screen
    marginLeft -= originLeft + marginLeft - 2;
  }
  // Pull from right edge + small gap
  const offsetRight = offsetLeft + marginLeft + popup.clientWidth + 5;
  if (offsetRight > window.innerWidth) {
    marginLeft -= offsetRight - window.innerWidth;
  }
  popup.style.marginTop = -popup.clientHeight - 10;
  popup.style.marginLeft = marginLeft;
  setTimeout(() => {
    popup.classList.remove("simple-popup--hidden");
  }, 10);
  setTimeout(() => {
    popup.classList.add("simple-popup--hidden");
  }, 3500);
  setTimeout(() => {
    popup.remove();
  }, 4000);
};

theme.manuallyLoadImages = (container) => {
  container.querySelectorAll("img[data-manual-src]").forEach((el) => {
    el.src = el.dataset.manualSrc;
    el.removeAttribute("data-manual-src");
    if (el.dataset.manualSrcset) {
      el.srcset = el.dataset.manualSrcset;
      el.removeAttribute("data-manual-srcset");
    }
  });
};

theme.whenComponentLoaded = (component, callback) => {
  const components =
    Symbol.iterator in Object(component) ? [...component] : [component];
  if (!components.find((c) => !c.hasAttribute("loaded"))) {
    callback();
    return;
  }
  const onMutation = (mutationList, observer) => {
    for (let i = 0; i < mutationList.length; i += 1) {
      const mutation = mutationList[i];
      if (mutation.type === "attributes") {
        if (!components.find((c) => !c.hasAttribute("loaded"))) {
          observer.disconnect();
          callback.call();
        }
      }
    }
  };
  const observer = new MutationObserver(onMutation);
  components.forEach((c) =>
    observer.observe(c, { attributes: true, attributeFilter: ["loaded"] })
  );
};

/**
 * Returns a function that as long as it continues to be invoked, won't be triggered.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Delay (in milliseconds).
 * @returns {Function}
 */
function debounce(fn, wait = 300) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

window.addEventListener(
  "resize",
  debounce(() => {
    window.dispatchEvent(new CustomEvent("on:debounced-resize"));
  })
);

/**
 * Creates a 'mediaMatches' object from the media queries specified in the theme,
 * and adds listeners for each media query. If a breakpoint is crossed, the mediaMatches
 * values are updated and a 'on:breakpoint-change' event is dispatched.
 */
(() => {
  const { mediaQueries } = theme;
  if (!mediaQueries) return;

  const mqKeys = Object.keys(mediaQueries);
  const mqLists = {};
  theme.mediaMatches = {};

  /**
   * Handles a media query (breakpoint) change.
   */
  const handleMqChange = () => {
    const newMatches = mqKeys.reduce((acc, media) => {
      acc[media] = !!(mqLists[media] && mqLists[media].matches);
      return acc;
    }, {});

    // Update mediaMatches values after breakpoint change.
    Object.keys(newMatches).forEach((key) => {
      theme.mediaMatches[key] = newMatches[key];
    });

    window.dispatchEvent(new CustomEvent("on:breakpoint-change"));
  };

  mqKeys.forEach((mq) => {
    // Create mqList object for each media query.
    mqLists[mq] = window.matchMedia(mediaQueries[mq]);

    // Get initial matches for each query.
    theme.mediaMatches[mq] = mqLists[mq].matches;

    // Add an event listener to each query.
    try {
      mqLists[mq].addEventListener("change", handleMqChange);
    } catch (err1) {
      // Fallback for legacy browsers (Safari < 14).
      mqLists[mq].addListener(handleMqChange);
    }
  });
})();

/**
 * Sets a 'viewport-height' custom property on the root element.
 */
function setViewportHeight() {
  document.documentElement.style.setProperty(
    "--viewport-height",
    `${window.innerHeight}px`
  );
}

/**
 * Sets a 'header-height' custom property on the root element.
 */
function setHeaderHeight() {
  const header = document.getElementById("shopify-section-header");
  if (!header) return;
  let height = header.offsetHeight;

  // Add announcement bar height (if shown).
  const announcement = document.getElementById("shopify-section-announcement");
  if (announcement) height += announcement.offsetHeight;

  document.documentElement.style.setProperty("--header-height", `${height}px`);
}

/**
 * Sets a 'scrollbar-width' custom property on the root element.
 */
function setScrollbarWidth() {
  document.documentElement.style.setProperty(
    "--scrollbar-width",
    `${window.innerWidth - document.documentElement.clientWidth}px`
  );
}

/**
 * Sets the dimension variables.
 */
function setDimensionVariables() {
  setViewportHeight();
  setHeaderHeight();
  setScrollbarWidth();
}

// Set the dimension variables.
setDimensionVariables();

// Update the dimension variables if viewport resized.
window.addEventListener("resize", debounce(setDimensionVariables, 50));

// iOS alters screen width without resize event, if unexpectedly wide content is found
setTimeout(setViewportHeight, 3000);

/**
 * Pauses all media (videos/models) within an element.
 * @param {Element} [el=document] - Element to pause media within.
 */
function pauseAllMedia(el = document) {
  el.querySelectorAll(".js-youtube, .js-vimeo, video").forEach((video) => {
    const component = video.closest("video-component");
    if (component && component.dataset.background === "true") return;

    if (video.matches(".js-youtube")) {
      video.contentWindow.postMessage(
        '{ "event": "command", "func": "pauseVideo", "args": "" }',
        "*"
      );
    } else if (video.matches(".js-vimeo")) {
      video.contentWindow.postMessage('{ "method": "pause" }', "*");
    } else {
      video.pause();
    }
  });

  el.querySelectorAll("product-model").forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

class DeferredMedia extends HTMLElement {
  constructor() {
    super();

    const loadBtn = this.querySelector(".js-load-media");
    if (loadBtn) {
      loadBtn.addEventListener("click", this.loadContent.bind(this));
    } else {
      this.addObserver();
    }
  }

  /**
   * Adds an Intersection Observer to load the content when viewport scroll is near
   */
  addObserver() {
    if ("IntersectionObserver" in window === false) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.loadContent(false, false, "observer");
            observer.unobserve(this);
          }
        });
      },
      { rootMargin: "0px 0px 1000px 0px" }
    );

    observer.observe(this);
  }

  /**
   * Loads the deferred media.
   * @param {boolean} [focus=true] - Focus the deferred media element after loading.
   * @param {boolean} [pause=true] - Whether to pause all media after loading.
   * @param {string} [loadTrigger='click'] - The action that caused the deferred content to load.
   */
  loadContent(focus = true, pause = true, loadTrigger = "click") {
    if (pause) pauseAllMedia();
    if (this.getAttribute("loaded") !== null) return;

    this.loadTrigger = loadTrigger;
    const content =
      this.querySelector("template").content.firstElementChild.cloneNode(true);
    this.appendChild(content);
    this.setAttribute("loaded", "");

    const deferredEl = this.querySelector("video, model-viewer, iframe");
    if (deferredEl && focus) deferredEl.focus();
  }
}

customElements.define("deferred-media", DeferredMedia);

class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.disclosure = this.querySelector("details");
    this.toggle = this.querySelector("summary");
    // this.panel = this.toggle.nextElementSibling;
    // this.init();
    this.addContentHeight();
  }

  //   init() {
  //     // Check if the content element has a CSS transition.
  //     if (window.getComputedStyle(this.panel).transitionDuration !== "0s") {
  //       this.toggle.addEventListener("click", this.handleToggle.bind(this));
  //       // this.disclosure.addEventListener(
  //       //   "transitionend",
  //       //   this.handleTransitionEnd.bind(this)
  //       // );
  //     }
  //   }

  //   /**
  //    * Handles 'click' events on the summary element.
  //    * @param {object} evt - Event object.
  //    */
  //   handleToggle(evt) {
  //     evt.preventDefault();

  // this.addContentHeight();
  //     if (!this.disclosure.open) {
  //       this.open();
  //     } else {
  //       this.close();
  //     }
  //   }

  //   /**
  //    * Handles 'transitionend' events on the details element.
  //    * @param {object} evt - Event object.
  //    */
  //   handleTransitionEnd(evt) {
  //     if (evt.target !== this.panel) return;

  //     if (this.disclosure.classList.contains("is-closing")) {
  //       this.disclosure.classList.remove("is-closing");
  //       this.disclosure.open = false;
  //     }

  //     this.panel.removeAttribute("style");
  //   }

  //   /**
  //    * Adds inline 'height' style to the content element, to trigger open transition.
  //    */
  addContentHeight() {
    this.panel.style.height = `${this.panel.scrollHeight}px`;
  }

  //   /**
  //    * Opens the details element.
  //    */
  open() {
    //     // Set content 'height' to zero before opening the details element.
    //     // this.panel.style.height = "0";

    //     // Open the details element
    //     this.disclosure.open = true;
    this.disclosure.classList.add("active");

    //     // Set content 'height' to its scroll height, to enable CSS transition.
    //     // this.addContentHeight();
  }

  //   /**
  //    * Closes the details element.
  //    */
  close() {
    //     // Set content height to its scroll height, to enable transition to zero.
    //     // this.addContentHeight();

    //     // Add class to enable styling of content or toggle icon before or during close transition.
    //     // this.disclosure.classList.add("is-closing");
    this.disclosure.classList.remove("active");

    //     // Set content height to zero to trigger the transition.
    //     // Slight delay required to allow scroll height to be applied before changing to '0'.
    //     // setTimeout(() => {
    //       // this.panel.style.height = "0";
    //     // });
  }
}

// customElements.define("details-disclosure", DetailsDisclosure);

if (!customElements.get("gift-card-recipient")) {
  class GiftCardRecipient extends HTMLElement {
    connectedCallback() {
      this.recipientEmail = this.querySelector(
        '[name="properties[Recipient email]"]'
      );
      this.recipientEmailLabel = this.querySelector(
        `label[for="${this.recipientEmail.id}"]`
      );
      this.recipientName = this.querySelector(
        '[name="properties[Recipient name]"]'
      );
      this.recipientMessage = this.querySelector(
        '[name="properties[Message]"]'
      );
      this.recipientSendOn = this.querySelector('[name="properties[Send on]"]');
      this.recipientOffsetProperty = this.querySelector(
        '[name="properties[__shopify_offset]"]'
      );

      // When JS is enabled, the recipient email field is required.
      // Input labels are changed to reflect this.
      if (
        this.recipientEmailLabel &&
        this.recipientEmailLabel.dataset.jsLabel
      ) {
        this.recipientEmailLabel.innerText =
          this.recipientEmailLabel.dataset.jsLabel;
      }

      // Set the timezone offset property input and enable it
      if (this.recipientOffsetProperty) {
        this.recipientOffsetProperty.value = new Date()
          .getTimezoneOffset()
          .toString();
        this.recipientOffsetProperty.removeAttribute("disabled");
      }

      this.recipientCheckbox = this.querySelector(
        ".gift-card-recipient__checkbox"
      );
      this.recipientCheckbox.addEventListener("change", () =>
        this.synchronizeProperties()
      );
      this.synchronizeProperties();
    }

    synchronizeProperties() {
      if (this.recipientCheckbox.checked) {
        this.recipientEmail.setAttribute("required", "");
        this.recipientEmail.removeAttribute("disabled");
        this.recipientName.removeAttribute("disabled");
        this.recipientMessage.removeAttribute("disabled");
        this.recipientSendOn.removeAttribute("disabled");
        if (this.recipientOffsetProperty) {
          this.recipientOffsetProperty.removeAttribute("disabled");
        }
      } else {
        this.recipientEmail.removeAttribute("required");
        this.recipientEmail.setAttribute("disabled", "");
        this.recipientName.setAttribute("disabled", "");
        this.recipientMessage.setAttribute("disabled", "");
        this.recipientSendOn.setAttribute("disabled", "");
        if (this.recipientOffsetProperty) {
          this.recipientOffsetProperty.setAttribute("disabled", "");
        }
      }
    }
  }

  customElements.define("gift-card-recipient", GiftCardRecipient);
}

const trapFocusHandlers = {};

/**
 * Removes focus trap event listeners and optionally focuses an element.
 * @param {Element} [elementToFocus=null] - Element to focus when trap is removed.
 */
function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener("focusin", trapFocusHandlers.focusin);
  document.removeEventListener("focusout", trapFocusHandlers.focusout);
  document.removeEventListener("keydown", trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

/**
 * Traps focus within a container, e.g. modal or side drawer.
 * @param {Element} container - Container element to trap focus within.
 * @param {Element} [elementToFocus=container] - Initial element to focus when trap is applied.
 */
function trapFocus(container, elementToFocus = container) {
  const focusableEls = Array.from(
    container.querySelectorAll(
      'summary, a[href], area[href], button:not([disabled]), input:not([type=hidden]):not([disabled]), select:not([disabled]), textarea:not([disabled]), object, iframe, audio[controls], video[controls], [tabindex]:not([tabindex^="-"])'
    )
  );

  const firstEl = focusableEls[0];
  const lastEl = focusableEls[focusableEls.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (evt) => {
    if (
      evt.target !== container &&
      evt.target !== lastEl &&
      evt.target !== firstEl
    )
      return;
    document.addEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = () => {
    document.removeEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = (evt) => {
    if (evt.code !== "Tab") return;

    // If tab pressed on last focusable element, focus the first element.
    if (evt.target === lastEl && !evt.shiftKey) {
      evt.preventDefault();
      firstEl.focus();
    }

    //  If shift + tab pressed on the first focusable element, focus the last element.
    if ((evt.target === container || evt.target === firstEl) && evt.shiftKey) {
      evt.preventDefault();
      lastEl.focus();
    }
  };

  document.addEventListener("focusout", trapFocusHandlers.focusout);
  document.addEventListener("focusin", trapFocusHandlers.focusin);

  (elementToFocus || container).focus();
}

class Modal extends HTMLElement {
  constructor() {
    super();
    this.addEventListener("click", this.handleClick.bind(this));
  }

  /**
   * Handles 'click' events on the modal.
   * @param {object} evt - Event object.
   */
  handleClick(evt) {
    if (evt.target !== this && !evt.target.matches(".js-close-modal")) return;
    this.close();
  }

  /**
   * Opens the modal.
   * @param {Element} opener - Modal opener element.
   */
  open(opener) {
    // Prevent page behind from scrolling when side drawer is open
    this.scrollY = window.scrollY;
    document.body.classList.add("fixed");
    document.body.style.top = `-${this.scrollY}px`;

    this.setAttribute("open", "");
    this.openedBy = opener;

    trapFocus(this);
    window.pauseAllMedia();

    // Add event handler (so the bound event listener can be removed).
    this.keyupHandler = (evt) => evt.key === "Escape" && this.close();

    // Add event listener (for while modal is open).
    this.addEventListener("keyup", this.keyupHandler);
  }

  /**
   * Closes the modal.
   */
  close() {
    // Restore page position and scroll behaviour.
    document.body.style.top = "";
    document.body.classList.remove("fixed");
    window.scrollTo(0, this.scrollY);

    this.removeAttribute("open");

    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();

    // Remove event listener added on modal opening.
    this.removeEventListener("keyup", this.keyupHandler);
  }
}

customElements.define("modal-dialog", Modal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector("button");
    if (!button) return;

    button.addEventListener("click", () => {
      const modal = document.getElementById(this.dataset.modal);
      if (modal) modal.open(button);
    });
  }
}

customElements.define("modal-opener", ModalOpener);

class SideDrawer extends HTMLElement {
  constructor() {
    super();
    this.overlay = document.querySelector(".js-overlay");
  }

  /**
   * Handles a 'click' event on the drawer.
   * @param {object} evt - Event object.
   */
  handleClick(evt) {
    if (evt.target.matches(".js-close-drawer") || evt.target === this.overlay) {
      this.close();
    }
  }

  /**
   * Opens the drawer.
   * @param {Element} [opener] - Element that triggered opening of the drawer.
   * @param {Element} [elementToFocus] - Element to focus after drawer opened.
   * @param {Function} [callback] - Callback function to trigger after the open has completed
   */
  open(opener, elementToFocus, callback) {
    this.dispatchEvent(
      new CustomEvent(`on:${this.dataset.name}:before-open`, {
        bubbles: true,
      })
    );

    // Prevent page behind from scrolling when side drawer is open.
this.scrollY = window.scrollY;
document.body.style.overflow = "hidden";
document.documentElement.style.overflow = "hidden";

    this.overlay.classList.add("is-visible");
    this.setAttribute("open", "");
    this.setAttribute("aria-hidden", "false");
    this.opener = opener;

    trapFocus(this, elementToFocus);

    // Create event handler variables (so the bound event listeners can be removed).
    this.clickHandler = this.clickHandler || this.handleClick.bind(this);
    this.keyupHandler = (evt) => {
      if (evt.key !== "Escape" || evt.target.closest(".cart-drawer-popup"))
        return;
      this.close();
    };

    // Add event listeners (for while drawer is open).
    this.addEventListener("click", this.clickHandler);
    this.addEventListener("keyup", this.keyupHandler);
    this.overlay.addEventListener("click", this.clickHandler);

    // Handle events after the drawer opens
    const transitionDuration = parseFloat(
      getComputedStyle(this).getPropertyValue("--longest-transition-in-ms")
    );
    setTimeout(() => {
      if (callback) callback();
      this.dispatchEvent(
        new CustomEvent(`on:${this.dataset.name}:after-open`, {
          bubbles: true,
        })
      );
    }, transitionDuration);
  }

  /**
   * Closes the drawer.
   * @param {Function} [callback] - Call back function to trigger after the close has completed
   */
  close(callback) {
    this.dispatchEvent(
      new CustomEvent(`on:${this.dataset.name}:before-close`, {
        bubbles: true,
      })
    );

    this.removeAttribute("open");
    this.setAttribute("aria-hidden", "true");
    this.overlay.classList.remove("is-visible");

    removeTrapFocus(this.opener);

    // Restore page position and scroll behaviour.
document.body.style.overflow = "";
document.documentElement.style.overflow = "";

    // Remove event listeners added on drawer opening.
    this.removeEventListener("click", this.clickHandler);
    this.removeEventListener("keyup", this.keyupHandler);
    this.overlay.removeEventListener("click", this.clickHandler);

    // Handle events after the drawer closes
    const transitionDuration = parseFloat(
      getComputedStyle(this).getPropertyValue("--longest-transition-in-ms")
    );
    setTimeout(() => {
      if (callback) callback();
      this.dispatchEvent(
        new CustomEvent(`on:${this.dataset.name}:after-close`, {
          bubbles: true,
        })
      );
    }, transitionDuration);
  }
}

customElements.define("side-drawer", SideDrawer);

class BuyButtons extends HTMLElement {
  constructor() {
    super();
    window.initLazyScript(this, this.initLazySection.bind(this));
  }

  initLazySection() {
    this.dynamicPaymentButtonTemplate = this.querySelector(
      ".dynamic-payment-button-template"
    );

    if (this.dynamicPaymentButtonTemplate) {
      this.variantIdInput = this.querySelector('[name="id"]');

      if (this.variantIdInput.value) {
        this.tryInitDynamicPaymentButton();
      } else {
        this.boundTryInitDynamicPaymentButtonOnChange =
          this.tryInitDynamicPaymentButton.bind(this);
        this.variantIdInput.addEventListener(
          "change",
          this.boundTryInitDynamicPaymentButtonOnChange
        );
      }
    }
  }

  /**
   * Initialise dynamic payment button, if variant exists
   */
  tryInitDynamicPaymentButton() {
    if (this.variantIdInput.value) {
      if (this.boundTryInitDynamicPaymentButtonOnChange) {
        this.variantIdInput.removeEventListener(
          "change",
          this.boundTryInitDynamicPaymentButtonOnChange
        );
      }

      this.dynamicPaymentButtonTemplate.insertAdjacentHTML(
        "afterend",
        this.dynamicPaymentButtonTemplate.innerHTML
      );
      this.dynamicPaymentButtonTemplate.remove();

      if (Shopify.PaymentButton) {
        Shopify.PaymentButton.init();
      }
    }
  }
}

customElements.define("buy-buttons", BuyButtons);

const CartForm = class extends HTMLElement {
  connectedCallback() {
    this.enableAjaxUpdate = this.dataset.ajaxUpdate;

    if (this.enableAjaxUpdate) {
      this.sectionId = this.dataset.sectionId;
      this.boundRefresh = this.refresh.bind(this);
      document.addEventListener("on:cart:change", this.boundRefresh);

      theme.addDelegateEventListener(
        this,
        "click",
        ".cart-item__remove",
        (evt) => {
          evt.preventDefault();
          this.adjustItemQuantity(evt.target.closest(".cart-item"), { to: 0 });
        }
      );

      theme.addDelegateEventListener(this, "click", ".quantity-down", (evt) => {
        evt.preventDefault();
        this.adjustItemQuantity(evt.target.closest(".cart-item"), {
          decrease: true,
        });
      });

      theme.addDelegateEventListener(this, "click", ".quantity-up", (evt) => {
        evt.preventDefault();
        this.adjustItemQuantity(evt.target.closest(".cart-item"), {
          increase: true,
        });
      });

      theme.addDelegateEventListener(
        this,
        "change",
        ".cart-item__quantity-input",
        (evt) => {
          this.adjustItemQuantity(evt.target.closest(".cart-item"), {
            currentValue: true,
          });
        }
      );
    }
  }

  disconnectedCallback() {
    if (this.enableAjaxUpdate) {
      document.removeEventListener("on:cart:change", this.boundRefresh);
    }
  }

  refresh(options = {}) {
    this.classList.add("cart-form--refreshing");

    return fetch(`${window.Shopify.routes.root}?section_id=${this.sectionId}`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.text();
      })
      .then((response) => {
        this.refreshFromHtml(response, options);
        return this;
      })
      .catch((error) => {
        this.classList.remove("cart-form--refreshing");
        throw error;
      });
  }

  refreshFromHtml(html, options = {}) {
    const frag = document.createDocumentFragment();
    const newContent = document.createElement("div");
    frag.appendChild(newContent);
    newContent.innerHTML = html;

    newContent
      .querySelectorAll("[data-cc-animate]")
      .forEach((el) => el.removeAttribute("data-cc-animate"));

    /*
     * Save the current gift progress before AJAX merge.
     * Example: 0.30 before the customer adds product number 2.
     */
    const currentProgressCard = this.querySelector(
      "#mak-cart-footer .mak-gift-progress-card"
    );
    // Also retain 1+1 / 2+2 progress before this AJAX footer is replaced.
    const previousBogoCard = this.querySelector("#mak-cart-footer .mn-cart-bogo-offer");
    const previousBogoCount = previousBogoCard
      ? Number(previousBogoCard.dataset.freeShippingCurrentCart)
      : 0;
    const previousBogoProgress = previousBogoCard
      ? parseFloat(getComputedStyle(previousBogoCard.querySelector(".mn-cart-bogo-steps"))
          .getPropertyValue("--mn-bogo-line-progress"))
      : 0;

    let previousGiftProgress = null;
    const previousGiftCount = currentProgressCard
      ? Number(currentProgressCard.dataset.freeShippingCurrentCart)
      : NaN;

    if (currentProgressCard) {
      previousGiftProgress = parseFloat(
        getComputedStyle(currentProgressCard)
          .getPropertyValue("--mak-gift-line-progress")
          .trim()
      );

      if (Number.isNaN(previousGiftProgress)) {
        previousGiftProgress = null;
      }
    }

    /*
     * Merge the refreshed cart HTML.
     * The progress card now receives its NEW Liquid value.
     */
    theme.mergeNodes(newContent, this);

    // Update the three-step BOGO animation only after the new cart has rendered.
    // No effect on the existing 2+1 progress bar or product-selection logic.
    const updatedBogoCard = this.querySelector("#mak-cart-footer .mn-cart-bogo-offer");
    if (updatedBogoCard) {
      const nextBogoCount = Number(updatedBogoCard.dataset.freeShippingCurrentCart);
      // data-merge-attributes only updates the wrapper attributes. Update the
      // four steps and the CTA as well when the cart quantity changes.
      const incomingBogoCard = newContent.querySelector("#mak-cart-footer .mn-cart-bogo-offer");
      if (incomingBogoCard && nextBogoCount !== previousBogoCount) {
        updatedBogoCard.replaceChildren(...incomingBogoCard.childNodes);
      }
      const updatedBogoSteps = updatedBogoCard.querySelector(".mn-cart-bogo-steps");
      const nextBogoProgress = updatedBogoSteps
        ? parseFloat(getComputedStyle(updatedBogoSteps).getPropertyValue("--mn-bogo-line-progress"))
        : NaN;
      if (Number.isFinite(nextBogoProgress) && Number.isFinite(previousBogoProgress) &&
          nextBogoCount !== previousBogoCount) {
        updatedBogoCard.classList.remove("mak-progress-animate", "mak-progress-ajax");
        updatedBogoSteps.style.setProperty("--mn-bogo-line-progress", previousBogoProgress);
        void updatedBogoSteps.offsetWidth;
        updatedBogoCard.classList.add("mak-progress-ajax");
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            updatedBogoSteps.style.setProperty("--mn-bogo-line-progress", nextBogoProgress);
            clearTimeout(this.mnBogoProgressTimer);
            this.mnBogoProgressTimer = setTimeout(() => {
              updatedBogoCard.classList.remove("mak-progress-ajax");
            }, 750);
          });
        });
      }
      // Reaching 1 / 3 opens the lock on step 2 / 3, independent of
      // the progress-line calculation. Do not replay this on drawer open.
      const unlockingStep = previousBogoCount === 0 && nextBogoCount === 1 ? 2
        : previousBogoCount === 2 && nextBogoCount === 3 ? 3 : null;
      if (unlockingStep) {
        const targetStep = updatedBogoCard.querySelector(
          `.mn-cart-bogo-step[data-mn-bogo-step="${unlockingStep}"]`
        );
        if (targetStep && targetStep.querySelector(".mn-bogo-lock-open")) {
          updatedBogoCard.classList.remove("mn-bogo-unlock-animate");
          updatedBogoCard.querySelectorAll(".mn-bogo-pulse-target").forEach(
            (step) => step.classList.remove("mn-bogo-pulse-target")
          );
          targetStep.classList.add("mn-bogo-pulse-target");
          void updatedBogoCard.offsetWidth;
          updatedBogoCard.classList.add("mn-bogo-unlock-animate");
          clearTimeout(this.mnBogoUnlockTimer);
          this.mnBogoUnlockTimer = setTimeout(() => {
            updatedBogoCard.classList.remove("mn-bogo-unlock-animate");
            targetStep.classList.remove("mn-bogo-pulse-target");
          }, 900);
        }
      }
    }

    /*
     * Animate old progress -> new progress after AJAX update.
     */
    const updatedProgressCard = this.querySelector(
      "#mak-cart-footer .mak-gift-progress-card"
    );

    // Trigger Unlock & Pulse only on the actual 1 -> 2 cart transition.
    // Never replay it on drawer open, page reload, or unrelated cart updates.
    const nextGiftCount = updatedProgressCard
      ? Number(updatedProgressCard.dataset.freeShippingCurrentCart)
      : NaN;
    const justUnlockedGift = previousGiftCount === 1 && nextGiftCount === 2;
    if (justUnlockedGift && updatedProgressCard) {
      updatedProgressCard.classList.add("mak-gift-unlock-animate");
      clearTimeout(this.makGiftUnlockTimer);
      this.makGiftUnlockTimer = setTimeout(() => {
        updatedProgressCard.classList.remove("mak-gift-unlock-animate");
      }, 900);
    }

    if (
      updatedProgressCard &&
      previousGiftProgress !== null &&
      !updatedProgressCard.classList.contains("mak-gift-progress-complete")
    ) {
      const newGiftProgress = parseFloat(
        getComputedStyle(updatedProgressCard)
          .getPropertyValue("--mak-gift-line-progress")
          .trim()
      );

      if (
        !Number.isNaN(newGiftProgress) &&
        newGiftProgress !== previousGiftProgress
      ) {
        const progressTrack = updatedProgressCard.querySelector(
          ".mak-gift-progress-track"
        );

        /*
         * Do not run the drawer-open animation at the same time
         * as the AJAX progress animation.
         */
        if (progressTrack) {
          progressTrack.classList.remove("mak-progress-animate");
        }

        updatedProgressCard.classList.remove("mak-progress-animate");
        updatedProgressCard.classList.remove("mak-progress-ajax");

        /*
         * First render the OLD progress value.
         */
        updatedProgressCard.style.setProperty(
          "--mak-gift-line-progress",
          previousGiftProgress
        );

        /*
         * Force browser layout so the old state is actually painted.
         */
        void updatedProgressCard.offsetWidth;

        /*
         * Enable the CSS width transition.
         */
        updatedProgressCard.classList.add("mak-progress-ajax");

        /*
         * On the next frames, move smoothly to the NEW progress value.
         */
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            updatedProgressCard.style.setProperty(
              "--mak-gift-line-progress",
              newGiftProgress
            );

            /*
             * Keep the final Liquid-equivalent value in place after
             * the transition. Only remove the helper class.
             */
            setTimeout(() => {
              updatedProgressCard.classList.remove("mak-progress-ajax");
              updatedProgressCard.style.setProperty(
                "--mak-gift-line-progress",
                newGiftProgress
              );
            }, 750);
          });
        });
      }
    }

    this.classList.remove("cart-form--refreshing");
    this.querySelectorAll(".merge-item-refreshing").forEach((el) =>
      el.classList.remove("merge-item-refreshing")
    );

    this.dispatchEvent(
      new CustomEvent("on:cart:after-merge", {
        bubbles: true,
        cancelable: false,
      })
    );

    if (
      !options.suppressDrawerOpen &&
      theme.settings.afterAddToCart === "drawer" &&
      this.closest(".drawer") &&
      !this.closest(".drawer").hasAttribute("open")
    ) {
      document.dispatchEvent(
        new CustomEvent("theme:open-cart-drawer", {
          bubbles: true,
          cancelable: false,
        })
      );
    }
  }

  adjustItemQuantity(item, change) {
    const quantityInput = item.querySelector(".cart-item__quantity-input");
    console.log(quantityInput);
    let newQuantity = parseInt(quantityInput.value, 10);
    if (typeof change.to !== "undefined") {
      newQuantity = change.to;
      quantityInput.value = newQuantity;
    } else if (change.increase) {
      newQuantity += quantityInput.step || 1;
      quantityInput.value = newQuantity;
    } else if (change.decrease) {
      newQuantity -= quantityInput.step || 1;
      quantityInput.value = newQuantity;
    } else if (change.currentValue);

    if (
      quantityInput.max &&
      parseInt(quantityInput.value, 10) > parseInt(quantityInput.max, 10)
    ) {
      newQuantity = quantityInput.max;
      quantityInput.value = newQuantity;
      theme.showQuickPopup(
        theme.strings.cartItemsQuantityError.replace(
          "[QUANTITY]",
          quantityInput.max
        ),
        quantityInput
      );
    }

    clearTimeout(this.adjustItemQuantityTimeout);
    this.adjustItemQuantityTimeout = setTimeout(
      () => {
        const updateParam = { updates: {} };
        this.querySelectorAll(
          ".cart-item__quantity-input:not([disabled])"
        ).forEach((el) => {
          updateParam.updates[el.dataset.key] = el.value;
          if (el.value !== el.dataset.initialValue) {
            el.closest("[data-merge-list-item]").classList.add(
              "merge-item-refreshing"
            );
          }
        });
        console.log("updateParam: ", updateParam);
        fetch(theme.routes.cartUpdate, {
          method: "POST",
          body: JSON.stringify(updateParam),
          headers: {
            "Content-Type": "application/json",
          },
        })
          .then((response) => {
            if (!response.ok) {
              throw new Error(`HTTP error! Status: ${response.status}`);
            }
            document.dispatchEvent(
              new CustomEvent("on:cart:change", {
                bubbles: true,
                cancelable: false,
              })
            );
          })
          .catch((error) => {
            // eslint-disable-next-line no-console
            console.log(error.message);
            this.dispatchEvent(
              new CustomEvent("on:cart:error", {
                bubbles: true,
                detail: {
                  error: error.message,
                },
              })
            );
            // Uncertainty... Reload page.
            window.location.reload();
          });
      },
      newQuantity === 0 ? 10 : 700
    );
  }
};

window.customElements.define("cart-form", CartForm);

const CCCartCrossSell = class extends HTMLElement {
  init() {
    this.productList = this.querySelector(".product-grid");

    if (this.dataset.from) {
      fetch(this.dataset.from)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
          return response.text();
        })
        .then((response) => {
          const frag = document.createDocumentFragment();
          const newContent = document.createElement("div");
          frag.appendChild(newContent);
          newContent.innerHTML = response;

          const pl = newContent.querySelector(".product-grid");
          if (pl) {
            this.productList.innerHTML = pl.innerHTML;
            this.querySelectorAll(".product-block").forEach((el) =>
              el.classList.add("slider__item")
            );
            this.querySelectorAll("carousel-slider").forEach((el) =>
              el.refresh()
            );
          } else {
            this.classList.add("hidden");
          }
        });
    }
  }
};

window.customElements.define("cc-cart-cross-sell", CCCartCrossSell);

const CCFetchedContent = class extends HTMLElement {
  connectedCallback() {
    fetch(this.dataset.url)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.text();
      })
      .then((response) => {
        const frag = document.createDocumentFragment();
        const fetchedContent = document.createElement("div");
        frag.appendChild(fetchedContent);
        fetchedContent.innerHTML = response;

        const replacementContent = fetchedContent.querySelector(
          `[data-id="${this.dataset.id}"]`
        );
        if (replacementContent) {
          this.innerHTML = replacementContent.innerHTML;
        }
      });
  }
};

window.customElements.define("cc-fetched-content", CCFetchedContent);

const FilterContainer = class extends HTMLElement {
  constructor() {
    super();
    this.section = this.closest(".shopify-section");
    this.filters = this.querySelector(".filters");

    const utilityBar = document.querySelector(".utility-bar");
    if (utilityBar) {
      // duplicate utility bar for mobile
      this.utilBarClone = utilityBar.cloneNode(true);
      this.utilBarClone.classList.add("utility-bar--sticky-mobile-copy");
      this.utilBarClone.removeAttribute("data-ajax-container");
      utilityBar.insertAdjacentElement("afterend", this.utilBarClone);
      // ensure ids are unique
      theme.suffixIds(this.utilBarClone, "dupe");

      this.previousScrollTop = window.scrollY;
      this.throttledCheckStickyScroll = theme.throttleEvent(
        this.checkStickyScroll.bind(this),
        200
      );
    }

    if (this.filters) {
      // auto-select availability
      this.allowAutoApplyHideUnavailable =
        this.filters.dataset.autoApplyHideUnavailable;
      if (this.allowAutoApplyHideUnavailable) {
        theme.addDelegateEventListener(
          this,
          "change",
          ".filter-group__checkbox, .cc-price-range__input",
          (evt, delEl) => {
            if (this.allowAutoApplyHideUnavailable) {
              if (
                (delEl.type === "checkbox" && delEl.checked) ||
                (delEl.type === "text" && delEl.value)
              ) {
                const toEnable = this.filters.querySelector(
                  '[name="filter.v.availability"][value="1"]'
                );
                if (toEnable) {
                  toEnable.checked = true;
                  toEnable.dispatchEvent(
                    new CustomEvent("change", {
                      bubbles: true,
                      cancelable: false,
                    })
                  );
                }
              }
            }
          }
        );

        theme.addDelegateEventListener(
          this,
          "change",
          ".filter-group--availability .filter-toggle__input",
          (evt, delEl) => {
            if (delEl.checked && delEl.value === "1") {
              this.allowAutoApplyHideUnavailable = false;
            }
          }
        );
      }

      // ajax filter
      if (this.dataset.ajaxFiltering === "true") {
        // ajax load form submission
        const debouncedAjaxLoadForm = debounce(
          this.ajaxLoadForm.bind(this),
          700
        );
        theme.addDelegateEventListener(
          this,
          "change",
          "#CollectionFilterForm",
          debouncedAjaxLoadForm
        );
        theme.addDelegateEventListener(
          this,
          "submit",
          "#CollectionFilterForm",
          debouncedAjaxLoadForm
        );
      } else {
        theme.addDelegateEventListener(
          this,
          "change",
          "#CollectionFilterForm",
          (_evt, delEl) => delEl.submit()
        );
      }

      // init things that may need re-initialising on ajax load
      this.initFiltersEtc();
    }

    // ajax sort, filter, and pagination
    if (this.dataset.ajaxFiltering === "true") {
      // ajax load on link click
      theme.addDelegateEventListener(
        this.section,
        "click",
        ".link-dropdown__link, .filter-group__applied-item, .filter-group__clear-link, .pagination a",
        (evt, delEl) => {
          evt.preventDefault();
          this.ajaxLoadUrl(delEl.href);
        }
      );
    }
  }

  connectedCallback() {
    // scroll
    if (this.throttledCheckStickyScroll) {
      window.addEventListener("scroll", this.throttledCheckStickyScroll);
    }

    // back button
    if (this.dataset.ajaxFiltering === "true") {
      this.boundAjaxPopState = this.ajaxPopState.bind(this);
      window.addEventListener("popstate", this.boundAjaxPopState);
    }

    // layout switch
    if (this.section.querySelector(".layout-switchers")) {
      this.boundSwitchGridLayout = theme.addDelegateEventListener(
        this.section,
        "click",
        ".layout-switch",
        this.switchGridLayout.bind(this)
      );
    }

    // show/hide filters
    this.delegatedToggleFiltersCallback = theme.addDelegateEventListener(
      this.section,
      "click",
      "[data-toggle-filters]",
      (evt) => {
        evt.preventDefault();
        this.classList.toggle("filter-container--show-filters-desktop");
        this.classList.toggle("filter-container--show-filters-mobile");
        const isNowVisible = this.classList.contains(
          "filter-container--show-filters-desktop"
        );
        this.section
          .querySelectorAll(".toggle-btn[data-toggle-filters]")
          .forEach((el) => {
            el.classList.toggle("toggle-btn--revealed-desktop", isNowVisible);
          });
      }
    );
  }

  disconnectedCallback() {
    if (this.boundCheckStickyScroll) {
      window.removeEventListener("scroll", this.throttledCheckStickyScroll);
    }

    if (this.boundAjaxPopState) {
      window.removeEventListener("popstate", this.boundAjaxPopState);
    }
  }

  initFiltersEtc() {
    this.classList.add("filter-container--mobile-initialised");

    // append query vars onto sort urls (e.g. filters, vendor collection)
    if (window.location.href.indexOf("?") >= 0) {
      document
        .querySelectorAll("#sort-dropdown-options .link-dropdown__link")
        .forEach((el) => {
          const queryTerms = window.location.href.split("?")[1].split("&");
          let newHref = el.href;
          queryTerms.forEach((term) => {
            if (term.indexOf("sort_by=") === -1) {
              newHref += `&${term}`;
            }
          });
          el.href = newHref;
        });
    }
  }

  switchGridLayout(evt) {
    evt.preventDefault();
    if (evt.target.classList.contains("layout-switch--one-column")) {
      this.querySelectorAll(".product-grid").forEach((el) => {
        el.classList.remove("product-grid--per-row-mob-2");
        el.classList.add("product-grid--per-row-mob-1");
      });
    } else {
      this.querySelectorAll(".product-grid").forEach((el) => {
        el.classList.remove("product-grid--per-row-mob-1");
        el.classList.add("product-grid--per-row-mob-2");
      });
    }
    evt.target.classList.add("layout-switch--active");
    (
      evt.target.nextElementSibling || evt.target.previousElementSibling
    ).classList.remove("layout-switch--active");
  }

  checkStickyScroll() {
    const utilityBarOffsetY = theme.getOffsetTopFromDoc(
      this.section.querySelector(".utility-bar")
    );
    if (
      window.innerWidth < 768 &&
      this.previousScrollTop > window.scrollY &&
      window.scrollY > utilityBarOffsetY
    ) {
      document.body.classList.add("utility-bar-sticky-mobile-copy-reveal");
    } else {
      document.body.classList.remove("utility-bar-sticky-mobile-copy-reveal");
    }
    this.previousScrollTop = window.scrollY;
  }

  ajaxLoadForm(evt) {
    if (evt.type === "submit") {
      evt.preventDefault();
    }
    const queryVals = [];
    this.filters.querySelectorAll("input, select").forEach((input) => {
      if (
        ((input.type !== "checkbox" && input.type !== "radio") ||
          input.checked) && // is an active input value
        input.value !== "" // has a value
      ) {
        queryVals.push([input.name, encodeURIComponent(input.value)]);
      }
    });
    // new url
    let newUrl = window.location.pathname;
    queryVals.forEach((value) => {
      newUrl += `&${value[0]}=${value[1]}`;
    });
    newUrl = newUrl.replace("&", "?");
    // load
    this.ajaxLoadUrl.call(this, newUrl);
  }

  ajaxPopState() {
    this.ajaxLoadUrl.call(this, document.location.href, true);
  }

  ajaxLoadUrl(url, noPushState) {
    if (!noPushState) {
      // update url history
      let fullUrl = url;
      if (fullUrl.slice(0, 1) === "/") {
        fullUrl = `${window.location.protocol}//${window.location.host}${fullUrl}`;
      }
      window.history.pushState({ path: fullUrl }, "", fullUrl);
    }

    // limit render to section, if possible (added after pushState)
    let fetchUrl = url;
    if (this.dataset.filterSectionId) {
      fetchUrl = `${url}${url.indexOf("?") >= 0 ? "&" : "?"}section_id=${this.dataset.filterSectionId
        }`;
    }

    // start fetching URL
    const refreshContainerSelector = "[data-ajax-container]";
    const ajaxContainers = this.section.querySelectorAll(
      refreshContainerSelector
    );

    // loading state
    ajaxContainers.forEach((el) => el.classList.add("ajax-loading"));

    // cancel current fetch & fetch next
    if (this.ajaxLoadUrlFetchAbortController) {
      this.ajaxLoadUrlFetchAbortController.abort();
    }
    this.ajaxLoadUrlFetchAbortController = new AbortController();

    fetch(fetchUrl, {
      method: "get",
      signal: this.ajaxLoadUrlFetchAbortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.text();
      })
      .then((response) => {
        // save active element
        if (document.activeElement) {
          this.activeElementId = document.activeElement.id;
        }

        // before replace - check if container scrolls, if so find item at scroll top
        const scrollContainer = this.querySelector(".filters");
        let elAboveScrollTopData = null;
        if (
          scrollContainer &&
          getComputedStyle(scrollContainer).overflow === "auto"
        ) {
          const allFilterChildren =
            scrollContainer.querySelectorAll(".filters *");
          let elAboveScrollTop = allFilterChildren[0];
          for (let i = 1; i < allFilterChildren.length; i += 1) {
            if (allFilterChildren[i].offsetTop) {
              // if above fold
              if (allFilterChildren[i].offsetTop < scrollContainer.scrollTop) {
                // if below previously assigned element, assign
                if (
                  allFilterChildren[i].offsetTop > elAboveScrollTop.offsetTop
                ) {
                  elAboveScrollTop = allFilterChildren[i];
                }
              } else {
                break;
              }
            }
          }
          if (elAboveScrollTop.offsetTop === 0) {
            elAboveScrollTop = false;
          } else {
            // determine how to identify this element
            elAboveScrollTopData = {
              selector: "",
              textContent: elAboveScrollTop.textContent,
              extraScrollOffset:
                scrollContainer.scrollTop - elAboveScrollTop.offsetTop,
            };
            const attributeNames = elAboveScrollTop.getAttributeNames();
            for (let i = 0; i < attributeNames.length; i += 1) {
              const attrName = attributeNames[i];
              if (attrName === "class") {
                Array.from(elAboveScrollTop.classList)
                  .filter((a) => a !== "filter-group__item--disabled")
                  .forEach((cl) => {
                    elAboveScrollTopData.selector += `.${cl}`;
                  });
              } else {
                elAboveScrollTopData.selector += `[${attrName}="${CSS.escape(
                  elAboveScrollTop.getAttribute(attrName)
                )}"]`;
              }
            }
          }
        }

        // replace contents
        const template = document.createElement("template");
        template.innerHTML = response;
        const newAjaxContainers = template.content.querySelectorAll(
          refreshContainerSelector
        );
        newAjaxContainers.forEach((el, index) => {
          ajaxContainers[index].innerHTML = el.innerHTML;
        });

        // init js
        this.initFiltersEtc();

        // after replace
        if (elAboveScrollTopData) {
          this.querySelectorAll(elAboveScrollTopData.selector).forEach((el) => {
            if (el.textContent === elAboveScrollTopData.textContent) {
              scrollContainer.scrollTop =
                el.offsetTop + elAboveScrollTopData.extraScrollOffset;
            }
          });
        }

        // update mobile count
        if (this.utilBarClone) {
          const from = document.querySelector(
            ".utility-bar:not(.utility-bar--sticky-mobile-copy) .utility-bar__centre"
          );
          const to = this.utilBarClone.querySelector(".utility-bar__centre");
          if (from && to) {
            to.innerHTML = from.innerHTML;
          }
        }

        // remove loading state
        ajaxContainers.forEach((el) => el.classList.remove("ajax-loading"));

        // restore active element
        if (this.activeElementId) {
          const el = document.getElementById(this.activeElementId);
          if (el) {
            el.focus();
          }
        }

        // scroll viewport (must be done after any page size changes)
        const scrollToY =
          theme.getOffsetTopFromDoc(
            this.section.querySelector("[data-ajax-scroll-to]")
          ) - document.querySelector(".section-header").clientHeight;
        window.scrollTo({
          top: scrollToY,
          behavior: "smooth",
        });
      });
  }
};

window.customElements.define("filter-container", FilterContainer);

/* eslint-disable max-len */
const GalleryViewer = class extends HTMLElement {
  connectedCallback() {
    if (!this.initialised) {
      this.initialised = true;

      // ui
      this.classList.add("gallery-viewer--pre-reveal");
      this.zoomContainer = this.querySelector(
        ".gallery-viewer__zoom-container"
      );
      this.thumbContainer = this.querySelector(".gallery-viewer__thumbs");
      this.controlsContainer = this.querySelector(".gallery-viewer__controls");
      this.previousBtn = this.querySelector(".gallery-viewer__prev");
      this.nextBtn = this.querySelector(".gallery-viewer__next");

      // consts
      this.wheelZoomMultiplier = -0.001;
      this.pinchZoomMultiplier = 0.003;
      this.touchPanModifier = 1.0;

      // vars
      this.currentZoomImage = null;
      this.currentTransform = {
        panX: 0,
        panY: 0,
        zoom: 1,
      };
      this.pinchTracking = {
        isTracking: false,
        lastPinchDistance: 0,
      };
      this.touchTracking = {
        isTracking: false,
        lastTouchX: 0,
        lastTouchY: 0,
      };

      // events
      theme.addDelegateEventListener(
        this,
        "click",
        ".gallery-viewer__thumb",
        this.onThumbClick.bind(this)
      );
      this.addEventListener("touchend", this.stopTrackingTouch.bind(this));
      this.addEventListener("touchmove", this.trackInputMovement.bind(this));
      this.addEventListener("mousemove", this.trackInputMovement.bind(this));
      this.addEventListener("wheel", this.trackWheel.bind(this));
      // prevent pan while swiping thumbnails
      this.thumbContainer.addEventListener("touchmove", (evt) =>
        evt.stopPropagation()
      );
      this.previousBtn.addEventListener(
        "click",
        this.selectPreviousThumb.bind(this)
      );
      this.nextBtn.addEventListener("click", this.selectNextThumb.bind(this));
      this.zoomContainer.addEventListener(
        "click",
        this.onZoomContainerClick.bind(this)
      );
    }

    document.documentElement.classList.add("gallery-viewer-open");
    this.addEventListener("keyup", this.handleKeyup.bind(this));
    setTimeout(() => this.classList.remove("gallery-viewer--pre-reveal"), 10);
  }

  // eslint-disable-next-line class-methods-use-this
  disconnectedCallback() {
    document.documentElement.classList.remove("gallery-viewer-open");
  }

  static createEl(type, className, appendTo, innerHTML) {
    const el = document.createElement(type);
    el.className = className;
    if (type === "a") {
      el.href = "#";
    }
    if (appendTo) {
      appendTo.insertAdjacentElement("beforeend", el);
    }
    if (innerHTML) {
      el.innerHTML = innerHTML;
    }
    return el;
  }

  init(currentFullUrl) {
    this.selectThumb(
      [...this.thumbContainer.children].find(
        (el) => el.dataset.zoomUrl === currentFullUrl
      ) || this.thumbContainer.firstElementChild
    );
  }

  panZoomImageFromCoordinate(inputX, inputY) {
    // do nothing if the image fits, pan if not
    const doPanX = this.currentZoomImage.clientWidth > this.clientWidth;
    const doPanY = this.currentZoomImage.clientHeight > this.clientHeight;

    if (doPanX || doPanY) {
      const midX = this.clientWidth / 2;
      const midY = this.clientHeight / 2;

      const offsetFromCentreX = inputX - midX;
      const offsetFromCentreY = inputY - midY;

      // the offsetMultipler ensures it can only pan to the edge of the image, no further
      let finalOffsetX = 0;
      let finalOffsetY = 0;

      if (doPanX) {
        const offsetMultiplierX =
          (this.currentZoomImage.clientWidth - this.clientWidth) / 2 / midX;
        finalOffsetX = Math.round(-offsetFromCentreX * offsetMultiplierX);
      }
      if (doPanY) {
        const offsetMultiplierY =
          (this.currentZoomImage.clientHeight - this.clientHeight) / 2 / midY;
        finalOffsetY = Math.round(-offsetFromCentreY * offsetMultiplierY);
      }

      this.currentTransform.panX = finalOffsetX;
      this.currentTransform.panY = finalOffsetY;
      this.alterCurrentPanBy(0, 0); // sanitise
      this.updateImagePosition();
    }
  }

  alterCurrentPanBy(x, y) {
    this.currentTransform.panX += x;
    // limit offset to keep most of image on screen
    let panXMax =
      (this.currentZoomImage.naturalWidth * this.currentTransform.zoom -
        this.clientWidth) /
      2.0;
    panXMax = Math.max(panXMax, 0);
    this.currentTransform.panX = Math.min(this.currentTransform.panX, panXMax);
    this.currentTransform.panX = Math.max(this.currentTransform.panX, -panXMax);

    this.currentTransform.panY += y;
    let panYMax =
      (this.currentZoomImage.naturalHeight * this.currentTransform.zoom -
        this.clientHeight) /
      2.0;
    panYMax = Math.max(panYMax, 0);
    this.currentTransform.panY = Math.min(this.currentTransform.panY, panYMax);
    this.currentTransform.panY = Math.max(this.currentTransform.panY, -panYMax);
    this.updateImagePosition();
  }

  setCurrentTransform(panX, panY, zoom) {
    this.currentTransform.panX = panX;
    this.currentTransform.panY = panY;
    this.currentTransform.zoom = zoom;
    this.alterCurrentTransformZoomBy(0);
  }

  alterCurrentTransformZoomBy(delta) {
    this.currentTransform.zoom += delta;
    // do not zoom out further than fit
    const maxZoomX = this.clientWidth / this.currentZoomImage.naturalWidth;
    const maxZoomY = this.clientHeight / this.currentZoomImage.naturalHeight;
    this.currentTransform.zoom = Math.max(
      this.currentTransform.zoom,
      Math.min(maxZoomX, maxZoomY)
    );

    // do not zoom in further than native size
    this.currentTransform.zoom = Math.min(this.currentTransform.zoom, 1.0);

    // reasses pan bounds
    this.alterCurrentPanBy(0, 0);
    this.updateImagePosition();
  }

  updateImagePosition() {
    this.currentZoomImage.style.transform = `translate3d(${this.currentTransform.panX}px, ${this.currentTransform.panY}px, 0) scale(${this.currentTransform.zoom})`;
  }

  selectThumb(thumb) {
    [...thumb.parentElement.children].forEach((el) => {
      if (el === thumb) {
        el.classList.add("gallery-viewer__thumb--active");
      } else {
        el.classList.remove("gallery-viewer__thumb--active");
      }
    });

    // replace zoom image
    this.zoomContainer.classList.add("gallery-viewer__zoom-container--loading");
    this.currentZoomImage = GalleryViewer.createEl(
      "img",
      "gallery-viewer__zoom-image"
    );
    this.currentZoomImage.alt = "";
    this.currentZoomImage.style.visibility = "hidden";
    this.currentZoomImage.onload = () => {
      this.zoomContainer.classList.remove(
        "gallery-viewer__zoom-container--loading"
      );
      this.currentZoomImage.style.visibility = "";
      this.currentZoomImage.style.top = `${this.clientHeight / 2 - this.currentZoomImage.clientHeight / 2
        }px`;
      this.currentZoomImage.style.left = `${this.clientWidth / 2 - this.currentZoomImage.clientWidth / 2
        }px`;
      this.setCurrentTransform(0, 0, 0); // centre, zoomed out
    };
    this.currentZoomImage.src = thumb.dataset.zoomUrl;
    this.zoomContainer.replaceChildren(this.currentZoomImage);
  }

  selectPreviousThumb(evt) {
    if (evt) evt.preventDefault();
    if (this.thumbContainer.childElementCount < 2) return;

    let previous = this.thumbContainer.querySelector(
      ".gallery-viewer__thumb--active"
    ).previousElementSibling;
    while (!previous || !previous.offsetParent) {
      if (!previous) {
        previous = this.thumbContainer.lastElementChild;
      } else {
        previous = previous.previousElementSibling;
      }
    }
    this.selectThumb(previous);
  }

  selectNextThumb(evt) {
    if (evt) evt.preventDefault();
    if (this.thumbContainer.childElementCount < 2) return;

    let next = this.thumbContainer.querySelector(
      ".gallery-viewer__thumb--active"
    ).nextElementSibling;
    while (!next || !next.offsetParent) {
      if (!next) {
        next = this.thumbContainer.firstElementChild;
      } else {
        next = next.nextElementSibling;
      }
    }
    this.selectThumb(next);
  }

  stopTrackingTouch() {
    this.pinchTracking.isTracking = false;
    this.touchTracking.isTracking = false;
  }

  trackInputMovement(evt) {
    evt.preventDefault();
    if (evt.type === "touchmove" && evt.touches.length > 0) {
      // pan
      const touch1 = evt.touches[0];
      if (!this.touchTracking.isTracking) {
        this.touchTracking.isTracking = true;
        this.touchTracking.lastTouchX = touch1.clientX;
        this.touchTracking.lastTouchY = touch1.clientY;
      } else {
        this.alterCurrentPanBy(
          (touch1.clientX - this.touchTracking.lastTouchX) *
          this.touchPanModifier,
          (touch1.clientY - this.touchTracking.lastTouchY) *
          this.touchPanModifier
        );
        this.touchTracking.lastTouchX = touch1.clientX;
        this.touchTracking.lastTouchY = touch1.clientY;
      }

      if (evt.touches.length === 2) {
        // pinch
        const touch2 = evt.touches[1];
        const pinchDistance = Math.sqrt(
          (touch1.clientX - touch2.clientX) ** 2 +
          (touch1.clientY - touch2.clientY) ** 2
        );
        if (!this.pinchTracking.isTracking) {
          this.pinchTracking.lastPinchDistance = pinchDistance;
          this.pinchTracking.isTracking = true;
        } else {
          const pinchDelta =
            pinchDistance - this.pinchTracking.lastPinchDistance;
          this.alterCurrentTransformZoomBy(
            pinchDelta * this.pinchZoomMultiplier
          );
          this.pinchTracking.lastPinchDistance = pinchDistance;
        }
      } else {
        this.pinchTracking.isTracking = false;
      }
    } else {
      // mousemove
      this.panZoomImageFromCoordinate(evt.clientX, evt.clientY);
    }
  }

  trackWheel(evt) {
    evt.preventDefault();
    if (evt.deltaY !== 0) {
      this.alterCurrentTransformZoomBy(evt.deltaY * this.wheelZoomMultiplier);
    }
  }

  onThumbClick(evt, thumb) {
    evt.preventDefault();
    this.selectThumb(thumb);
  }

  onZoomContainerClick(evt) {
    evt.preventDefault();

    if (this.currentTransform.zoom === 1.0) {
      this.currentTransform.zoom = 0;
      this.alterCurrentTransformZoomBy(0);
    } else {
      this.currentTransform.zoom = 1;
      this.alterCurrentTransformZoomBy(0);
      this.panZoomImageFromCoordinate(evt.clientX, evt.clientY);
    }
  }

  handleKeyup(evt) {
    switch (evt.key) {
      case "ArrowLeft":
        evt.preventDefault();
        this.selectPreviousThumb();
        break;
      case "ArrowRight":
        evt.preventDefault();
        this.selectNextThumb();
        break;
    }
  }
};

window.customElements.define("gallery-viewer", GalleryViewer);

const LinkDropdown = class extends HTMLElement {
  constructor() {
    super();

    this.open = false;
    this.button = this.querySelector(".link-dropdown__button");
    this.button.addEventListener("click", this.toggle.bind(this));
  }

  connectedCallback() {
    if (this.open) {
      this.addDismissListener();
    }
  }

  disconnectedCallback() {
    if (this.open) {
      this.removeDismissListener();
    }
  }

  toggle(evt, isDismiss) {
    if (!isDismiss) {
      evt.preventDefault();
      evt.stopPropagation();
    }

    const doExpand = this.button.getAttribute("aria-expanded") === "false";
    this.button.setAttribute("aria-expanded", doExpand);
    this.button.style.width = `${this.button.clientWidth}px`;

    let newWidth = null;
    const optsBox = this.button.nextElementSibling;
    const isLeftAligned = this.button
      .closest(".link-dropdown")
      .classList.contains("link-dropdown--left-aligned");
    if (!isLeftAligned) {
      if (doExpand) {
        newWidth = optsBox.clientWidth;
        // rtl - could be either side
        if (document.querySelector("html[dir=rtl]")) {
          newWidth += parseInt(getComputedStyle(optsBox).left, 10);
        } else {
          newWidth += parseInt(getComputedStyle(optsBox).right, 10);
        }
        newWidth -= parseInt(
          getComputedStyle(optsBox.querySelector(".link-dropdown__link"))
            .paddingInlineStart,
          10
        );
      } else {
        newWidth =
          parseInt(getComputedStyle(this.button).paddingInlineEnd, 10) +
          Math.ceil(
            this.button
              .querySelector(".link-dropdown__button-text")
              .getBoundingClientRect().width
          );
      }
      setTimeout(() => {
        this.button.style.width = `${newWidth}px`;
      }, 10);
    }

    if (doExpand) {
      this.open = true;
      this.addDismissListener();
    } else {
      this.open = false;
      this.removeDismissListener();
    }
  }

  addDismissListener() {
    this.dismissCallback = this.toggle.bind(this, true);
    document.addEventListener("click", this.dismissCallback);
  }

  removeDismissListener() {
    document.removeEventListener("click", this.dismissCallback);
    this.dismissCallback = null;
  }
};

window.customElements.define("link-dropdown", LinkDropdown);

const MainNavigation = class extends HTMLElement {
  constructor() {
    super();

    this.navHoverDelay = 250;
    this.navLastOpenDropdown = null;
    this.navOpenTimeoutId = -1;

    // hover
    this.querySelectorAll(
      ".navigation__tier-1 > .navigation__item--with-children"
    ).forEach((el) => {
      el.addEventListener("mouseenter", this.onNavParentHoverIn.bind(this));
    });
    this.querySelectorAll(
      ".navigation__tier-1 > .navigation__item--with-children"
    ).forEach((el) => {
      el.addEventListener("mouseleave", this.onNavParentHoverOut.bind(this));
    });

    // touch
    theme.addDelegateEventListener(
      this,
      "touchstart",
      ".navigation__tier-1 > .navigation__item--with-children > .navigation__link",
      (evt, el) => {
        this.handleTouch(evt, el);
      },
      { passive: true }
    );
    theme.addDelegateEventListener(
      this,
      "touchend",
      ".navigation__tier-1 > .navigation__item--with-children > .navigation__link",
      (evt, el) => {
        this.handleTouch(evt, el);
      }
    );
    theme.addDelegateEventListener(
      this,
      "click",
      ".navigation__tier-1 > .navigation__item--with-children > .navigation__link",
      this.onNavParentHoverIn.bind(this)
    );

    // keypress
    theme.addDelegateEventListener(
      this,
      "keydown",
      ".navigation__tier-1 > .navigation__item--with-children > .navigation__link",
      this.onNavKeydown.bind(this)
    );

    // click #-link - open child nav
    this.querySelectorAll(
      '.navigation__link[href="#"][aria-haspopup="true"]'
    ).forEach((el) => {
      el.addEventListener("click", (evt) => {
        evt.preventDefault();
        evt.currentTarget.nextElementSibling.dispatchEvent(
          new Event("click", { bubbles: true })
        );
      });
    });

    // transparent header hover
    this.addEventListener("mouseenter", this.handleNavHover.bind(this));
    this.addEventListener("mouseleave", this.handleNavHover.bind(this));
  }

  ensureDropdownsInPageBounds() {
    this.querySelectorAll(
      ".navigation__item--with-small-menu > .navigation__child-tier"
    ).forEach((el) => {
      const parentBcr = el.parentElement.getBoundingClientRect();
      const childBcr = el.getBoundingClientRect();
      const diff = window.innerWidth - (parentBcr.left + childBcr.width);
      if (diff < 25) {
        el.style.setProperty("--nav-side-offset", `${diff - 25}px`);
      }
    });
  }

  connectedCallback() {
    // shuffle dropdowns off RHS of viewport
    const debouncedEnsureDropdownsInPageBounds = debounce(
      this.ensureDropdownsInPageBounds.bind(this),
      300
    );
    debouncedEnsureDropdownsInPageBounds();
    this.resizeObserver = new ResizeObserver((entries) => {
      for (let i = 0; i < entries.length; i += 1) {
        const entry = entries[i];
        if (entry.contentBoxSize || entry.contentRect) {
          debouncedEnsureDropdownsInPageBounds();
        }
      }
    });
    this.resizeObserver.observe(this);

    // proxy for desktop nav interaction events
    this.proxyTier1Nav = document.getElementById(this.dataset.proxyNav);

    if (this.proxyTier1Nav) {
      // reposition small menu
      this.proxyTier1NavBoundEvents = [];

      const boundOnProxyNavEnterSmallMenu =
        this.onProxyNavEnterSmallMenu.bind(this);
      this.proxyTier1Nav
        .querySelectorAll(".navigation__item--with-small-menu")
        .forEach((el) => {
          el.addEventListener("mouseenter", boundOnProxyNavEnterSmallMenu);
          this.proxyTier1NavBoundEvents.push({
            element: el,
            name: "mouseenter",
            fn: boundOnProxyNavEnterSmallMenu,
          });
        });

      this.proxyTier1NavBoundEvents.push({
        element: this.proxyTier1Nav,
        name: "touchstart",
        fn: theme.addDelegateEventListener(
          this.proxyTier1Nav,
          "touchstart",
          ".navigation__item--with-small-menu",
          this.onProxyNavEnterSmallMenu.bind(this),
          { passive: true }
        ),
      });

      // transfer all events to main menu
      const onProxyNavEnterLeaveLargeMenu = (evt) => {
        const elIndex = [...evt.currentTarget.parentNode.children].indexOf(
          evt.currentTarget
        );
        this.querySelectorAll(".navigation__tier-1 > .navigation__item")[
          elIndex
        ].dispatchEvent(new Event(evt.type));
      };
      this.proxyTier1Nav
        .querySelectorAll(
          ".navigation__tier-1 > .navigation__item--with-children"
        )
        .forEach((el) => {
          el.addEventListener("mouseenter", onProxyNavEnterLeaveLargeMenu);
          this.proxyTier1NavBoundEvents.push({
            element: el,
            name: "mouseenter",
            fn: onProxyNavEnterLeaveLargeMenu,
          });
          el.addEventListener("mouseleave", onProxyNavEnterLeaveLargeMenu);
          this.proxyTier1NavBoundEvents.push({
            element: el,
            name: "mouseleave",
            fn: onProxyNavEnterLeaveLargeMenu,
          });
        });

      const eventNames = ["touchstart", "touchend", "click"];
      for (let i = 0; i < eventNames.length; i += 1) {
        const eventName = eventNames[i];
        this.proxyTier1NavBoundEvents.push({
          element: this.proxyTier1Nav,
          name: eventName,
          fn: theme.addDelegateEventListener(
            this.proxyTier1Nav,
            eventName,
            ".navigation__tier-1 > .navigation__item--with-children > .navigation__link",
            (evt, el) => {
              const elIndex = [...el.parentNode.parentNode.children].indexOf(
                el.parentNode
              );
              const proxiedLink = this.querySelectorAll(
                ".navigation__tier-1 > .navigation__item"
              )[elIndex].firstElementChild;
              this.handleTouch(evt, proxiedLink);
            },
            { passive: eventName === "touchstart" }
          ),
        });
      }

      this.proxyTier1NavBoundEvents.push({
        element: this.proxyTier1Nav,
        name: "keydown",
        fn: theme.addDelegateEventListener(
          this.proxyTier1Nav,
          "keydown",
          ".navigation__tier-1 > .navigation__item--with-children > .navigation__link",
          (evt, el) => {
            if (evt.key === "Enter") {
              const elIndex = [...el.parentNode.parentNode.children].indexOf(
                el.parentNode
              );
              const proxiedLink = this.querySelectorAll(
                ".navigation__tier-1 > .navigation__item"
              )[elIndex].firstElementChild;
              el.setAttribute(
                "aria-expanded",
                !proxiedLink.parentElement.classList.contains(
                  "navigation__item--show-children"
                )
              );
              this.onNavKeydown(evt, proxiedLink);
            }
          }
        ),
      });

      // transparent header hover
      const boundHandleNavHover = this.handleNavHover.bind(this);
      this.proxyTier1Nav.addEventListener("mouseenter", boundHandleNavHover);
      this.proxyTier1Nav.addEventListener("mouseleave", boundHandleNavHover);
      this.proxyTier1NavBoundEvents.push({
        element: this.proxyTier1Nav,
        name: "mouseenter",
        fn: boundHandleNavHover,
      });
      this.proxyTier1NavBoundEvents.push({
        element: this.proxyTier1Nav,
        name: "mouseleave",
        fn: boundHandleNavHover,
      });
    }

    // create mobile nav
    const tDiv = document.createElement("div");
    tDiv.innerHTML = this.querySelector(
      ".mobile-navigation-drawer-template"
    ).innerHTML;
    this.mobileDrawer = tDiv.firstElementChild;

    // add announcement bar items
    const mobileDrawerFooter = this.mobileDrawer.querySelector(
      ".mobile-navigation-drawer__footer"
    );
    const annBarMenu = document.querySelector(".announcement-bar .inline-menu");
    if (annBarMenu) {
      const clone = annBarMenu.cloneNode(true);
      clone.classList.remove("desktop-only");
      mobileDrawerFooter.appendChild(clone);
    }

    const annBarLocalizations = document.querySelector(
      ".announcement-bar .header-localization"
    );
    if (annBarLocalizations) {
      const clone = annBarLocalizations.cloneNode(true);
      clone.classList.remove("desktop-only");
      mobileDrawerFooter.appendChild(clone);

      clone.querySelector("form").addEventListener("change", (evt) => {
        const input = evt.target.previousElementSibling;
        if (input && input.tagName === "INPUT") {
          input.value = evt.detail.selectedValue;
          evt.currentTarget.submit();
        }
      });
    }

    const annBarSocial = document.querySelector(".announcement-bar .social");
    if (annBarSocial) {
      const clone = annBarSocial.cloneNode(true);
      clone.classList.remove("desktop-only");
      mobileDrawerFooter.appendChild(clone);
    }

    // ensure ids are unique
    theme.suffixIds(this.mobileDrawer, "MobileNav");

    // insert into page
    document
      .querySelector(".section-header")
      .insertAdjacentElement("afterend", this.mobileDrawer);

    // event: open second tier
    theme.addDelegateEventListener(
      this.mobileDrawer,
      "click",
      ".navigation__tier-1 > .navigation__item > .navigation__children-toggle",
      (evt, delEl) => {
        evt.preventDefault();

        // set text in header
        delEl.parentElement.classList.add("navigation__item--open");
        this.mobileDrawer.classList.add("mobile-navigation-drawer--child-open");
        this.mobileDrawer.querySelector(".mobile-nav-title").innerText =
          delEl.previousElementSibling.innerText;

        // position under header
        delEl.nextElementSibling.style.top = `${Math.ceil(
          this.mobileDrawer.querySelector(".navigation__mobile-header")
            .clientHeight + 1
        )}px`;

        // scroll container
        this.mobileDrawer
          .closest(".mobile-navigation-drawer")
          .scrollTo({ top: 0, left: 0, behavior: "instant" }); // 'smooth' not working in iOS 15
      }
    );

    if (this.mobileDrawer.dataset.mobileExpandWithEntireLink === "true") {
      theme.addDelegateEventListener(
        this.mobileDrawer,
        "click",
        ".navigation__item--with-children > .navigation__link",
        (evt, delEl) => {
          evt.preventDefault();
          delEl.nextElementSibling.dispatchEvent(
            new Event("click", { bubbles: true, cancelable: true })
          );
        }
      );
    } else {
      theme.addDelegateEventListener(
        this.mobileDrawer,
        "click",
        '.navigation__item--with-children > .navigation__link[href="#"]',
        (evt, delEl) => {
          evt.preventDefault();
          delEl.nextElementSibling.dispatchEvent(
            new Event("click", { bubbles: true, cancelable: true })
          );
        }
      );
    }

    // event: close second tier
    theme.addDelegateEventListener(
      this.mobileDrawer,
      "click",
      ".mobile-nav-back",
      (evt) => {
        evt.preventDefault();
        this.mobileDrawer.classList.remove(
          "mobile-navigation-drawer--child-open"
        );
        this.mobileDrawer
          .querySelectorAll(".navigation__tier-1 > .navigation__item--open")
          .forEach((el) => {
            el.classList.remove("navigation__item--open");
          });
      }
    );

    // event: toggle third tier
    theme.addDelegateEventListener(
      this.mobileDrawer,
      "click",
      ".navigation__tier-2 > .navigation__item > .navigation__children-toggle",
      (evt, delEl) => {
        evt.preventDefault();
        const doOpen = !delEl.parentElement.classList.contains(
          "navigation__item--open"
        );

        if (doOpen) {
          delEl.parentElement.classList.add("navigation__item--open");
          delEl.nextElementSibling.style.height = `${delEl.nextElementSibling.firstElementChild.clientHeight}px`;
        } else {
          delEl.parentElement.classList.remove("navigation__item--open");
          delEl.nextElementSibling.style.height = "";
        }
      }
    );
  }

  handleNavHover(evt) {
    this.closest(".section-header").classList.toggle(
      "section-header--nav-hover",
      evt.type === "mouseenter"
    );
  }

  onNavParentHoverIn(evt) {
    const dropdownContainer = evt.currentTarget;
    clearTimeout(this.navOpenTimeoutId);
    clearTimeout(dropdownContainer.dataset.navCloseTimeoutId);
    const openSiblings = [...dropdownContainer.parentNode.children].filter(
      (child) =>
        child !== dropdownContainer &&
        child.classList.contains("navigation__item--show-children")
    );

    // close all menus but last opened
    openSiblings
      .filter((el) => el !== this.navLastOpenDropdown)
      .forEach((el) => el.classList.remove("navigation__item--show-children"));
    this.navLastOpenDropdown = dropdownContainer;

    // show after a delay, based on first-open or not
    const timeoutDelay = openSiblings.length === 0 ? 0 : this.navHoverDelay;

    // open it
    const newNavOpenTimeoutId = setTimeout(() => {
      [...dropdownContainer.parentNode.children].forEach((el) => {
        if (el === dropdownContainer) {
          el.classList.add("navigation__item--show-children");
        } else {
          el.classList.remove("navigation__item--show-children");
        }
      });
      dropdownContainer
        .closest(".section-header")
        .classList.add("section-header--nav-open");
    }, timeoutDelay);

    this.navOpenTimeoutId = newNavOpenTimeoutId;
    dropdownContainer.dataset.navOpenTimeoutId = newNavOpenTimeoutId;

    dropdownContainer.firstElementChild.setAttribute("aria-expanded", true);
  }

  onNavParentHoverOut(evt) {
    // cancel opening, close after delay, and clear transforms
    const dropdownContainer = evt.currentTarget;
    clearTimeout(dropdownContainer.dataset.navOpenTimeoutId);
    dropdownContainer.dataset.navCloseTimeoutId = setTimeout(() => {
      dropdownContainer.classList.remove("navigation__item--show-children");
      dropdownContainer
        .closest(".section-header")
        .classList.remove("section-header--nav-open");
    }, this.navHoverDelay);

    dropdownContainer.firstElementChild.setAttribute("aria-expanded", false);
  }

  handleTouch(evt, link) {
    if (window.innerWidth > 767) {
      if (evt.type === "touchstart") {
        link.dataset.touchstartedAt = evt.timeStamp.toString();
      } else if (evt.type === "touchend") {
        // down & up in under a second - presume tap
        if (evt.timeStamp - parseInt(link.dataset.touchstartedAt, 10) < 1000) {
          link.dataset.touchOpenTriggeredAt = evt.timeStamp.toString();
          if (
            link.parentElement.classList.contains(
              "navigation__item--show-children"
            )
          ) {
            // trigger close
            link.parentElement.dispatchEvent(new Event("mouseleave"));
          } else {
            // trigger close on any open items
            this.querySelectorAll(".navigation__item--show-children").forEach(
              (el) => el.dispatchEvent(new Event("mouseleave"))
            );
            // trigger open
            link.parentElement.dispatchEvent(new Event("mouseenter"));
          }
          // prevent fake click
          evt.preventDefault();
          evt.stopPropagation();
        }
      } else if (evt.type === "click") {
        // if touch open was triggered very recently, prevent click event
        if (
          link.dataset.touchOpenTriggeredAt &&
          evt.timeStamp - parseInt(link.dataset.touchOpenTriggeredAt, 10) < 1000
        ) {
          evt.preventDefault();
          evt.stopPropagation();
        }
      }
    }
  }

  // eslint-disable-next-line class-methods-use-this
  onNavKeydown(evt, el) {
    if (evt.key === "Enter") {
      if (
        el.parentElement.classList.contains("navigation__item--show-children")
      ) {
        el.parentElement.dispatchEvent(new Event("mouseleave"));
      } else {
        el.parentElement.dispatchEvent(new Event("mouseenter"));
      }
      evt.preventDefault();
    }
  }

  onProxyNavEnterSmallMenu(evt, delEl) {
    const el = delEl || evt.currentTarget;
    const elIndex = [...el.parentNode.children].indexOf(el);
    const dropdown = this.querySelectorAll(
      ".navigation__tier-1 > .navigation__item"
    )[elIndex].querySelector(".navigation__tier-2-container");
    if (document.querySelector('html[dir="rtl"]')) {
      dropdown.style.right = `${dropdown.offsetParent.clientWidth -
        (theme.getOffsetLeftFromDoc(el) + el.clientWidth)
        }px`;
    } else {
      dropdown.style.left = `${theme.getOffsetLeftFromDoc(el)}px`;
    }
  }

  disconnectedCallback() {
    // remove externalities
    this.mobileDrawer.remove();

    if (this.proxyTier1NavBoundEvents) {
      for (let i = 0; i < this.proxyTier1NavBoundEvents.length; i += 1) {
        this.proxyTier1NavBoundEvents[i].element.removeEventListener(
          this.proxyTier1NavBoundEvents[i].name,
          this.proxyTier1NavBoundEvents[i].fn
        );
      }
    }
  }
};

window.customElements.define("main-navigation", MainNavigation);

const ProductBlock = class extends HTMLElement {
  constructor() {
    super();
    window.initLazyScript(this, this.init.bind(this));
  }

  init() {
    this.images = Array.from(this.querySelectorAll(".product-block__image"));
    if (this.images.length > 1) {
      this.initImagePagination();
      this.monitorSwatchSelection();
      this.initCustomLazyLoading();
    }
  }

  initCustomLazyLoading() {
    // preload hover images after a delay
    setTimeout(() => {
      this.querySelector(
        ".product-block__image--show-on-hover"
      ).classList.remove("product-block__image--inactivated");
    }, 1000);
    // activate all images after first interaction
    this.boundLazyLoadAll = this.lazyLoadAll.bind(this);
    this.addEventListener("mouseenter", this.boundLazyLoadAll);
    this.addEventListener("touchstart", this.boundLazyLoadAll, {
      passive: true,
    });
  }

  lazyLoadAll() {
    this.querySelectorAll(".product-block__image--inactivated").forEach((el) =>
      el.classList.remove("product-block__image--inactivated")
    );
    this.removeEventListener("mouseenter", this.boundLazyLoadAll);
    this.removeEventListener("touchstart", this.boundLazyLoadAll);
  }

  // get image at index, safely wrapping index
  getImageAt(index) {
    // positive modulo, like it should be, JS does not do *maths* correctly
    return this.images[
      ((index % this.images.length) + this.images.length) % this.images.length
    ];
  }

  incrementActiveImage(increment) {
    let index = 0;
    for (let i = 0; i < this.images.length; i += 1) {
      if (this.images[i].classList.contains("product-block__image--active")) {
        index = i;
        break;
      }
    }
    this.setActiveImage(index + increment);
  }

  setActiveImage(index) {
    const newActiveImage = this.getImageAt(index);
    const hoverImage = this.getImageAt(index + 1);

    // set new active image visibility
    [...newActiveImage.parentElement.children].forEach((el) => {
      el.classList.toggle(
        "product-block__image--active",
        el === newActiveImage
      );
      el.classList.toggle(
        "product-block__image--show-on-hover",
        el === hoverImage
      );
    });

    // dots
    this.querySelectorAll(".product-block__image-dot").forEach((el, iter) => {
      el.classList.toggle("product-block__image-dot--active", iter === index);
    });
  }

  initImagePagination() {
    // next button
    this.querySelector(".image-page-button--next").addEventListener(
      "click",
      (evt) => {
        evt.preventDefault();
        this.incrementActiveImage(1);
      }
    );

    // previous button
    this.querySelector(".image-page-button--previous").addEventListener(
      "click",
      (evt) => {
        evt.preventDefault();
        this.incrementActiveImage(-1);
      }
    );

    // swipe (when not in a carousel)
    if (!this.closest(".carousel, .product-grid--scrollarea")) {
      const touchContainer = this.querySelector(
        ".image-cont--with-secondary-image"
      );
      touchContainer.addEventListener(
        "touchstart",
        (evt) => {
          // swipe may end in another block
          theme.productBlockTouchTracking = true;
          theme.productBlockTouchStartX = evt.touches[0].clientX;
          theme.productBlockTouchStartY = evt.touches[0].clientY;
        },
        { passive: true }
      );

      touchContainer.addEventListener(
        "touchmove",
        (evt) => {
          if (theme.productBlockTouchTracking) {
            if (
              Math.abs(evt.touches[0].clientY - theme.productBlockTouchStartY) <
              30
            ) {
              const deltaX =
                evt.touches[0].clientX - theme.productBlockTouchStartX;
              if (deltaX > 25) {
                this.incrementActiveImage(-1);
                theme.productBlockTouchTracking = false;
              } else if (deltaX < -25) {
                this.incrementActiveImage(1);
                theme.productBlockTouchTracking = false;
              }
            }
          }
        },
        { passive: true }
      );

      touchContainer.addEventListener("touchend", () => {
        theme.productBlockTouchTracking = false;
      });
    }
  }

  onSelectSwatch(evt) {
    evt.preventDefault();
    let index = -1;

    for (let i = 0; i < this.images.length; i += 1) {
      if (this.images[i].dataset.mediaId === evt.currentTarget.dataset.media) {
        index = i;
        break;
      }
    }

    if (index < 0) return;

    this.setActiveImage(index);

    // include in URL
    const { optionName } = evt.currentTarget.closest(
      ".product-block-options"
    ).dataset;
    const optionValue = evt.currentTarget.dataset.optionItem;
    this.querySelectorAll(".product-link, .quickbuy-toggle").forEach((el) => {
      const url = new URL(el.href);
      const params = new URLSearchParams(url.search);
      params.set(optionName, optionValue);
      el.href = `${url.pathname}?${params}`;
      el.rel = "nofollow";
    });
  }

  monitorSwatchSelection() {
    this.querySelectorAll("[data-media].product-block-options__item").forEach(
      (el) => {
        el.addEventListener("mouseenter", this.onSelectSwatch.bind(this));
        el.addEventListener("click", this.onSelectSwatch.bind(this));
      }
    );
  }
};

window.customElements.define("product-block", ProductBlock);

const MediaGallery = class extends HTMLElement {
  constructor() {
    super();
    this.section = this.closest(".js-product");
    this.mainImageContainer = this.querySelector(".main-image");
    this.slideshow = this.mainImageContainer.querySelector(
      ".main-image carousel-slider"
    );
    this.collage = this.querySelector(".product-media-collage");
    this.xrButton = this.querySelector("[data-shopify-xr]");
    this.variantPicker = this.section.querySelector("variant-picker");
    this.mediaGroupingEnabled =
      this.variantPicker &&
      this.hasAttribute("data-media-grouping-enabled") &&
      this.getMediaGroupData();
    this.mediaLists = [
      this.querySelectorAll(".main-image .slider__item"),
      this.querySelectorAll(".product-media-collage__item"),
      this.querySelectorAll(".thumbnails .slider__item"),
    ];
    this.originalCollageItems = this.collage
      ? Array.from(this.collage.querySelectorAll(".product-media-collage__item"))
      : [];

    if (this.variantPicker) {
      const toLoad = [this.variantPicker];
      if (this.slideshow.offsetParent) {
        toLoad.push(this.slideshow);
      }
      const thumbnailSlider = this.querySelector("carousel-slider.thumbnails");
      if (thumbnailSlider && thumbnailSlider.offsetParent) {
        toLoad.push(thumbnailSlider);
      }
      theme.whenComponentLoaded(toLoad, this.setFromVariantPicker.bind(this));
    }

    if (this.hasAttribute("data-zoom-enabled")) {
      this.galleryModal = this.querySelector(
        ".js-media-zoom-template"
      ).content.firstElementChild.cloneNode(true);
      this.mediaLists.push(
        this.galleryModal.querySelectorAll(".gallery-viewer__thumb")
      );
      theme.addDelegateEventListener(
        this,
        "click",
        ".show-gallery",
        this.openGalleryViewer.bind(this)
      );
    }

    this.section.addEventListener(
      "on:carousel-slider:select",
      this.selectMainMedia.bind(this)
    );
    theme.addDelegateEventListener(
      this,
      "click",
      ".thumbnail",
      this.selectThumbnail.bind(this)
    );
    this.section.addEventListener(
      "on:variant:change",
      this.onVariantChange.bind(this)
    );

    // preload slideshow images after a short delay, if slideshow is visible
    setTimeout(() => {
      if (this.slideshow.offsetParent) {
        this.slideshow.querySelectorAll(".theme-img").forEach((el) => {
          el.loading = "eager";
        });
      }
    }, 3000);
  }

  setFromVariantPicker() {
    this.defaultVariantOptions = this.variantPicker.getSelectedOptions();

    if (this.mediaGroupingEnabled) {
      this.setMediaGroupOnFirstLoad();
    } else {
      // For two-material products with Standard/Diamantkedja: use the same
      // original-media-order / variant-anchor slicing as the five-material
      // collage, but ONLY when the desktop collage is displayed.
      const desktopCollageVisible = Boolean(this.collage && this.collage.offsetParent);
      const variant = this.variantPicker.getSelectedVariant();
      const filteredOnDesktop = desktopCollageVisible &&
        this.filterDesktopTwoMaterialChainMedia({
          detail: {
            variant,
            selectedOptions: this.variantPicker.getSelectedOptions(),
            allVariants: this.variantPicker.data?.variants || [],
          },
        });
      if (variant && variant.featured_media) {
        // All other product types keep the original first-load behavior.
        this.setActiveMedia(variant.featured_media.id,
          !desktopCollageVisible || !filteredOnDesktop);
      }
    }
  }

  /**
   * Run on first load to set initial media group and focussed image
   */
  setMediaGroupOnFirstLoad() {
    this.setActiveMediaGroup(this.getMediaGroupFromOptionSelectors());

    // Find which variant is selected and set active media.
    const variant = this.variantPicker.getSelectedVariant();
    if (variant && variant.featured_media) {
      this.setActiveMedia(variant.featured_media.id, true);
      return;
    }

    // Default to first visible image.
    this.setActiveMedia(
      this.querySelector(".thumbnails .slider__item:not([hidden])").dataset
        .mediaId,
      false
    );
  }

  /**
   * Open large image viewer.
   * @param {Event} evt - click event for image
   * @param {Element} imageLink - link clicked on
   */
  openGalleryViewer(evt, imageLink) {
    evt.preventDefault();

    // add to document on first open
    if (!this.galleryModal.parentElement) {
      document.body.appendChild(this.galleryModal);
    }

    this.galleryModal.open(imageLink);
    const viewer = this.galleryModal.querySelector("gallery-viewer");
    viewer.init(imageLink.getAttribute("href"));
    viewer.focus();
  }

  /**
   * Handle a change in variant on the page.
   * @param {Event} evt - variant change event dispatched by variant-picker
   */
  onVariantChange(evt) {
    if (this.mediaGroupingEnabled) {
      this.setActiveMediaGroup(this.getMediaGroupFromOptionSelectors(evt));
    }

    const desktopCollageVisible = Boolean(
      this.collage && this.collage.offsetParent
    );

    if (evt.detail.variant && evt.detail.variant.featured_media) {
      // Desktop only: use the theme's native grouping for grouped products.
      // For ordinary products, keep all media visible and simply move the
      // selected variant image to the start of the desktop collage.
      if (desktopCollageVisible) {
        if (this.mediaGroupingEnabled) {
          this.filterDesktopVariantMedia(evt);
        } else if (!this.filterDesktopTwoMaterialChainMedia(evt)) {
          // Keep the original ungrouped behavior on every other product.
          this.collage.append(...this.originalCollageItems);
          this.originalCollageItems.forEach((item) => { item.hidden = false; });
          const selectedMediaId = String(evt.detail.variant.featured_media.id);
          const selectedCollageMedia = this.originalCollageItems.find(
            (item) => item.dataset.mediaId === selectedMediaId
          );
          if (selectedCollageMedia) {
            this.collage.prepend(selectedCollageMedia);
          }
        }
      }
      this.setActiveMedia(
        evt.detail.variant.featured_media.id,
        !desktopCollageVisible
      );
    } else if (!this.mediaGroupingEnabled && desktopCollageVisible &&
               this.filterDesktopTwoMaterialChainMedia(evt)) {
      // Desktop two-material grouping has been updated without a featured image.
    } else if (this.mediaGroupChanged) {
      // Default to the first visible image if the current variant has no image.
      this.setActiveMedia(
        this.querySelector(".thumbnails .slider__item:not([hidden])").dataset
          .mediaId,
        !desktopCollageVisible
      );
    }
  }

  /**
   * Gets the media group from currently selected variant options.
   * @param {Event} evt - variant change event dispatched by variant-picker
   * @returns {?object}
   */
  getMediaGroupFromOptionSelectors(evt) {
    if (evt) {
      return evt.detail.selectedOptions[
        this.getMediaGroupData().groupOptionIndex
      ];
    }

    return this.variantPicker.getSelectedOptions()[
      this.getMediaGroupData().groupOptionIndex
    ];
  }

  /**
   * Gets the variant media associations for a product.
   * @returns {?object}
   */
  getMediaGroupData() {
    if (typeof this.variantMediaData === "undefined") {
      const dataEl = this.querySelector(".js-data-variant-media");
      if (dataEl) {
        this.variantMediaData = JSON.parse(dataEl.textContent);
      } else {
        this.variantMediaData = false;
      }
    }

    return this.variantMediaData;
  }

  /**
   * On the five-material diamond-chain product, Silverplätering and Rostfritt
   * stål may use the same Shopify featured image. The Liquid media grouping
   * then assigns that image to just one of the two material names.
   * Share the two silver media groups ONLY while viewing Rostfritt stål.
   * The mn-diamond-mode guard leaves ordinary two-material products unchanged.
   */
  getVisibleMediaGroups(groupName) {
    if (
      groupName === "Rostfritt stål" &&
      this.getMediaGroupData()?.groupOptionName === "Material" &&
      this.variantPicker?.querySelector(
        ".option-selector--material-cards.mn-diamond-mode"
      )
    ) {
      return ["Rostfritt stål", "Silverplätering"];
    }
    return [groupName];
  }

  /**
   * Show only images associated to the current variant.
   * @param {string} groupName - optional - Group to show (uses this.currentItem if empty)
   */
  setActiveMediaGroup(groupName) {
    this.mediaGroupChanged = this.currentMediaGroup !== groupName;
    this.currentMediaGroup = groupName;

    if (!this.mediaGroupChanged) return;

    if (!groupName) {
      this.mediaLists.forEach((list) => {
        list.forEach((mediaItem) => {
          mediaItem.hidden = false;
        });
      });
      return;
    }

    const mediaGroupData = this.getMediaGroupData();
    const visibleGroups = this.getVisibleMediaGroups(groupName);

    this.mediaLists.forEach((list) => {
      // Set hidden
      list.forEach((el) => {
        const mediaItemGroup = mediaGroupData.media[el.dataset.mediaId].group;
        const showThis =
          visibleGroups.includes(mediaItemGroup) || mediaItemGroup === true;
        el.hidden = !showThis;
      });

      // Move all-groups media to start/end
      [...list].forEach((el) => {
        if (mediaGroupData.media[el.dataset.mediaId].group === true) {
          if (mediaGroupData.media[el.dataset.mediaId].position === "start") {
            el.parentElement.prepend(el);
          } else {
            el.parentElement.append(el);
          }
        }
      });

      // Move hidden elements to the end (to allow nth-child CSS in layouts)
      [...list]
        .filter((el) => el.hidden)
        .forEach((el) => el.parentElement.append(el));

      // Refresh slider
      if (list.length > 0) {
        const slider = list[0].closest("carousel-slider");
        if (slider && slider.offsetParent) {
          slider.refresh();
        }
      }
    });
  }

  /**
   * Reproduce the five-material theme's media-group map for two-material
   * Standard/Diamantkedja products, but in JS so nothing changes on mobile.
   * Shopify's featured media mark the start of each contiguous image group.
   * The media order is captured once, before any desktop reordering.
   */
  getDesktopTwoMaterialChainData() {
    if (this.desktopTwoMaterialChainData !== undefined) {
      return this.desktopTwoMaterialChainData;
    }
    this.desktopTwoMaterialChainData = null;
    if (this.mediaGroupingEnabled || !this.collage || !this.variantPicker) return null;

    const selectors = Array.from(this.variantPicker.optionSelectors ||
      this.variantPicker.querySelectorAll('.option-selector:not(.option-selector--custom)'));
    const materialIndex = selectors.findIndex((selector) =>
      selector.classList.contains('option-selector--material-cards--two'));
    const chainIndex = selectors.findIndex((selector) =>
      selector.classList.contains('option-selector--chain-cards'));
    if (materialIndex < 0 || chainIndex < 0) return null;

    const variants = this.variantPicker.data?.variants || [];
    const materials = new Set(variants.map((variant) => variant.options?.[materialIndex]));
    const chains = new Set(variants.map((variant) => variant.options?.[chainIndex]));
    if (materials.size !== 2 || !chains.has('Diamantkedja') ||
        !Array.from(chains).some((chain) => /standard/i.test(chain || ''))) return null;

    // Mirror the Liquid logic of main-product.liquid, which normally emits
    // .js-data-variant-media only for five-material diamond-chain products.
    const sortedVariants = [...variants].sort((a, b) =>
      String(a.options?.[materialIndex] || '').localeCompare(
        String(b.options?.[materialIndex] || ''), 'sv'));
    const media = {};
    let currentGroup = null;
    let standardMediaFound = false;
    const anchoredGroups = new Set();
    this.originalCollageItems.forEach((item) => {
      const mediaId = item.dataset.mediaId;
      const matches = sortedVariants.filter((variant) =>
        variant.featured_media && String(variant.featured_media.id) === mediaId);
      if (matches.length) {
        // If a featured photo is shared by both materials, prefer whichever
        // group was last in Shopify's sorted material variant list (Liquid).
        currentGroup = matches[matches.length - 1].options[materialIndex];
        anchoredGroups.add(currentGroup);
      }
      const alt = item.querySelector('img[alt]')?.getAttribute('alt') || '';
      if (/all-groups/i.test(alt)) {
        media[mediaId] = { group: true, position: standardMediaFound ? 'end' : 'start' };
      } else {
        standardMediaFound = true;
        media[mediaId] = { group: currentGroup };
      }
    });
    // Do not filter by a guessed material if Shopify has not supplied
    // distinct image anchors for both materials.
    if (anchoredGroups.size !== 2) return null;

    this.desktopTwoMaterialChainData = { groupOptionIndex: materialIndex,
      chainIndex, media };
    return this.desktopTwoMaterialChainData;
  }

  /**
   * Apply exactly the same group + featured-image-boundary slicing principle
   * as filterDesktopVariantMedia(), ONLY to the desktop collage of two-
   * material Standard/Diamantkedja products. The mobile slider and thumbnails
   * are deliberately not modified.
   * @returns {boolean} true only when the desktop two-material filter applied.
   */
  filterDesktopTwoMaterialChainMedia(evt) {
    if (!this.collage || !this.collage.offsetParent) return false;
    const data = this.getDesktopTwoMaterialChainData();
    if (!data) return false;

    const selectedOptions = evt.detail?.selectedOptions ||
      this.variantPicker.getSelectedOptions();
    const selectedMaterial = selectedOptions[data.groupOptionIndex];
    const selectedVariant = evt.detail?.variant ||
      this.variantPicker.getSelectedVariant();
    if (!selectedMaterial || !selectedVariant?.featured_media) return false;

    const selectedId = String(selectedVariant.featured_media.id);
    const groupItems = this.originalCollageItems.filter((item) =>
      data.media[item.dataset.mediaId]?.group === selectedMaterial);
    const start = groupItems.findIndex((item) => item.dataset.mediaId === selectedId);
    if (start < 0) return false;

    // In the five-material version, a non-default chain slices at the next
    // variant image. Apply that SAME boundary to either chain here, so the
    // default Standard group does not get mixed with Diamantkedja either.
    const variantIds = new Set((evt.detail?.allVariants ||
      this.variantPicker.data?.variants || [])
      .filter((variant) => variant.featured_media)
      .map((variant) => String(variant.featured_media.id)));
    const end = groupItems.findIndex((item, index) =>
      index > start && variantIds.has(item.dataset.mediaId));
    const visibleItems = groupItems.slice(start, end < 0 ? undefined : end);
    if (!visibleItems.length) return false;

    const sharedStart = this.originalCollageItems.filter((item) => {
      const m = data.media[item.dataset.mediaId];
      return m?.group === true && m.position === 'start';
    });
    const sharedEnd = this.originalCollageItems.filter((item) => {
      const m = data.media[item.dataset.mediaId];
      return m?.group === true && m.position !== 'start';
    });
    const visibleIds = new Set([...sharedStart, ...visibleItems, ...sharedEnd]
      .map((item) => item.dataset.mediaId));

    this.originalCollageItems.forEach((item) => {
      item.hidden = !visibleIds.has(item.dataset.mediaId);
    });
    this.collage.append(...visibleItems, ...sharedEnd,
      ...this.originalCollageItems.filter((item) => item.hidden));
    this.collage.prepend(...sharedStart);
    return true;
  }

  /**
   * Filters the desktop collage when a non-default secondary option is selected.
   * @param {Event} evt - Variant change event.
   */
  filterDesktopVariantMedia(evt) {
    const data = this.getMediaGroupData();
    const selectedOptions = evt.detail.selectedOptions || [];
    const secondaryIndex = selectedOptions.findIndex(
      (_option, index) => index !== data.groupOptionIndex
    );
    const showFullGroup =
      secondaryIndex < 0 ||
      selectedOptions[secondaryIndex] ===
        this.defaultVariantOptions[secondaryIndex];
    const visibleGroups = this.getVisibleMediaGroups(this.currentMediaGroup);
    const groupItems = this.originalCollageItems.filter(
      (item) => visibleGroups.includes(data.media[item.dataset.mediaId].group)
    );
    const sharedStart = this.originalCollageItems.filter((item) => {
      const media = data.media[item.dataset.mediaId];
      return media.group === true && media.position === "start";
    });
    const sharedEnd = this.originalCollageItems.filter((item) => {
      const media = data.media[item.dataset.mediaId];
      return media.group === true && media.position !== "start";
    });
    let visibleItems = groupItems;

    if (!showFullGroup) {
      const selectedId = evt.detail.variant.featured_media.id.toString();
      const start = groupItems.findIndex(
        (item) => item.dataset.mediaId === selectedId
      );
      const variantIds = new Set(
        (evt.detail.allVariants || [])
          .filter((variant) => variant.featured_media)
          .map((variant) => variant.featured_media.id.toString())
      );
      const end = groupItems.findIndex(
        (item, index) =>
          index > start && variantIds.has(item.dataset.mediaId)
      );

      if (start >= 0) {
        visibleItems = groupItems.slice(start, end < 0 ? undefined : end);
      }
    }

    const visibleIds = new Set(
      [...sharedStart, ...visibleItems, ...sharedEnd].map(
        (item) => item.dataset.mediaId
      )
    );

    this.originalCollageItems.forEach((item) => {
      item.hidden = !visibleIds.has(item.dataset.mediaId);
    });

    this.collage.append(
      ...visibleItems,
      ...sharedEnd,
      ...this.originalCollageItems.filter((item) => item.hidden)
    );
    this.collage.prepend(...sharedStart);
  }

  /**
   * Sets the active media item.
   * @param {number} mediaId - Media ID to set as active.
   * @param {boolean} [scrollToItem=true] - Scroll to the active media item.
   */
  setActiveMedia(mediaId, scrollToItem = true) {
    const strMediaId = mediaId.toString();
    const mediaEl = this.mainImageContainer.querySelector(
      `[data-media-id="${strMediaId}"]`
    );

    if (scrollToItem) {
  // Slideshow (if visible)
  if (
    this.slideshow.hasAttribute("loaded") &&
    this.slideshow.offsetParent
  ) {
    this.slideshow.scrollToElement(mediaEl, "instant");
  }

  // Collage (if visible)
  if (this.collage && this.collage.offsetParent) {
    const collageMediaEl = this.collage.querySelector(
      `[data-media-id="${strMediaId}"]`
    );

    if (collageMediaEl) {
      // Keep the original gallery navigation
      theme.scrollToRevealElement(collageMediaEl);

      clearTimeout(this.collage.dataset.highlightTimeoutId);

      // Remove the gray transition from all gallery images
      this.collage
        .querySelectorAll(".product-media-collage__item--highlight-off")
        .forEach((el) => {
          el.classList.remove(
            "product-media-collage__item--highlight-off"
          );
        });
    }
  }
}

    this.mediaLists.forEach((list) => {
      list.forEach((mediaItem) => {
        if (mediaItem.dataset.mediaId === strMediaId) {
          mediaItem.classList.add("is-active");
          const carousel = mediaItem.closest("carousel-slider");
          if (carousel && carousel.offsetParent) {
            carousel.scrollToElement(mediaItem);
          }
        } else {
          mediaItem.classList.remove("is-active");
        }
      });
    });

    // play media
    window.pauseAllMedia();
    if (mediaEl) {
      // deferred
      const deferredMedia = mediaEl.querySelector(
        "product-model"
      );
      if (deferredMedia) deferredMedia.loadContent(true);
    }

    // set model ID
    if (this.xrButton && this.mediaIsModel(mediaId)) {
      this.xrButton.dataset.shopifyModel3dId = mediaId;
    }
  }

  /**
   * Check if media is a 3D model
   * @param {number} mediaId - Media ID to check.
   * @returns {boolean} - If it is a 3D model.
   */
  mediaIsModel(mediaId) {
    return !!this.querySelector(
      `.product-media--model[data-model-id="${mediaId}"]`
    );
  }

  selectThumbnail(evt, thumbnail) {
    evt.preventDefault();
    this.setActiveMedia(thumbnail.parentNode.dataset.mediaId, true);
  }

  selectMainMedia(evt) {
    if (evt.detail.slide.dataset.mediaId) {
      this.setActiveMedia(evt.detail.slide.dataset.mediaId, false);
    }
  }
};

window.customElements.define("media-gallery", MediaGallery);

/* eslint-disable max-len */
/* eslint-disable no-multiple-empty-lines */
const ProductForm = class extends HTMLElement {
  constructor() {
    super();

    this.form = this.querySelector(".js-product-form");
    if (this.form) {
      const idInput = this.form.querySelector('[name="id"]');
      idInput.disabled = false;
      idInput.required = true;

      if (theme.settings.afterAddToCart !== "no-js") {
        this.submitBtn = this.querySelector('[name="add"]');
        this.form.addEventListener("submit", this.handleSubmit.bind(this));
      }
    }
  }

  /**
   * Handles submission of the product form.
   * @param {object} evt - Event object.
   */
  async handleSubmit(evt) {
    evt.preventDefault();
    if (this.submitBtn.getAttribute("aria-disabled") === "true") return;

    this.setErrorMsgState();

    const formValid = this.validate();
    if (!formValid) return;

    // Disable "Add to Cart" button until submission is complete.
    this.submitBtn.setAttribute("aria-disabled", "true");
    this.submitBtn.classList.add("is-loading");

    const formData = window.makCombineMultiNameFormData
      ? window.makCombineMultiNameFormData(new FormData(this.form))
      : new FormData(this.form);
    const sectionsToUpdate = ["page-header", "cart-drawer"]
      .map((sel) => document.querySelector(sel))
      .filter((el) => el);
    const sectionIds = sectionsToUpdate.map((el) => el.dataset.sectionId);

    formData.append("sections_url", window.location.pathname);
    formData.append("sections", sectionIds.join(","));
    console.log("formData: ", formData);

    const fetchRequestOpts = {
      method: "POST",
      headers: {
        Accept: "application/javascript",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: formData,
    };

    try {
      const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
      const data = await response.json();
      let error =
        typeof data.description === "string" ? data.description : data.message;
      if (data.errors && typeof data.errors === "object") {
        error = Object.entries(data.errors).map((item) => item[1].join(", "));
      }
      if (data.status) this.setErrorMsgState(error);
      if (!response.ok) throw new Error(response.status);

      if (document.querySelector(".template-cart")) {
        // Cart page
        const cartForm = document.querySelector("cart-form");
        if (cartForm && cartForm.enableAjaxUpdate) {
          cartForm.refresh();
        }
      } else if (theme.settings.afterAddToCart === "page") {
        // Allow the tick animation to complete
        setTimeout(() => {
          window.location.href = theme.routes.cart;
        }, 300);
      } else {
        sectionsToUpdate.forEach((el) => {
          el.updateFromCartChange(data.sections[el.dataset.sectionId]);
        });

        if (theme.settings.afterAddToCart === "notification") {
          const notification = document
            .getElementById("AddedNotification")
            .content.firstElementChild.cloneNode(true);
          notification.dataset.productTitle = data.product_title;
          let notificationContainer = document.querySelector(
            ".pageheader--sticky"
          );
          if (!notificationContainer) {
            notificationContainer = document.querySelector("body");
          }
          notificationContainer.appendChild(notification);
        } else if (theme.settings.afterAddToCart === "drawer") {
          document.querySelector(".js-cart-drawer").open();
        }
      }

      this.dispatchEvent(
        new CustomEvent("on:cart:add", {
          bubbles: true,
          detail: {
            variantId: data.variant_id,
          },
        })
      );

      // Re-enable 'Add to Cart' button.
      this.submitBtn.classList.add("is-success");
      this.submitBtn.removeAttribute("aria-disabled");
      setTimeout(() => {
        this.submitBtn.classList.remove("is-loading");
        this.submitBtn.classList.remove("is-success");
      }, 2000);
    } catch (error) {
      console.log(error); // eslint-disable-line
      this.dispatchEvent(
        new CustomEvent("on:cart:error", {
          bubbles: true,
          detail: {
            error: this.errorMsg.textContent,
          },
        })
      );

      // Re-enable 'Add to Cart' button.
      this.submitBtn.classList.remove("is-loading");
      this.submitBtn.classList.remove("is-success");
      this.submitBtn.removeAttribute("aria-disabled");
    }
  }

  /**
   * Shows/hides an error message.
   * @param {string} [error=false] - Error to show a message for.
   */
  setErrorMsgState(error = false) {
    this.errorMsg = this.errorMsg || this.querySelector(".js-form-error");
    if (!this.errorMsg) return;

    this.errorMsg.hidden = !error;
    if (error) {
      this.errorMsg.innerHTML = "";
      const errorArray = Array.isArray(error) ? error : [error];
      errorArray.forEach((err, index) => {
        if (index > 0) this.errorMsg.insertAdjacentHTML("beforeend", "<br>");
        this.errorMsg.insertAdjacentText("beforeend", err);
      });
    }
  }

  /**
   * Validate form & show errors.
   * @returns {boolean} - Is form valid.
   */
  validate() {
    let isValid = true;
    this.querySelectorAll("variant-picker .option-selector").forEach((el) => {
      if (el.dataset.selectorType === "listed") {
        const input = el.querySelector("input");
        const inputIsValid = input.reportValidity();
        if (!inputIsValid) {
          isValid = false;
        }
      } else {
        // Clear prior errors
        el.querySelectorAll(".label__prefix").forEach((label) =>
          label.remove()
        );
        el.querySelectorAll(".label--contains-error").forEach((label) =>
          label.classList.remove("label--contains-error")
        );

        const selectedOption = el.querySelector(
          '.custom-select .js-option[aria-selected="true"]'
        );
        if (!selectedOption) {
          const label = el.querySelector(".label");
          label.setAttribute("aria-live", "polite");
          label.classList.add("label--contains-error");
          const labelPrefix = document.createElement("span");
          labelPrefix.innerText = `${theme.strings.productsProductChooseA} `;
          labelPrefix.className = "label__prefix";
          label.prepend(labelPrefix);
          theme.scrollToRevealElement(el);
          isValid = false;
        }
      }
    });

    return isValid;
  }
};

window.customElements.define("product-form", ProductForm);

class ProductInventory extends HTMLElement {
  constructor() {
    super();
    window.initLazyScript(this, this.initLazySection.bind(this));
  }

  initLazySection() {
    this.threshold = parseInt(this.dataset.threshold, 10);
    this.productInventory = this.querySelector(".product-inventory");
    this.inventoryNotice = this.querySelector(".product-inventory__status");
    this.variantInventory = this.getVariantInventory();

    this.closest(".js-product").addEventListener(
      "on:variant:change",
      this.handleVariantChange.bind(this)
    );
  }

  /**
   * Gets the inventory data for all product variants
   * @returns {?object}
   */
  getVariantInventory() {
    const dataEl = this.querySelector('[type="application/json"]');
    return this.variantInventory || JSON.parse(dataEl.textContent);
  }

  /**
   * Handles a 'change' event on the variant picker element
   * @param {object} evt - Event object
   */
  handleVariantChange(evt) {
    this.updateInventory(
      evt.detail.variant
        ? this.variantInventory.find((v) => v.id === evt.detail.variant.id)
        : null
    );
  }

  /**
   *
   * Updates the inventory notice
   * @param {?object} inventory - the inventory data
   */
  updateInventory(inventory) {
    if (!inventory) {
      this.productInventory.hidden = true;
      return;
    }

    const count = inventory.inventory_quantity;
    const showCount =
      this.dataset.showInventoryCount === "always" ||
      (this.dataset.showInventoryCount === "low" && count <= this.threshold);

    let notice = null;
    if (showCount) {
      if (count <= this.threshold) {
        notice = this.dataset.textXLeftLow.replace("[QTY]", count);
      } else {
        notice = this.dataset.textXLeftOk.replace("[QTY]", count);
      }
    } else {
      // eslint-disable-next-line no-lonely-if
      if (count <= this.threshold) {
        notice = this.dataset.textLow;
      } else {
        notice = this.dataset.textOk;
      }
    }

    this.productInventory.classList.toggle(
      "product-inventory--low",
      count <= this.threshold
    );
    this.productInventory.classList.toggle(
      "product-inventory--ok",
      count > this.threshold
    );
    this.inventoryNotice.innerText = notice;
    this.productInventory.hidden = false;
  }
}

customElements.define("product-inventory", ProductInventory);

if (!customElements.get("quantity-wrapper")) {
  class QuantityWrapper extends HTMLElement {
    connectedCallback() {
      this.addEventListener("click", this.handleClick.bind(this));
    }

    handleClick(evt) {
      const btn = evt.target.closest("[data-quantity]");
      if (btn) {
        evt.preventDefault();
        const input = this.querySelector('[name="quantity"]');
        let change = btn.dataset.quantity === "up" ? 1 : -1;
        if (input.step) {
          change *= parseInt(input.step, 10);
        }
        input.value = Math.max(1, parseInt(input.value, 10) + change);
        input.dispatchEvent(
          new CustomEvent("change", { bubbles: true, cancelable: false })
        );
      }
    }
  }

  customElements.define("quantity-wrapper", QuantityWrapper);
}

if (theme.settings.quickbuyStyle !== "off") {
  theme.quickbuy = {
    quickbuyResizeObserver: null,
    init: () => {
      theme.quickbuy.quickbuyResizeObserver = new ResizeObserver((entries) => {
        for (let i = 0; i < entries.length; i += 1) {
          const entry = entries[i];
          if (entry.contentBoxSize || entry.contentRect) {
            theme.quickbuy.debouncedQuickbuyResize(entry.target);
          }
        }
      });

      theme.addDelegateEventListener(
        document,
        "click",
        ".quickbuy-toggle",
        (evt, delEl) => {
          const productUrl = delEl.href;

          // Only show dropdown if screen is large enough for it to be useful
          if (window.innerWidth >= 768) {
            evt.preventDefault();

            // cancel current request if one exists
            if (theme.quickbuy.currentRequestAbortController) {
              theme.quickbuy.currentRequestAbortController.abort();
            }
            theme.quickbuy.currentRequestAbortController =
              new AbortController();

            const block = delEl.closest(".product-block");
            const slider = delEl.closest(".collection-slider");
            let detailCont = null;
            let quickbuyCont = null;
            let sliderRow = null;

            // do different things if it's inside a slideshow
            if (slider) {
              sliderRow = slider.closest(".collection-slider-row");
              // slider without detail
              if (!sliderRow.querySelector(".quickbuy-container")) {
                return;
              }
              quickbuyCont = sliderRow.querySelector(".quickbuy-container");
            } else {
              theme.quickbuy.setBlockHeights(block);
              quickbuyCont = block.querySelector(".quickbuy-container");
            }
            detailCont = quickbuyCont.querySelector(".inner");

            // toggle active class on block
            block.classList.toggle("expanded");
            if (block.classList.contains("expanded")) {
              quickbuyCont.dispatchEvent(
                new CustomEvent("on:quickbuy:before-open", { bubbles: true })
              );

              // expanding
              if (slider) {
                // if another block is expanded, remove its expanded class
                const otherExpanded = Array.from(
                  slider.querySelectorAll(".product-block.expanded")
                ).filter((el) => el !== block);
                otherExpanded.forEach((el) => el.classList.remove("expanded"));

                // if expanding from empty, set initial detail container height to 0
                if (otherExpanded.length === 0) {
                  quickbuyCont.style.height = 0;
                }
              } else {
                // close expanded siblings
                [...block.parentElement.children].forEach((el) => {
                  if (el !== block && el.classList.contains("expanded")) {
                    theme.quickbuy.contractDetail(el, true);
                  }
                });
              }

              // monitor for contents changing size
              theme.quickbuy.quickbuyResizeObserver.disconnect();
              theme.quickbuy.quickbuyResizeObserver.observe(detailCont);

              // add spinner
              detailCont.innerHTML = '<div class="loading-spinner"></div>';

              // load in content
              fetch(productUrl, {
                method: "get",
                signal: theme.quickbuy.currentRequestAbortController.signal,
              })
                .then((response) => {
                  if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                  }
                  return response.text();
                })
                .then((response) => {
                  const tmpl = document.createElement("template");
                  tmpl.innerHTML = response;
                  const newDetail = tmpl.content
                    .querySelector(".quickbuy-content")
                    .cloneNode(true);

                  // convert to quickbuy content
                  newDetail.querySelectorAll(".more").forEach((el) => {
                    el.href = productUrl;
                  });
                  newDetail.querySelectorAll(".product-title").forEach((el) => {
                    el.innerHTML = `<a href="">${el.innerHTML}</a>`;
                    el.firstElementChild.href = productUrl;
                  });
                  newDetail.querySelectorAll(".show-gallery").forEach((el) => {
                    el.classList.remove("show-gallery");
                    el.href = productUrl;
                  });
                  newDetail
                    .querySelectorAll(".not-in-quickbuy")
                    .forEach((el) => {
                      el.remove();
                    });
                  newDetail
                    .querySelectorAll(".only-in-quickbuy")
                    .forEach((el) => {
                      el.classList.remove("only-in-quickbuy");
                    });
                  newDetail
                    .querySelectorAll(".sticky-content-container")
                    .forEach((el) => {
                      el.classList.remove("sticky-content-container");
                    });
                  newDetail
                    .querySelectorAll("pickup-availability")
                    .forEach((el) => {
                      el.remove();
                    });
                  newDetail
                    .querySelectorAll('variant-picker[data-update-url="true"]')
                    .forEach((el) => {
                      el.dataset.updateUrl = false;
                    });
                  newDetail
                    .querySelectorAll("noscript")
                    .forEach((el) => el.remove());

                  // enforce thumbnails-under gallery layout
                  newDetail
                    .querySelectorAll(
                      ".media-gallery .main-image carousel-slider"
                    )
                    .forEach((el) => {
                      el.classList.remove("mobile-only");
                    });
                  newDetail
                    .querySelectorAll(".product-media-collage")
                    .forEach((el) => {
                      el.remove();
                    });
                  newDetail
                    .querySelectorAll(".media-gallery .thumbnails")
                    .forEach((el) => {
                      el.classList.remove("mobile-only");
                    });
                  [
                    "media-gallery--layout-carousel-beside",
                    "media-gallery--layout-columns-1",
                    "media-gallery--layout-columns-2",
                    "media-gallery--layout-collage-1",
                    "media-gallery--layout-collage-2",
                  ].forEach((cl) => {
                    newDetail.querySelectorAll(`.${cl}`).forEach((el) => {
                      el.classList.remove(cl);
                      el.classList.add("media-gallery--layout-carousel-under");
                    });
                  });

                  // add any external resources
                  tmpl.content
                    .querySelectorAll(
                      '.section-main-product link[rel="stylesheet"]'
                    )
                    .forEach((el) => {
                      if (
                        !document.querySelector(
                          `link[rel="stylesheet"][href="${el.getAttribute(
                            "href"
                          )}"]`
                        )
                      ) {
                        document.head.insertAdjacentHTML(
                          "beforeend",
                          el.outerHTML
                        );
                      }
                    });
                  tmpl.content
                    .querySelectorAll(".section-main-product script[src]")
                    .forEach((el) => {
                      if (
                        !document.querySelector(
                          `script[src="${el.getAttribute("src")}"]`
                        )
                      ) {
                        const scr = document.createElement("script");
                        scr.src = el.getAttribute("src");
                        scr.defer = "defer";
                        document.head.appendChild(scr);
                      }
                    });

                  detailCont.innerHTML = "";
                  detailCont.appendChild(newDetail);

                  quickbuyCont.dispatchEvent(
                    new CustomEvent("on:quickbuy:after-open", { bubbles: true })
                  );
                });

              // enable close button
              const closeButton = quickbuyCont.querySelector(".close-detail");
              closeButton.removeAttribute("tabindex");
              closeButton.addEventListener(
                "click",
                theme.quickbuy.onCloseClick
              );

              // Scroll to an appropriate position
              const scrollOffset = -120 - theme.stickyHeaderHeight();
              if (slider) {
                // Simple for a carousel
                window.scrollTo({
                  top: theme.getOffsetTopFromDoc(quickbuyCont) + scrollOffset,
                  behavior: "smooth",
                });
              } else {
                // Grid - calculate where to scroll if quickbuys above this block will collapse
                theme.quickbuy.saveProductGridSpacingData();
                window.scrollTo({
                  top:
                    parseFloat(
                      block.dataset.qbScrollTo ||
                      theme.getOffsetTopFromDoc(quickbuyCont)
                    ) + scrollOffset,
                  behavior: "smooth",
                });
              }
            } else {
              // Close quick buy
              if (slider) {
                // collapse detail container & empty
                quickbuyCont.style.height = "0px";
                setTimeout(() => {
                  if (quickbuyCont.style.height === "0px") {
                    detailCont.innerHTML = "";
                  }
                }, parseFloat(getComputedStyle(quickbuyCont).transitionDuration) * 1000);
              } else {
                theme.quickbuy.contractDetail(block);
              }

              // scroll to top of closing block, after reflow
              setTimeout(() => {
                window.scrollTo({
                  top:
                    theme.getOffsetTopFromDoc(block) -
                    theme.stickyHeaderHeight() -
                    20,
                  behavior: "smooth",
                });
              }, 100);

              // disable close button
              const closeButton = quickbuyCont.querySelector(".close-detail");
              closeButton.setAttribute("tabindex", "-1");
              closeButton.removeEventListener(
                "click",
                theme.quickbuy.onCloseClick
              );
            }
          }
        }
      );
    },

    debouncedQuickbuyResizeTimeoutID: -1,
    debouncedQuickbuyResize: (qbInner) => {
      clearTimeout(theme.quickbuy.debouncedQuickbuyResizeTimeoutID);
      theme.quickbuy.debouncedQuickbuyResizeTimeoutID = setTimeout(() => {
        const qbc = qbInner.closest(".quickbuy-container");
        const block = qbc.closest(".product-block");
        if (block) {
          // use padding in grid
          // also check expanded class in case it's mid close transition
          if (block.classList.contains("expanded")) {
            const targetHeight = qbInner.clientHeight;
            block.style.paddingBottom = `${targetHeight + 20}px`;
            qbc.style.height = `${targetHeight}px`;
          }
        }
        if (qbInner.childElementCount > 0) {
          // use height in carousel
          // - if not empty (e.g. post-close tidy-up)
          qbc.style.height = `${qbInner.clientHeight}px`;
        }
      }, 100);
    },

    contractDetail: (block, instant) => {
      // Cancel everything
      theme.quickbuy.quickbuyResizeObserver.disconnect();
      clearTimeout(theme.quickbuy.debouncedQuickbuyResizeTimeoutID);

      block.classList.remove("expanded");
      const quickbuyCont = block.querySelector(".quickbuy-container");
      if (instant) {
        quickbuyCont.style.transitionDuration = "0ms";
        block.style.transitionDuration = "0ms";
      }
      quickbuyCont.style.height = 0;
      block.style.paddingBottom = 0;
      // empty .inner after transition
      const msToClose =
        parseFloat(getComputedStyle(quickbuyCont).transitionDuration) * 1000;
      setTimeout(() => {
        if (quickbuyCont.style.height === "0px") {
          quickbuyCont.querySelector(".inner").innerHTML = "";
        }
        if (instant) {
          quickbuyCont.style.transitionDuration = "";
          block.style.transitionDuration = "";
        }
      }, msToClose);

      quickbuyCont.dispatchEvent(
        new CustomEvent("on:quickbuy:after-close", { bubbles: true })
      );
    },

    saveProductGridSpacingData: () => {
      document.querySelectorAll(".product-grid").forEach((el) => {
        const blocks = el.querySelectorAll(".product-block .block-inner");
        if (blocks.length <= 1) return; // Skip for empty colls
        let row = 0;
        let currTop = 0;
        let runningHeight = 0;
        const gutter = parseFloat(getComputedStyle(el).rowGap);
        const listingOffset = theme.getOffsetTopFromDoc(blocks[0]);
        const rowOffsets = [listingOffset];
        blocks.forEach((block, index) => {
          if (block.clientHeight > runningHeight) {
            runningHeight = block.clientHeight;
          }

          const currOffsetTop = theme.getOffsetTopFromDoc(block);
          if (index === 0) {
            currTop = currOffsetTop;
          } else if (currOffsetTop > currTop) {
            row += 1;
            currTop = currOffsetTop;
            rowOffsets.push(gutter + runningHeight + rowOffsets[row - 1]);
            runningHeight = 0;
          }

          block.dataset.gridRow = row;
        });

        blocks.forEach((block) => {
          block.dataset.qbScrollTo =
            rowOffsets[block.dataset.gridRow] + block.clientHeight;
        });
      });
    },

    setBlockHeights: (block) => {
      // Align product block heights in this row
      const list = block.closest(".product-grid");
      if (!list) return;

      let tallest = 0;
      const blockTop = block.offsetTop;
      const blocksInThisRow = [
        ...list.querySelectorAll(".product-block"),
      ].filter((el) => el.offsetTop === blockTop);
      blocksInThisRow.forEach((el) => {
        const elInnerHeight = el.querySelector(
          ".block-inner .block-inner-inner"
        ).clientHeight;
        if (elInnerHeight > tallest) tallest = elInnerHeight;
      });
      blocksInThisRow.forEach((el) => {
        el.querySelector(".block-inner").style.setProperty(
          "--qb-block-height",
          `${tallest}px`
        );
      });
    },

    onCloseClick: (evt) => {
      evt.preventDefault();
      evt.stopPropagation();
      const slider = evt.target.closest(".collection-slider-row");
      if (slider) {
        slider
          .querySelector(".product-block.expanded .quickbuy-toggle")
          .dispatchEvent(
            new CustomEvent("click", { bubbles: true, cancelable: true })
          );
      } else {
        evt.target
          .closest(".product-block")
          .querySelector(".quickbuy-toggle")
          .dispatchEvent(
            new CustomEvent("click", { bubbles: true, cancelable: true })
          );
      }
    },
  };

  theme.quickbuy.init();
}

class CarouselSlider extends HTMLElement {
  constructor() {
    super();
    this.slides = [...this.querySelectorAll(".slider__item:not([hidden])")];
    if (this.slides.length < 2) {
      this.setCarouselState(false);
      return;
    }

    window.initLazyScript(this, this.init.bind(this));
  }

  init() {
    this.slider = this.querySelector(".slider");
    this.grid = this.querySelector(".slider__grid");
    this.nav = this.querySelector(".slider-nav");
    this.rtl = document.dir === "rtl";

    if (this.nav) {
      this.prevBtn = this.querySelector('button[name="prev"]');
      this.nextBtn = this.querySelector('button[name="next"]');
    }

    this.initSlider();
    window.addEventListener(
      "on:debounced-resize",
      this.handleResize.bind(this)
    );

    this.setAttribute("loaded", "");
  }

  initSlider() {
    this.gridWidth = this.grid.clientWidth;

    // Distance between leading edges of adjacent slides (i.e. width of card + gap).
    this.slideSpan =
      this.getWindowOffset(this.slides[1]) -
      this.getWindowOffset(this.slides[0]);

    // Note: Slide can re-initialise after previous use
    this.currentIndex =
      Math.round(this.slider.scrollLeft / this.slideSpan) || 0;

    // Width of gap between slides.
    this.slideGap = this.slideSpan - this.slides[0].clientWidth;

    this.slidesPerPage = Math.round(
      (this.gridWidth + this.slideGap) / this.slideSpan
    );
    this.slidesToScroll =
      theme.settings.sliderItemsPerNav === "page" ? this.slidesPerPage : 1;
    this.totalPages = this.slides.length - this.slidesPerPage + 1;

    this.setCarouselState(this.totalPages > 1);
    if (this.dataset.dynamicHeight === "true") {
      this.updateDynamicHeight();
    }
    this.addListeners();
    if (this.totalPages < 2 || !this.nav) return;

    this.sliderStart = this.getWindowOffset(this.slider);
    if (!this.sliderStart)
      this.sliderStart = (this.slider.clientWidth - this.gridWidth) / 2;
    this.sliderEnd = this.sliderStart + this.gridWidth;

    if (window.matchMedia("(pointer: fine)").matches) {
      this.slider.classList.add("is-grabbable");
    }

    this.setButtonStates();
  }

  /**
   * Re-initialise state. Call if slides have changed.
   */
  refresh() {
    if (this.getAttribute("loaded")) {
      this.removeListeners();
      this.style.removeProperty("--current-slide-height");
    }
    this.slides = [...this.querySelectorAll(".slider__item:not([hidden])")];
    if (this.slides.length < 2) {
      this.setCarouselState(false);
      return;
    }
    this.init();
  }

  addListeners() {
    this.scrollHandler = debounce(this.handleScroll.bind(this), 100);
    this.slider.addEventListener("scroll", this.scrollHandler);

    if (this.nav) {
      this.navClickHandler = this.handleNavClick.bind(this);
      this.nav.addEventListener("click", this.navClickHandler);
    }

    if (window.matchMedia("(pointer: fine)").matches) {
      this.mousedownHandler = this.handleMousedown.bind(this);
      this.mouseupHandler = this.handleMouseup.bind(this);
      this.mousemoveHandler = this.handleMousemove.bind(this);

      this.slider.addEventListener("mousedown", this.mousedownHandler);
      this.slider.addEventListener("mouseup", this.mouseupHandler);
      this.slider.addEventListener("mouseleave", this.mouseupHandler);
      this.slider.addEventListener("mousemove", this.mousemoveHandler);
    }
  }

  removeListeners() {
    this.slider.removeEventListener("scroll", this.scrollHandler);
    this.nav.removeEventListener("click", this.navClickHandler);
    this.slider.removeEventListener("mousedown", this.mousedownHandler);
    this.slider.removeEventListener("mouseup", this.mouseupHandler);
    this.slider.removeEventListener("mouseleave", this.mouseupHandler);
    this.slider.removeEventListener("mousemove", this.mousemoveHandler);
  }

  /**
   * Handles 'scroll' events on the slider element.
   */
  handleScroll() {
    const previousIndex = this.currentIndex;
    this.currentIndex = Math.round(
      Math.abs(this.slider.scrollLeft) / this.slideSpan
    );

    if (this.nav) {
      this.setButtonStates();
    }

    if (this.dataset.dynamicHeight === "true") {
      this.updateDynamicHeight();
    }

    if (
      this.dataset.dispatchEvents === "true" &&
      previousIndex !== this.currentIndex
    ) {
      this.dispatchEvent(
        new CustomEvent("on:carousel-slider:select", {
          bubbles: true,
          detail: {
            index: this.currentIndex,
            slide: this.slides[this.currentIndex],
          },
        })
      );
    }
  }

  /**
   * Scroll to a specific slide by index.
   * @param {number} index - The index of the slide to show (0-based).
   * @param {string} transition - Scroll behavior ('smooth' or 'auto'). Default is 'smooth'.
   */
  scrollToIndex(index, transition = 'smooth') {
    if (index < 0 || index >= this.slides.length) return; // Invalid index

    // Calculate scroll position based on index and slideSpan
    const targetSlide = this.slides[index];
    const targetPosition = targetSlide.offsetLeft;

    this.slider.scrollTo({
      left: targetPosition,
      behavior: transition
    });

    this.currentIndex = index;
    this.setButtonStates();

    // Dispatch event if required
    if (this.dataset.dispatchEvents === 'true') {
      this.dispatchEvent(
        new CustomEvent('on:carousel-slider:select', {
          bubbles: true,
          detail: {
            index: this.currentIndex,
            slide: this.slides[this.currentIndex]
          }
        })
      );
    }
  }

  /**
   * Handles 'mousedown' events on the slider element.
   * @param {object} evt - Event object.
   */
  handleMousedown(evt) {
    this.mousedown = true;
    this.startX = evt.pageX - this.sliderStart;
    this.scrollPos = this.slider.scrollLeft;
    this.slider.classList.add("is-grabbing");
  }

  /**
   * Handles 'mouseup' events on the slider element.
   */
  handleMouseup() {
    this.mousedown = false;
    this.slider.classList.remove("is-grabbing");
  }

  /**
   * Handles 'mousemove' events on the slider element.
   * @param {object} evt - Event object.
   */
  handleMousemove(evt) {
    if (!this.mousedown) return;
    evt.preventDefault();

    const x = evt.pageX - this.sliderStart;
    this.slider.scrollLeft = this.scrollPos - (x - this.startX) * 2;
  }

  /**
   * Handles 'click' events on the nav buttons container.
   * @param {object} evt - Event object.
   */
  handleNavClick(evt) {
    if (!evt.target.matches(".slider-nav__btn")) return;

    if (
      (evt.target.name === "next" && !this.rtl) ||
      (evt.target.name === "prev" && this.rtl)
    ) {
      this.scrollPos =
        this.slider.scrollLeft + this.slidesToScroll * this.slideSpan;
    } else {
      this.scrollPos =
        this.slider.scrollLeft - this.slidesToScroll * this.slideSpan;
    }

    this.slider.scrollTo({ left: this.scrollPos, behavior: "smooth" });
  }

  /**
   * Handles 'on:debounced-resize' events on the window.
   */
  handleResize() {
    if (this.nav) this.removeListeners();
    this.initSlider();
  }

  /**
   * Show a specific item inside this slider.
   * @param {Element} el - Slide element
   * @param {string} transition - Transition to pass to behavior parameter of scrollTo (optional)
   */
  scrollToElement(el, transition) {
    if (!this.getSlideVisibility(el)) {
      // this.scrollPos = this.rtl ? (el.offsetLeft + this.slideSpan) : el.offsetLeft;
      this.scrollPos = el.offsetLeft;
      this.slider.scrollTo({
        left: this.scrollPos,
        behavior: transition || "smooth",
      });
    }
  }

  /**
   * Sets a height property based on the current slide.
   */
  updateDynamicHeight() {
    this.style.setProperty(
      "--current-slide-height",
      `${this.slides[this.currentIndex].firstElementChild.clientHeight}px`
    );
  }

  /**
   * Gets the offset of an element from the edge of the viewport (left for ltr, right for rtl).
   * @param {number} el - Element.
   * @returns {number}
   */
  getWindowOffset(el) {
    return this.rtl
      ? window.innerWidth - el.getBoundingClientRect().right
      : el.getBoundingClientRect().left;
  }

  /**
   * Gets the visible state of a slide.
   * @param {Element} el - Slide element.
   * @returns {boolean}
   */
  getSlideVisibility(el) {
    const slideStart = this.getWindowOffset(el);
    const slideEnd = Math.floor(slideStart + this.slides[0].clientWidth);
    return slideStart >= this.sliderStart && slideEnd <= this.sliderEnd;
  }

  /**
   * Sets the active state of the carousel.
   * @param {boolean} active - Set carousel as active.
   */
  setCarouselState(active) {
    if (active) {
      this.removeAttribute("inactive");

      // If slider width changed when activated, reinitialise it.
      if (this.gridWidth !== this.grid.clientWidth) {
        this.handleBreakpointChange();
      }
    } else {
      this.setAttribute("inactive", "");
    }
  }

  /**
   * Sets the disabled state of the nav buttons.
   */
  setButtonStates() {
    this.prevBtn.disabled =
      this.getSlideVisibility(this.slides[0]) && this.slider.scrollLeft === 0;
    this.nextBtn.disabled = this.getSlideVisibility(
      this.slides[this.slides.length - 1]
    );
  }
}

customElements.define("carousel-slider", CarouselSlider);

const TermsAgreement = class extends HTMLElement {
  connectedCallback() {
    this.delegatedEvent = theme.addDelegateEventListener(
      document,
      "click",
      '#cartform [name="checkout"], .additional-checkout-buttons input, a[href*="/checkout"]',
      this.handleFormSubmittingEvent.bind(this)
    );
  }

  disconnectedCallback() {
    document.removeEventListener("click", this.delegatedEvent);
  }

  handleFormSubmittingEvent(evt) {
    if (!this.querySelector("input:checked")) {
      evt.preventDefault();
      theme.showQuickPopup(
        theme.strings.cartTermsConfirmation,
        this.querySelector('[type="checkbox"]')
      );
    }
  }
};

window.customElements.define("terms-agreement", TermsAgreement);

const ToggleTarget = class extends HTMLElement {
  constructor() {
    super();
    this.addEventListener("click", this.toggleOpen.bind(this));
    this.addEventListener("keyup", this.handleKeyUp.bind(this));
    if (this.dataset.toggleCloseLabel) {
      this.dataset.toggleOpenLabel = this.innerHTML;
    }
  }

  /**
   * Handles 'keyup' events on the document.
   * @param {object} evt - Event object.
   */
  handleKeyUp(evt) {
    if (evt.key === "Enter") this.toggleOpen(evt);
  }

  /**
   * Opens or closes the target element.
   * @param {object} evt - Event object.
   */
  toggleOpen(evt) {
    evt.preventDefault();

    const target = document.querySelector(this.dataset.toggleTarget);
    const transCont = target.querySelector(".toggle-target-container");
    const doCollapse = !target.classList.contains("toggle-target--hidden");
    const transitionDuration =
      parseFloat(getComputedStyle(target).transitionDuration) * 1000;

    if (!target.classList.contains("toggle-target--in-transition")) {
      if (doCollapse) {
        this.classList.add("toggle-target-toggler--is-hidden");
        target.classList.add(
          "toggle-target--in-transition",
          "toggle-target--hiding"
        );
        target.style.height = `${transCont.clientHeight}px`;
        target.style.opacity = 1;

        if (this.dataset.toggleCloseLabel) {
          this.innerHTML = this.dataset.toggleOpenLabel;
        }

        setTimeout(() => {
          target.style.height = 0;
          target.style.opacity = 0;

          setTimeout(() => {
            target.classList.add("toggle-target--hidden");
            target.classList.remove(
              "toggle-target--in-transition",
              "toggle-target--hiding"
            );
            target.style.height = "";
            target.style.opacity = "";
          }, transitionDuration);
        }, 10);
      } else {
        this.classList.remove("toggle-target-toggler--is-hidden");
        target.classList.add(
          "toggle-target--in-transition",
          "toggle-target--revealing"
        );
        target.style.height = 0;
        target.style.opacity = 0;
        target.style.display = "block";

        if (this.dataset.toggleCloseLabel) {
          this.innerHTML = this.dataset.toggleCloseLabel;
        }

        setTimeout(() => {
          target.style.height = `${transCont.clientHeight}px`;
          target.style.opacity = 1;

          setTimeout(() => {
            target.classList.remove(
              "toggle-target--hidden",
              "toggle-target--in-transition",
              "toggle-target--revealing"
            );
            target.style.height = "";
            target.style.opacity = "";
            target.style.display = "";
          }, transitionDuration);
        }, 10);
      }
    }
  }
};

window.customElements.define("toggle-target", ToggleTarget);

class VariantContent extends HTMLElement {
  constructor() {
    super();
    if (this.childElementCount > 0) {
      this.closest(".js-product").addEventListener(
        "on:variant:change",
        this.handleVariantChange.bind(this)
      );
    }
  }

  /**
   * Handles a 'change' event on the variant picker element
   * @param {object} evt - Event object
   */
  handleVariantChange(evt) {
    [...this.childNodes]
      .filter((el) => el.tagName !== "SCRIPT")
      .forEach((el) => el.remove());
    this.querySelectorAll(":not(script)").forEach((el) => el.remove());
    const contentToShow = this.querySelector(
      `[data-variant="${evt.detail.variant ? evt.detail.variant.id : ""}"]`
    );
    if (contentToShow) {
      this.insertAdjacentHTML("beforeend", contentToShow.innerHTML);
    }
  }
}

customElements.define("variant-content", VariantContent);

const AnnouncementBar = class extends HTMLElement {
  constructor() {
    super();

    this.announcements = this.querySelectorAll(".announcement");
    if (this.announcements.length > 1) {
      this.changeDelay = 5000;
      this.currentAnnouncement = 0;
      this.querySelector(".announcement-bar__middle").addEventListener(
        "focusin",
        this.pauseAnnouncements.bind(this)
      );
      this.querySelector(".announcement-bar__middle").addEventListener(
        "focusout",
        this.playAnnouncements.bind(this)
      );
      this.querySelector(".announcement-button--previous").addEventListener(
        "click",
        this.previousAnnouncement.bind(this)
      );
      this.querySelector(".announcement-button--next").addEventListener(
        "click",
        this.nextAnnouncement.bind(this)
      );
      this.observer = new IntersectionObserver(this.handleIntersect.bind(this));
      this.observer.observe(this);
    }
  }

  handleIntersect(entries) {
    entries.forEach((entry) => {
      if (entry.target === this) {
        if (entry.isIntersecting) {
          this.playAnnouncements(this);
        } else {
          this.pauseAnnouncements(this);
        }
      }
    });
  }

  playAnnouncements() {
    if (this.announcements.length > 1) {
      this.announcementInterval = setInterval(() => {
        this.setCurrentAnnouncement(this.currentAnnouncement + 1);
      }, this.changeDelay);
    }
  }

  pauseAnnouncements() {
    if (this.announcementInterval) {
      clearInterval(this.announcementInterval);
    }
  }

  setCurrentAnnouncement(newIndex) {
    this.currentAnnouncement = newIndex % this.announcements.length;
    this.announcements.forEach((el, index) => {
      if (index !== this.currentAnnouncement) {
        el.classList.add("announcement--inactive");
      } else {
        el.classList.remove("announcement--inactive");
      }
    });
  }

  previousAnnouncement() {
    this.setCurrentAnnouncement(this.currentAnnouncement - 1);
  }

  nextAnnouncement() {
    this.setCurrentAnnouncement(this.currentAnnouncement + 1);
  }
};

window.customElements.define("announcement-bar", AnnouncementBar);

/*
  If the following conditions are met:
  1. This section directly follows the header
  2. This section is set to be full page height

  Then:
  If the header is transparent, subtract the height of messages and announcement bar.
  If the header if not transparent, subtract the height of the header.
*/

const ImageWithTextOverlay = class extends HTMLElement {
  constructor() {
    super();

    this.fullHeightContainer = this.classList.contains("height--full")
      ? this
      : this.querySelector(".height--full");

    if (this.fullHeightContainer) {
      if (this.closest(".shopify-section").previousElementSibling) return;
      const ph = document.querySelector(".pageheader");
      if (!ph) {
        this.fullHeightContainer.classList.add(
          "height--full-ignore-header-height"
        );
        return;
      }

      const thisOffsetTop = theme.getOffsetTopFromDoc(this);
      const phOffsetTop = theme.getOffsetTopFromDoc(ph);
      if (phOffsetTop - 5 > thisOffsetTop) {
        // Must be lower. For trans header, it'll roughly match
        this.fullHeightContainer.classList.add(
          "height--full-ignore-header-height"
        );
        return;
      }

      this.fullHeightContainer.classList.add(
        "height--full-minus-header-height"
      );
      this.checkForHeaderHeightSubtraction();

      const handleResize = debounce(
        this.checkForHeaderHeightSubtraction.bind(this),
        300
      );
      this.resizeObserver = new ResizeObserver((entries) => {
        for (let i = 0; i < entries.length; i += 1) {
          const entry = entries[i];
          if (entry.contentBoxSize || entry.contentRect) {
            handleResize();
          }
        }
      });
      this.resizeObserver.observe(this);
    }
  }

  checkForHeaderHeightSubtraction() {
    let height = window.innerHeight;
    // transparent header & top section? slightly different calculation
    if (
      document.querySelector(".pageheader--transparent-permitted") &&
      !this.closest(".shopify-section").previousElementSibling
    ) {
      document
        .querySelectorAll(".section-store-messages, .section-announcement-bar")
        .forEach((el) => {
          height -= el.clientHeight;
        });
    } else {
      height -= document.querySelector(".section-header").clientHeight;
    }
    this.fullHeightContainer.style.setProperty("--image-height", `${height}px`);
  }
};

window.customElements.define("image-with-text-overlay", ImageWithTextOverlay);

const PageHeader = class extends HTMLElement {
  constructor() {
    super();

    // Search (delegate event as contents may update with cart changes)
    if (this.querySelector(".main-search")) {
      theme.addDelegateEventListener(
        this,
        "click",
        ".show-search-link",
        (evt) => {
          evt.preventDefault();
          document.body.classList.add("show-search");
          setTimeout(() => {
            this.querySelector(".main-search__input").focus();
          }, 500);
        }
      );
    }

    // Navigation
    theme.inlineNavigationCheck();

    // Reveal cart drawer
    if (
      theme.settings.cartType === "drawer" &&
      document.querySelector(".js-cart-drawer") &&
      this.querySelector(".cart-link")
    ) {
      theme.addDelegateEventListener(this, "click", ".cart-link", (evt) => {
        evt.preventDefault();
        document.querySelector(".js-cart-drawer").open();
      });
    }

    // init mobile nav again if it's open and this is a reload
    if (
      window.Shopify.designMode &&
      document.body.classList.contains("reveal-mobile-nav")
    ) {
      theme.openMobileNav();
    }

    // restore all disabled images
    setTimeout(() => theme.manuallyLoadImages(this), 250);
  }

  connectedCallback() {
    this.section = document.querySelector(".section-header");
    this.transparentPageheader = document.querySelector(
      ".pageheader--transparent-permitted"
    );

    this.setTransparency();
    this.setSticky();
    this.setHeaderHeightProperty();

    window.addEventListener("scroll", this.afterScroll.bind(this));

    this.refreshHeader = () => {
      fetch(`${window.location.origin}?sections=header`)
        .then((response) => response.json())
        .then((data) => {
          const template = document.createElement("template");
          template.innerHTML = data.header;
          const selectorsForRefresh = ["#pageheader .logo-area__right__inner"];
          for (let i = 0; i < selectorsForRefresh.length; i += 1) {
            const newEl = template.content.querySelector(
              selectorsForRefresh[i]
            );
            const currentEl = this.querySelector(selectorsForRefresh[i]);
            currentEl.innerHTML = newEl.innerHTML;
          }
        });
    };
    document.addEventListener("on:cart:change", this.refreshHeader);

    const debouncedAfterResize = debounce(this.afterResize.bind(this), 300);
    this.resizeObserver = new ResizeObserver((entries) => {
      for (let i = 0; i < entries.length; i += 1) {
        const entry = entries[i];
        if (entry.contentBoxSize || entry.contentRect) {
          debouncedAfterResize();
        }
      }
    });
    this.resizeObserver.observe(this);
  }

  disconnectedCallback() {
    this.resizeObserver.disconnect();
    document.removeEventListener("on:cart:change", this.refreshHeader);
  }

  afterScroll() {
    this.setTransparency();
  }

  afterResize() {
    if (theme.inlineNavigationCheck) {
      theme.inlineNavigationCheck();
    }
    this.setSticky();
    this.setHeaderHeightProperty();
  }

  setTransparency() {
    if (this.transparentPageheader) {
      const bar = document.querySelector(".section-announcement-bar");
      const scrollThreshold = bar ? bar.offsetTop + bar.clientHeight : 0;
      if (!this.isSticky || window.scrollY <= scrollThreshold) {
        this.transparentPageheader.classList.add("pageheader--transparent");
      } else {
        this.transparentPageheader.classList.remove("pageheader--transparent");
      }
    }
  }

  setSticky() {
    this.isSticky =
      getComputedStyle(this.section).position === "sticky" ||
      getComputedStyle(this.section).position === "-webkit-sticky";
  }

  setHeaderHeightProperty() {
    let headerHeight = 0;
    let stickyHeaderHeight = 0;

    if (this.section) {
      headerHeight = Math.ceil(this.section.clientHeight);

      if (this.isSticky) {
        stickyHeaderHeight = headerHeight;
      }
    }

    document.documentElement.style.setProperty(
      "--theme-header-height",
      `${headerHeight}px`
    );
    document.documentElement.style.setProperty(
      "--theme-sticky-header-height",
      `${stickyHeaderHeight}px`
    );
  }

  updateFromCartChange(html) {
    const selectorForUpdate = ".logo-area__right";
    const template = document.createElement("template");
    template.innerHTML = html;

    const elToUpdate = this.querySelector(selectorForUpdate);
    if (elToUpdate) {
      elToUpdate.innerHTML =
        template.content.querySelector(selectorForUpdate).innerHTML;
    }
  }
};

window.customElements.define("page-header", PageHeader);

const RelatedCollectionLinkButtons = class extends HTMLElement {
  constructor() {
    super();

    const handleResize = debounce(this.truncateButtons.bind(this), 300);
    this.resizeObserver = new ResizeObserver((entries) => {
      for (let i = 0; i < entries.length; i += 1) {
        const entry = entries[i];
        if (entry.contentBoxSize || entry.contentRect) {
          handleResize();
        }
      }
    });
    this.resizeObserver.observe(this);

    this.truncateButtons();
  }

  truncateButtons() {
    if (getComputedStyle(this).display === "flex") {
      const limit = 4;
      const btns = this.querySelectorAll(".btn");
      if (
        btns.length > limit &&
        !this.querySelector(".related-collection-links__expander")
      ) {
        Array.from(btns)
          .slice(limit - 1)
          .forEach((el) => el.classList.add("hidden"));
        const expander = document.createElement("a");
        expander.className =
          "btn btn--tertiary related-collection-links__expander";
        expander.href = "#";
        expander.innerText = this.dataset.expanderBtnText;
        expander.addEventListener("click", (evt) => {
          evt.preventDefault();
          this.resizeObserver.disconnect();
          [...evt.currentTarget.parentElement.children].forEach((el) =>
            el.classList.remove("hidden")
          );
          evt.currentTarget.remove();
        });
        this.insertAdjacentElement("beforeend", expander);
      }
    } else {
      const expander = this.querySelector(
        ".related-collection-links__expander"
      );
      if (expander) {
        [...expander.parentElement.children].forEach((el) =>
          el.classList.remove("hidden")
        );
        expander.remove();
      }
    }
  }
};

window.customElements.define(
  "related-collection-link-buttons",
  RelatedCollectionLinkButtons
);

requestAnimationFrame(initLazyImages);
window.initLazyScript = initLazyScript;

if (!theme.Shopify) theme.Shopify = {};
try {
  theme.Shopify.features = JSON.parse(
    document.documentElement.querySelector("#shopify-features").textContent
  );
} catch (e) {
  theme.Shopify.features = {};
}

document.addEventListener("DOMContentLoaded", () => {
  // Smooth scroll to an internal link
  const internalLinksSmoothScroll = (evt) => {
    const link =
      evt.target.tagName === "A" ? evt.target : evt.target.closest("a");
    if (
      link &&
      link.getAttribute("href") &&
      link.getAttribute("href").length > 1 &&
      link.getAttribute("href")[0] === "#"
    ) {
      const target = document.querySelector(link.getAttribute("href"));
      if (target && target.offsetParent) {
        evt.preventDefault();
        theme.scrollToRevealElement(target);
      }
    }
  };

  if (theme.settings.internalLinksSmoothScroll) {
    document.addEventListener("click", internalLinksSmoothScroll);
  }

  // Enable focus outlines after using the tab key, and remove scroll internal link scroll handler
  const tabCheck = (evt) => {
    if (evt.code === "Tab") {
      document.body.classList.add("tab-used");
      document.removeEventListener("keydown", tabCheck);
      document.removeEventListener("click", internalLinksSmoothScroll);
    }
  };
  document.addEventListener("keydown", tabCheck);

  // Opening links in a new tab
  if (theme.settings.externalLinksNewTab) {
    document.addEventListener("click", (evt) => {
      const link =
        evt.target.tagName === "A" ? evt.target : evt.target.closest("a");
      if (
        link &&
        link.href &&
        link.tagName === "A" &&
        window.location.hostname !== new URL(link.href).hostname
      ) {
        link.target = "_blank";
      }
    });
  }

  // Mobile nav
  theme.addDelegateEventListener(
    document,
    "click",
    ".mobile-nav-toggle",
    (evt) => {
      evt.preventDefault();
      if (document.body.classList.contains("enable-mobile-nav-transition")) {
        // hide nav
        document.body.classList.remove(
          "reveal-mobile-nav",
          "reveal-mobile-nav--revealed"
        );
        setTimeout(() => {
          document.body.classList.remove("enable-mobile-nav-transition");
        }, 750);
      } else {
        // show nav
        theme.openMobileNav();
      }
    }
  );

  theme.openMobileNav = () => {
    document.body.classList.add("enable-mobile-nav-transition");
    setTimeout(() => {
      document.body.classList.add("reveal-mobile-nav");

      // after reveal, add class
      const cs = getComputedStyle(
        document.querySelector(
          ".mobile-navigation-drawer .navigation__tier-1 > .navigation__item > .navigation__link"
        )
      );
      const delayS =
        parseFloat(cs.transitionDelay.split(",")[0]) +
        parseFloat(cs.transitionDuration.split(",")[0]);
      setTimeout(() => {
        document.body.classList.add("reveal-mobile-nav--revealed");
      }, delayS * 1000);

      // restore all disabled images
      theme.manuallyLoadImages(
        document.querySelector(".mobile-navigation-drawer")
      );
    }, 10);
  };

  // Window shade
  const shade = document.querySelector(".page-shade");
  if (shade) {
    shade.addEventListener("click", (evt) => {
      evt.preventDefault();
      document.body.classList.remove("reveal-mobile-nav", "show-search");
      setTimeout(() => {
        document.body.classList.remove("enable-mobile-nav-transition");
      }, 750);
    });
  }
});

// MAK_JS

// MAK FREE PRODUCT 2-STEP FLOW
window.makCartRecommendationMode = false;
window.makFreeGiftFlow = window.makFreeGiftFlow || {
  step: 1,
  savedItems: []
};

window.makResetFreeGiftFlow = function () {
  window.makCartGiftSelectionMode = false;
  window.makCartPendingGiftItem = null;
  window.makCartGiftStartCount = 0;
  window.makCartGiftStepTransitioning = false;
  window.makCartGiftRequiredSelections = 0;
  window.makCartGiftMode = null;
  document.querySelectorAll('.mak__free_items .mak-free-main-header h2[data-mn-cart-original-title]').forEach(function (heading) {
    heading.textContent = heading.getAttribute('data-mn-cart-original-title');
    heading.removeAttribute('data-mn-cart-original-title');
  });
  document.querySelectorAll('.mak__free_items .mn-free-one-plus-one-banner[data-mn-cart-original-banner]').forEach(function (banner) {
    banner.textContent = banner.getAttribute('data-mn-cart-original-banner');
    banner.removeAttribute('data-mn-cart-original-banner');
  });
  document.querySelectorAll('.mak__free_items.mak-cart-gift-bogo').forEach(function (drawer) {
    drawer.classList.remove('mak-cart-gift-bogo');
  });
  document.documentElement.classList.remove("mn-cart-gift-flow");
  document.querySelectorAll(".mak__free_items.mak-cart-gift-selection").forEach(function (drawer) {
    drawer.classList.remove("mak-cart-gift-selection");
    drawer.querySelectorAll("[data-mn-cart-gift-original]").forEach(function (heading) {
      heading.innerHTML = heading.getAttribute("data-mn-cart-gift-original");
      heading.removeAttribute("data-mn-cart-gift-original");
    });
  });
  window.makFreeGiftFlow = {
    step: 1,
    savedItems: []
  };

  // Reset the selected bundle slots after the upsell choice is completed.
  if (typeof window.makResetBundleSlot === "function") {
    window.makResetBundleSlot(2);
    window.makResetBundleSlot(3);
  }

  var drawerTitle = document.querySelector(
    ".mak__free_items.side-drawer .side-drawer-header h3"
  );

  if (drawerTitle) {
    drawerTitle.textContent = "Välj ett smycke";
  }

  document
    .querySelectorAll(".mak__free_items.side-drawer")
    .forEach(function (drawer) {
      drawer.classList.remove("mak-second-free-product-step");
    });
};

/**
 * Central lifecycle for the custom three-step free-gift drawer.
 *
 * The cart drawer is a Symmetry <side-drawer> Web Component. Instead of using
 * fixed timeouts, the free-gift drawer is handed off when Symmetry emits
 * "on:cart-drawer:before-open". This guarantees that the custom drawer is gone
 * before the cart drawer becomes visible.
 */
window.makFreeGiftDrawerHandoff = window.makFreeGiftDrawerHandoff || {
  drawer: null
};

window.makGetFreeGiftDrawer = function (source) {
  var element = source && source.jquery ? source[0] : source;

  if (!element) {
    return document.querySelector(".mak__free_items.side-drawer.open");
  }

  if (
    element.matches &&
    element.matches(".mak__free_items.side-drawer")
  ) {
    return element;
  }

  var closestDrawer = element.closest
    ? element.closest(".mak__free_items.side-drawer")
    : null;

  return closestDrawer ||
    document.querySelector(".mak__free_items.side-drawer.open");
};

window.makCloseFreeGiftDrawer = function (source, options) {
  var settings = Object.assign(
    {
      immediate: false,
      resetFlow: true,
      unlockScroll: true,
      returnToCart: true
    },
    options || {}
  );

  var drawer = window.makGetFreeGiftDrawer(source);

  if (!drawer) {
    return false;
  }

  // A previous animated close must not hide a newly reopened gift picker.
  if (drawer.__mnFreeGiftCloseTimer) {
    window.clearTimeout(drawer.__mnFreeGiftCloseTimer);
    drawer.__mnFreeGiftCloseTimer = null;
  }

  // When opened above an already-open cart, simply reveal that cart on close.
  var cartAlreadyOpen = !!document.querySelector("#mak-cart-drawer[open]");
  var returnToCart = settings.returnToCart && !cartAlreadyOpen &&
    drawer.classList.contains("mak-cart-gift-selection");
  var innerDrawers = drawer.querySelectorAll(
    ".mak__free_items-inner, .mak__upsell-inner"
  );

  /*
   * Desktop:
   * Same principle as the cart drawer.
   * Start fading the backdrop as soon as close begins.
   */
  var useDesktopBackdropFade =
    !settings.immediate &&
    window.matchMedia("(min-width: 768px)").matches;

  var freeGiftBackdrops = Array.from(
    document.querySelectorAll(".drawer__backdrop")
  );

  if (useDesktopBackdropFade) {
    freeGiftBackdrops.forEach(function (backdrop) {
      if (backdrop.classList.contains("active")) {
        backdrop.classList.add("mn-free-backdrop-closing");
      }
    });
  }

  if (settings.immediate) {
    drawer.classList.remove(
      "open",
      "mn-free-closing",
      "mak-second-free-product-step"
    );

    innerDrawers.forEach(function (innerDrawer) {
      innerDrawer.classList.remove("open");
    });
  } else {
    /*
     * Keep the drawer's open state and all inner layout intact while it closes.
     * Only move the complete drawer off-screen; reset classes after it is hidden.
     */
    drawer.classList.add("mn-free-closing");
    drawer.style.transform = "translateX(100%)";
    drawer.style.pointerEvents = "none";
  }

  if (useDesktopBackdropFade) {
    requestAnimationFrame(function () {
      freeGiftBackdrops.forEach(function (backdrop) {
        if (backdrop.classList.contains("mn-free-backdrop-closing")) {
          backdrop.classList.remove("active");
        }
      });
    });
  }

  var finishClose = function () {
    /*
     * Hide the complete drawer before resetting any state that can affect
     * widths, grids, images or the free-gift progress UI.
     */
    drawer.style.display = "none";

    drawer.classList.remove(
      "open",
      "mn-free-closing",
      "mak-second-free-product-step"
    );
    drawer.style.removeProperty("transform");
    drawer.style.removeProperty("pointer-events");

    innerDrawers.forEach(function (innerDrawer) {
      innerDrawer.style.display = "none";
      innerDrawer.classList.remove("open");
      innerDrawer.style.removeProperty("overflow");
      innerDrawer.style.removeProperty("overflow-y");
      innerDrawer.scrollTop = 0;
      innerDrawer.setAttribute("aria-hidden", "true");

      if (
        innerDrawer.classList.contains("mak__free_items-inner") &&
        window.makResetPdpGalleryState
      ) {
        window.makResetPdpGalleryState(innerDrawer);
      }
    });

    drawer.style.removeProperty("overflow");
    drawer.style.removeProperty("overflow-y");
    drawer.scrollTop = 0;
    drawer.setAttribute("aria-hidden", "true");
    drawer.__mnParentScrollLocked = false;
    if (drawer.__mnParentScrollGuard) {
      drawer.removeEventListener("scroll", drawer.__mnParentScrollGuard);
      delete drawer.__mnParentScrollGuard;
    }
    delete drawer.__mnParentScrollTop;
    delete drawer.__mnParentOverflow;
    delete drawer.__mnParentOverflowY;
    delete drawer.__mnParentOverscroll;

    drawer.querySelectorAll(".upsell-popup-data").forEach(function (upsell) {
      upsell.removeAttribute("data-add");
    });

    drawer.querySelectorAll(".mainupsell").forEach(function (upsell) {
      upsell.style.display = "block";
    });

    document.querySelectorAll(".drawer__backdrop").forEach(function (backdrop) {
      backdrop.classList.remove(
        "active",
        "mn-free-backdrop-closing"
      );
    });

    $(".collection-slider-row").css("z-index", "1");
    $(".collapsible-tabs__tab .disclosure__title").css("z-index", "1");
    $(".sticky-content-container").removeClass("no-sticky");

    makRestoreFreeGiftDrawer(drawer);

    if (window.makUnlockFreeGiftKeyboardPage) {
      window.makUnlockFreeGiftKeyboardPage();
    }

    if (settings.unlockScroll) {
      var freeGiftPageScrollY = window.__mnFreeGiftPageScrollY;
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
      if (window.innerWidth < 768 && typeof freeGiftPageScrollY === "number") {
        window.scrollTo(0, freeGiftPageScrollY);
      }
    }
    window.__mnFreeGiftKeyboardDrawer = null;
    window.__mnFreeGiftPageScrollY = null;

    if (
      window.makFreeGiftDrawerHandoff &&
      window.makFreeGiftDrawerHandoff.drawer === drawer
    ) {
      window.makFreeGiftDrawerHandoff.drawer = null;
    }

    if (settings.resetFlow && window.makResetFreeGiftFlow) {
      window.makResetFreeGiftFlow();
    }

    if (returnToCart) {
      document.dispatchEvent(new CustomEvent("dispatch:cart-drawer:open", { bubbles: true }));
    }

    return true;
  };

  if (settings.immediate) {
    return finishClose();
  }

  drawer.__mnFreeGiftCloseTimer = window.setTimeout(function () {
    drawer.__mnFreeGiftCloseTimer = null;
    finishClose();
  }, 300);
  return true;
};

window.makPrepareFreeGiftCartHandoff = function (source) {
  var drawer = window.makGetFreeGiftDrawer(source);

  if (!drawer) {
    return false;
  }

  window.makFreeGiftDrawerHandoff.drawer = drawer;
  return true;
};

document.addEventListener("on:cart-drawer:before-open", function () {
  var drawer = window.makFreeGiftDrawerHandoff.drawer;

  if (!drawer) {
    return;
  }

  // Immediate close: no 300 ms overlap above the cart drawer.
  window.makCloseFreeGiftDrawer(drawer, {
    immediate: true,
    resetFlow: true,
    unlockScroll: true
  });

  window.makFreeGiftDrawerHandoff.drawer = null;
});

document.addEventListener("on:cart:error", function () {
  // Keep the custom drawer open if the main product could not be added.
  window.makFreeGiftDrawerHandoff.drawer = null;
});


window.makGetSavedFreeGiftItems = function () {
  return (window.makFreeGiftFlow && window.makFreeGiftFlow.savedItems)
    ? window.makFreeGiftFlow.savedItems.map(function (item) {
        return JSON.parse(JSON.stringify(item));
      })
    : [];
};

window.makAppendSavedFreeGiftItems = function (itemsArray) {
  var savedItems = window.makGetSavedFreeGiftItems();
  savedItems.forEach(function (item) {
    itemsArray.push(item);
  });
  return itemsArray;
};

window.makPrependSavedFreeGiftItems = function (itemsArray) {
  var savedItems = window.makGetSavedFreeGiftItems();
  return savedItems.concat(itemsArray);
};

// Main Product 1+1: det valda gratissmycket är redan sparat.
// Skicka det en gång efter valet av presentask (ja/nej).
// Övriga flöden (2+1 och varukorgens gåvoval) påverkas inte.
window.makIsMainProductOnePlusOneFinish = function () {
  if (window.makCartGiftSelectionMode || window.makCartRecommendationMode) {
    return false;
  }
  var mainPicker = document.querySelector(
    '.mak__free_items.side-drawer.open[data-mn-main-free-selections="1"]'
  );
  return !!mainPicker && window.makGetSavedFreeGiftItems().length === 1;
};

window.makValidateFreeProductDrawer = function (drawerEl) {
  if (!drawerEl) {
    return false;
  }

  var goON = true;
  var onMultiple = false;

  drawerEl.querySelectorAll(".active #maktextInput").forEach(function (input, index) {
    if (input.value == "" && index == 0) {
      var error = input.parentElement ? input.parentElement.querySelector("#name-error1") : null;
      if (error) {
        error.style.display = "block";
      }

      input.focus();

      if (!onMultiple) {
        goON = false;
        onMultiple = false;
        drawerEl.scrollIntoView();
      }
    }

    input.addEventListener("input", function () {
      var wrapper = this.closest(".mak-input-wrapper") || this.closest(".mak-iec-input-wrapper");
      var error = wrapper ? wrapper.querySelector("#name-error1") : null;
      if (error) {
        error.style.display = "none";
      }
    });
  });

  drawerEl.querySelectorAll("#your-name").forEach(function (input, index) {
    if (input.value == "" && index == 0) {
      var error = input.parentElement && input.parentElement.parentElement
        ? input.parentElement.parentElement.querySelector("#name-error2")
        : null;

      if (error) {
        error.style.display = "block";
      }

      input.focus();

      if (!onMultiple) {
        goON = false;
        onMultiple = false;
        drawerEl.scrollIntoView();
      }
    }

    input.addEventListener("input", function () {
      var wrapper = this.closest(".mak-input-wrapper") || this.closest(".mak-iec-input-wrapper");
      var error = wrapper ? wrapper.querySelector("#name-error2") : null;
      if (error) {
        error.style.display = "none";
      }
    });
  });

  return goON;
};

/* Multi-name necklaces: submit one name property and a single font property.
   Only numbered name properties (Namn 1..5 / localized equivalent) are combined.
   Keep form fields, previews, validation and add/remove-name behavior unchanged. */
window.makCombineMultiNameProperties = function (properties) {
  if (!properties) return properties;
  const bases = [...new Set([window.language_name, 'Namn', 'Name'].filter(Boolean))];
  const indexedKeys = [];
  const names = [];
  let baseName = '';

  bases.some(function (base) {
    const found = [];
    for (let i = 1; i <= 5; i++) {
      const key = base + ' ' + i;
      if (Object.prototype.hasOwnProperty.call(properties, key)) found.push(key);
    }
    if (!found.length) return false;
    baseName = base;
    for (let i = 1; i <= 5; i++) {
      const key = base + ' ' + i;
      if (!Object.prototype.hasOwnProperty.call(properties, key)) continue;
      indexedKeys.push(key);
      const value = String(properties[key] || '').trim();
      if (value) names.push(value);
    }
    return true;
  });

  if (!indexedKeys.length) return properties;
  const result = {};
  if (names.length) result[baseName] = names.join(', ');
  Object.keys(properties).forEach(function (key) {
    if (!indexedKeys.includes(key) && key !== baseName) result[key] = properties[key];
  });
  return result;
};

/* Native Shopify product-form path: preserve the original field order and
   only replace the numbered name properties before /cart/add. */
window.makCombineMultiNameFormData = function (formData) {
  const properties = {};
  for (const [key, value] of formData.entries()) {
    const match = /^properties\[(.*)\]$/.exec(key);
    if (match) properties[match[1]] = value;
  }
  const normalized = window.makCombineMultiNameProperties(properties);
  if (normalized === properties) return formData;

  const removedKeys = new Set(Object.keys(properties).filter(function (key) {
    return !Object.prototype.hasOwnProperty.call(normalized, key);
  }).map(function (key) { return 'properties[' + key + ']'; }));
  // A combined property is inserted at the first numbered field position.
  const firstNumbered = [...removedKeys].find(function (key) {
    return / [1-5]\]$/.test(key);
  });
  if (!firstNumbered) return formData;
  const baseName = firstNumbered.slice('properties['.length, -1).replace(/ [1-5]$/, '');
  const combinedKey = 'properties[' + baseName + ']';
  const rebuilt = new FormData();
  let inserted = false;
  for (const [key, value] of formData.entries()) {
    if (removedKeys.has(key)) {
      if (!inserted && normalized[baseName]) {
        rebuilt.append(combinedKey, normalized[baseName]);
        inserted = true;
      }
      continue;
    }
    // Do not retain a duplicate pre-existing unnumbered property.
    if (key === combinedKey) continue;
    rebuilt.append(key, value);
  }
  return rebuilt;
};

window.makCollectFreeProductItemFromDrawer = function (drawerEl, triggerBtn) {
  if (!drawerEl) {
    return null;
  }

  var $drawer = $(drawerEl);
  var $button = $(triggerBtn);
  var container = $drawer.find(".mak__new__pdp-data").first();

  if (!container.length) {
    container = $button.closest(".mak__new__pdp-data");
  }

  if (!container.length) {
    return null;
  }

  var productId = $button.data("product-id") || container.data("product-id") || $drawer.data("pdp-id");
  var defaultVariantId = $button.attr("default-variant-id") || container.find(".mak__variants-main select[name='id']").val();

  var selectedOption = "";
  if (productId) {
    selectedOption = container
      .find('.mak__line-items[data-product-id="' + productId + '"] #valj-justerbar-kedjelangd')
      .val();
  }

  if (!selectedOption) {
    selectedOption = container.find("#valj-justerbar-kedjelangd").val();
  }

  var checkedVariant = container.find(".mak__variants-main .mak_variants_radio:checked");
  var variantId = 0;

  if (checkedVariant.length > 1) {
    var master = container.find(".mak__variants-main select[name='id'] option");
    var selected = checkedVariant;
    var variantname = "";

    for (let i = 0; i < selected.length; i++) {
      const element = selected[i].value;
      if (i == selected.length - 1) {
        variantname = variantname + element;
      } else {
        variantname = variantname + element + " / ";
      }
    }

    for (let j = 0; j < master.length; j++) {
      const element = master[j].getAttribute("data-name");
      if (element == variantname) {
        variantId = master[j].getAttribute("value");
      }
    }
  } else {
    variantId = checkedVariant.length
      ? checkedVariant.data("variant-id")
      : defaultVariantId;
  }

  if (!variantId || variantId == 0) {
    variantId = container.find(".mak__variants-main select[name='id']").val() || defaultVariantId;
  }

  var productWrapper = container.find(".mak-input-wrapper.drawer-product-font");
  if (!productWrapper.length) {
    productWrapper = container.find(".mak-input-wrapper");
  }

  var selectedFontName = productWrapper.find("#fontSelected").val() || container.find("#fontSelected").val();
  var selectedFont = productWrapper.find("#maktextInput-val, #your-name").first().val();

  var properties = {};
  var productNames = productWrapper.find(".mak-iec-input-wrapper.active");

  if (productNames.length > 1) {
    for (let index = 0; index < productNames.length; index++) {
      const selectedName = $(productNames[index]).find("#maktextInput-val, #your-name").val();
      if (selectedName) {
        properties[window.language_name + " " + (index + 1)] = selectedName;
      }
    }
  } else {
    if (selectedFont) {
      properties[window.language_name] = selectedFont;
    }
  }

  if (selectedFontName) {
    properties["Typsnitt"] = selectedFontName;
  }

  if (selectedOption) {
    properties["Kedjelängd"] = selectedOption;
  }

  return {
    id: parseInt(variantId, 10),
    quantity: 1,
    properties: window.makCombineMultiNameProperties(properties)
  };
};

window.makApplyDefaultPreviewMaterial = function (drawerEl) {
  if (!drawerEl) {
    return;
  }

  var defaultLabel = drawerEl.querySelector('.opt-label[data-swatch="guld"], .opt-label[data-swatch="gold"], .opt-label[data-swatch="Gold"], .opt-label[data-swatch="Guld"]');

  if (!defaultLabel) {
    defaultLabel = drawerEl.querySelector(".opt-label");
  }

  if (defaultLabel && typeof defaultLabel.click === "function") {
    defaultLabel.click();
  }

  var swatchValue = defaultLabel ? (defaultLabel.getAttribute("data-swatch") || defaultLabel.textContent || "") : "guld";
  var isSilver = /silver|sliver/i.test(swatchValue);
  var materialClass = isSilver ? "mar-sliver" : "mar-gold";

  drawerEl.querySelectorAll("#myHeading, #makmyHeading").forEach(function (heading) {
    heading.classList.remove("mar-gold", "mar-sliver", "mar-silver", "material-guld", "material-gold", "material-silver");
    heading.classList.add(materialClass);
    heading.setAttribute("data-material", materialClass);
  });
};

window.makResetFreeProductFontPreview = function (drawerEl) {
  if (!drawerEl) {
    return;
  }

  drawerEl.querySelectorAll("#myHeading, #makmyHeading").forEach(function (heading) {
    heading.innerHTML = "";
    heading.textContent = "";
  });

  drawerEl.querySelectorAll(".fontPreview, .fontPreview_FontPreview__4SFiv").forEach(function (preview) {
    preview.classList.remove("partial-width", "mak-partial-width");
  });

  drawerEl.querySelectorAll(".input-field, .iec-inner-text-wrapper, .mak-iec-inner-text-wrapper").forEach(function (field) {
    field.classList.remove("partial-width", "mak-partial-width");
  });

  drawerEl.querySelectorAll("#fontSelected, #hidden-name-new-font, #hidden-name-new-field, #textInput-val, #maktextInput-val").forEach(function (input) {
    input.value = "";
  });

  window.makApplyDefaultPreviewMaterial(drawerEl);
};

window.makClearFreeProductDrawerInputs = function (drawerEl) {
  if (!drawerEl) {
    return;
  }

  drawerEl.querySelectorAll("#textInput, #maktextInput, #maktextInput-val, #your-name, #personalizationInput, #personalization-field").forEach(function (input) {
    input.value = "";

    if (input.nextElementSibling && input.nextElementSibling.tagName === "INPUT") {
      input.nextElementSibling.value = "";
    }
  });

  window.makResetFreeProductFontPreview(drawerEl);
};

function makRestoreInlineStyle(element, property, value) {
  if (value) {
    element.style[property] = value;
  } else {
    element.style.removeProperty(property.replace(/[A-Z]/g, function (match) {
      return `-${match.toLowerCase()}`;
    }));
  }
}

window.makLockFreeProductParentScroll = function (drawerEl) {
  if (!drawerEl || window.innerWidth >= 768) return;

  var parentDrawer = drawerEl.closest(".mak__free_items.side-drawer");
  if (!parentDrawer || parentDrawer.__mnParentScrollLocked) return;

  parentDrawer.__mnParentScrollLocked = true;
  parentDrawer.__mnParentScrollTop = parentDrawer.scrollTop;
  parentDrawer.__mnParentOverflow = parentDrawer.style.overflow;
  parentDrawer.__mnParentOverflowY = parentDrawer.style.overflowY;
  parentDrawer.__mnParentOverscroll = parentDrawer.style.overscrollBehavior;
  parentDrawer.__mnParentScrollGuard = function () {
    if (parentDrawer.__mnParentScrollLocked && parentDrawer.scrollTop !== 0) {
      parentDrawer.scrollTop = 0;
    }
  };

  parentDrawer.style.overflow = "hidden";
  parentDrawer.style.overflowY = "hidden";
  parentDrawer.style.overscrollBehavior = "none";
  parentDrawer.scrollTop = 0;
  parentDrawer.addEventListener("scroll", parentDrawer.__mnParentScrollGuard, { passive: true });
  drawerEl.style.overscrollBehavior = "contain";
};

window.makUnlockFreeProductParentScroll = function (drawerEl, restoreScroll) {
  if (!drawerEl || window.innerWidth >= 768) return;

  var parentDrawer = drawerEl.closest(".mak__free_items.side-drawer");
  if (!parentDrawer || !parentDrawer.__mnParentScrollLocked) {
    drawerEl.style.removeProperty("overscroll-behavior");
    return;
  }

  var savedScrollTop = parentDrawer.__mnParentScrollTop;
  parentDrawer.__mnParentScrollLocked = false;

  if (parentDrawer.__mnParentScrollGuard) {
    parentDrawer.removeEventListener("scroll", parentDrawer.__mnParentScrollGuard);
  }

  makRestoreInlineStyle(parentDrawer, "overflow", parentDrawer.__mnParentOverflow);
  makRestoreInlineStyle(parentDrawer, "overflowY", parentDrawer.__mnParentOverflowY);
  makRestoreInlineStyle(parentDrawer, "overscrollBehavior", parentDrawer.__mnParentOverscroll);
  drawerEl.style.removeProperty("overscroll-behavior");

  if (restoreScroll !== false && savedScrollTop != null) {
    parentDrawer.scrollTop = savedScrollTop;
  }

  delete parentDrawer.__mnParentScrollTop;
  delete parentDrawer.__mnParentOverflow;
  delete parentDrawer.__mnParentOverflowY;
  delete parentDrawer.__mnParentOverscroll;
  delete parentDrawer.__mnParentScrollGuard;
};

window.makLockFreeGiftKeyboardPage = function (drawerEl) {
  if (!drawerEl || window.innerWidth >= 768 || window.__mnFreeGiftKeyboardPageState) return;

  var body = document.body;
  var html = document.documentElement;

  window.__mnFreeGiftKeyboardPageState = {
    scrollY: window.scrollY,
    body: {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      overflow: body.style.overflow
    },
    html: {
      overflow: html.style.overflow,
      overscrollBehavior: html.style.overscrollBehavior
    }
  };

  body.style.position = "fixed";
  body.style.top = `-${window.__mnFreeGiftKeyboardPageState.scrollY}px`;
  body.style.left = "0";
  body.style.right = "0";
  body.style.width = "100%";
  body.style.overflow = "hidden";
  html.style.overflow = "hidden";
  html.style.overscrollBehavior = "none";
};

window.makUnlockFreeGiftKeyboardPage = function () {
  var state = window.__mnFreeGiftKeyboardPageState;
  if (!state) return;

  var body = document.body;
  var html = document.documentElement;

  ["position", "top", "left", "right", "width", "overflow"].forEach(function (property) {
    makRestoreInlineStyle(body, property, state.body[property]);
  });
  ["overflow", "overscrollBehavior"].forEach(function (property) {
    makRestoreInlineStyle(html, property, state.html[property]);
  });

  window.__mnFreeGiftKeyboardPageState = null;
  window.scrollTo(0, state.scrollY);
};

if (!window.__mnFreeGiftKeyboardGuardBound) {
  window.__mnFreeGiftKeyboardGuardBound = true;
  var preScrollFreeGiftInput = function (drawer) {
    if (!drawer || window.innerWidth >= 768) return;

    var maxScrollTop = Math.max(0, drawer.scrollHeight - drawer.clientHeight);
    var remaining = maxScrollTop - drawer.scrollTop;

    // Redan nära botten: låt drawern ligga kvar där den är.
    if (remaining <= 70) return;

    var startScrollTop = drawer.scrollTop;
    var scrollDistance = Math.min(200, remaining - 70);
    var targetScrollTop = startScrollTop + scrollDistance;
    var startTime = null;
    var duration = 180;

    if (drawer.__mnPreKeyboardScrollFrame) {
      cancelAnimationFrame(drawer.__mnPreKeyboardScrollFrame);
    }

    var animate = function (time) {
      if (startTime === null) startTime = time;

      var progress = Math.min((time - startTime) / duration, 1);
      var eased = 1 - Math.pow(1 - progress, 3);
      drawer.scrollTop = startScrollTop + (targetScrollTop - startScrollTop) * eased;

      if (progress < 1) {
        drawer.__mnPreKeyboardScrollFrame = requestAnimationFrame(animate);
      } else {
        drawer.scrollTop = targetScrollTop;
        drawer.__mnPreKeyboardScrollFrame = null;
      }
    };

    drawer.__mnPreKeyboardScrollFrame = requestAnimationFrame(animate);
  };

  document.addEventListener("focusin", function (event) {
    if (window.innerWidth >= 768) return;
    if (!event.target.matches || !event.target.matches("#textInput, #maktextInput, #your-name")) return;

    var drawer = event.target.closest
      ? event.target.closest(".mak__free_items-inner.side-drawer-new.open")
      : null;
    if (!drawer) return;

    window.__mnFreeGiftKeyboardDrawer = drawer;
    window.makLockFreeGiftKeyboardPage(drawer);
    preScrollFreeGiftInput(drawer);
  }, true);

  document.addEventListener("focusout", function () {
    setTimeout(function () {
      var drawer = window.__mnFreeGiftKeyboardDrawer;
      var active = document.activeElement;
      if (drawer && active && drawer.contains(active)) return;

      window.__mnFreeGiftKeyboardDrawer = null;
    }, 0);
  }, true);

}

window.makCloseFreeProductInnerDrawer = function (drawerEl, restoreParentScroll) {
  if (!drawerEl) {
    return;
  }

  if (
    drawerEl.closest &&
    drawerEl.closest("[data-mn-shared-customize-drawer]") &&
    window.mnSharedCustomizeDrawer
  ) {
    window.mnSharedCustomizeDrawer.close();
    return;
  }

  if (window.makUnlockFreeGiftKeyboardPage) {
    window.makUnlockFreeGiftKeyboardPage();
  }
  window.__mnFreeGiftKeyboardDrawer = null;

  $(drawerEl).removeClass("open");

  setTimeout(function () {
    $(drawerEl).css("display", "none");

    if (window.makResetPdpGalleryState) {
      window.makResetPdpGalleryState(drawerEl);
    }

    if (window.innerWidth < 768) {
      window.makUnlockFreeProductParentScroll(drawerEl, restoreParentScroll);
    } else {
      $(".side-drawer").css("overflow-y", "scroll");
    }
  }, 300);
};

window.makGoToSecondFreeProduct = function (drawerEl) {
  var parentDrawer =
    document.querySelector(".mak__free_items.side-drawer.open") ||
    document.querySelector(".mak__free_items.side-drawer");

  if (!parentDrawer) {
    return;
  }

  var savedScrollTop = parentDrawer.scrollTop;
  var drawerTitle = parentDrawer.querySelector(".side-drawer-header h3");

  window.makCloseFreeProductInnerDrawer(drawerEl, false);

  setTimeout(function () {
    parentDrawer.classList.add("mak-second-free-product-step");
    parentDrawer.scrollTop = savedScrollTop;

    if (drawerTitle) {
      drawerTitle.textContent = "Välj ett smycke";
    }
  }, 350);
};


$(document).on("click", ".mak-atc-old", function (e) {
  e.preventDefault();
  var vrt = $(this).data("variant-id");

  $.ajax({
    url: "/cart/add.js",
    type: "POST",
    data: {
      id: vrt,
      quantity: 1,
    },
    dataType: "json",
    success: function () {
      // Dispatch a custom event to trigger the cart update
      document.dispatchEvent(
        new CustomEvent("on:cart:change", {
          bubbles: true,
          cancelable: false,
        })
      );

      // Open the cart drawer
      var $cartDrawer = $(".js-cart-drawer");
      $cartDrawer.attr({
        "aria-hidden": "false",
        open: "",
      });

      $cartDrawer
        .find(".cart-drawer__content")
        .removeClass("cart-drawer__content--hidden");

      $cartDrawer.focus();

      // Prevent page behind from scrolling when the cart drawer is open
$("body").css("overflow", "hidden");
$("html").css("overflow", "hidden");
$(".js-overlay").addClass("is-visible");
    },
    error: function (error) {
      console.error("Error adding to cart:", error);
    },
  });
});

// Close the cart drawer when #mak-cart-close or .drawer__close-btn is clicked
$(document).on("click", "#mak-cart-close, .drawer__close-btn", function () {
  $(".sticky-content-container").removeClass("no-sticky");

  var activeFreeGiftDrawer = document.querySelector(
    ".mak__free_items.side-drawer.open"
  );

  if (activeFreeGiftDrawer && window.makCloseFreeGiftDrawer) {
    window.makCloseFreeGiftDrawer(activeFreeGiftDrawer, {
      immediate: true,
      resetFlow: true,
      unlockScroll: false
    });
  }

  var $cartDrawer = $(".js-cart-drawer");

  // Check if the drawer is open
  if ($cartDrawer.attr("open")) {
    // Close the cart drawer
    $cartDrawer.attr({
      "aria-hidden": "true",
      open: null,
    });

    // Restore scroll position and scroll behavior
$("body").removeClass("fixed").css({
  top: "",
  overflow: ""
});

$("html").css("overflow", "");
$(".js-overlay").removeClass("is-visible");
  }
});

function makPortalFreeGiftDrawer(drawerEl, forceFromCart) {
  if (
    !drawerEl ||
    (window.innerWidth < 768 && !forceFromCart) ||
    drawerEl.classList.contains("mn-free-portal")
  ) {
    return;
  }

  var parent = drawerEl.parentNode;
  if (!parent) {
    return;
  }

  var backdrop = drawerEl.previousElementSibling;

  if (!backdrop || !backdrop.classList.contains("drawer__backdrop")) {
    backdrop = parent.querySelector(".drawer__backdrop");
  }

  drawerEl.__mnFreeDrawerPlaceholder = document.createComment(
    "mn-free-drawer-placeholder"
  );
  parent.insertBefore(drawerEl.__mnFreeDrawerPlaceholder, drawerEl);

  if (backdrop) {
    backdrop.__mnFreeBackdropPlaceholder = document.createComment(
      "mn-free-backdrop-placeholder"
    );
    backdrop.parentNode.insertBefore(
      backdrop.__mnFreeBackdropPlaceholder,
      backdrop
    );

    backdrop.classList.add("mn-free-portal-backdrop");
    document.body.appendChild(backdrop);
  }

  drawerEl.__mnFreeBackdrop = backdrop || null;
  drawerEl.classList.add("mn-free-portal");
  document.body.appendChild(drawerEl);
}

function makRestoreFreeGiftDrawer(drawerEl) {
  if (!drawerEl || !drawerEl.classList.contains("mn-free-portal")) {
    return;
  }

  var backdrop = drawerEl.__mnFreeBackdrop;
  var backdropPlaceholder = backdrop && backdrop.__mnFreeBackdropPlaceholder;
  var drawerPlaceholder = drawerEl.__mnFreeDrawerPlaceholder;

  if (backdrop && backdropPlaceholder && backdropPlaceholder.parentNode) {
    backdropPlaceholder.parentNode.insertBefore(backdrop, backdropPlaceholder);
    backdropPlaceholder.remove();
    backdrop.classList.remove("mn-free-portal-backdrop");
    delete backdrop.__mnFreeBackdropPlaceholder;
  }

  if (drawerPlaceholder && drawerPlaceholder.parentNode) {
    drawerPlaceholder.parentNode.insertBefore(drawerEl, drawerPlaceholder);
    drawerPlaceholder.remove();
  }

  drawerEl.classList.remove("mn-free-portal");
  delete drawerEl.__mnFreeDrawerPlaceholder;
  delete drawerEl.__mnFreeBackdrop;
}

$(document).ready(function () {
  // Open drawer
  $("#drawer__btn").on("click", function (e) {
    document.querySelectorAll(".main-product-font #maktextInput").forEach(function (inputWrapper) {
      inputWrapper.addEventListener("input", function () {
        this.closest(".mak-iec-input-wrapper").querySelector("#name-error1").style.display = "none";
      })
    })

    var goON = true;
    var onMultiple = false;
    document.querySelectorAll('.main-product-font .active #maktextInput').forEach(function (input, index) {
      if (input.value == '' && index == 0) {
        input.parentElement.querySelector('#name-error1').style.display = 'block';
        input.focus();


        if (!onMultiple) {
          goON = false;
          onMultiple = false;
          document.querySelector('.detail.product-column-right').scrollIntoView();
        }
      }
    });

    document.querySelectorAll('.main-product-font #your-name').forEach(function (input, index) {
      if (input.value == '' && index == 0) {
        input.parentElement.parentElement.querySelector('#name-error2').style.display = 'block';
        input.focus();
        if (!onMultiple) {
          goON = false;
          onMultiple = false;
          document.querySelector('.detail.product-column-right').scrollIntoView();
        }
      }
    });

    console.log(goON);

    if (!goON) {
      return;
    }


    var openDrawer = true;

    document.querySelectorAll(".main-product-font  .mak-iec-input-wrapper.active #maktextInput").forEach(function (inputWrapper) {
      inputWrapper.addEventListener("input", function () {
        this.closest(".mak-iec-input-wrapper").querySelector("#name-error1").style.display = "none";
      })
    })



    if (!openDrawer) {
      return
    }

    if (window.makResetFreeGiftFlow) {
      window.makResetFreeGiftFlow();
    }

    // If validation passes (or no validation needed), proceed with original functionality
    const productId = $(this).data("product-id");
    const drawer = $(`#side-drawer-${productId}`);

    if (window.innerWidth >= 768 && drawer[0]) {
      makPortalFreeGiftDrawer(drawer[0]);
    }

    drawer.css("display", "block");
    drawer.scrollTop(0);

    setTimeout(function () {
      drawer.addClass("open"); // Slide it in
    }, 10);

    $(".collection-slider-row").css("z-index", "-1");
    $(".collapsible-tabs__tab .disclosure__title").css("z-index", "-1");
    $(".drawer__backdrop").addClass("active");
    // $(".announcement-bar, .section-header").addClass("drawer-open");

    if (window.innerWidth < 768) {
      // Mobile: preserve the original behaviour.
      $(".sticky-content-container").addClass("no-sticky");
    }

    disableBodyScroll();
  });

  // Optional: Hide error message when user starts typing
  $("#your-name, #textInput").on("input", function () {
    $("#name-error1")? $("#name-error1").hide(): "";
    $("#name-error2")? $("#name-error2").hide(): "";
  });



  // Cart-only: when the cart has one jewel, choose the second AND third
  // in the same picker. With two jewels, choose the third only.
  // Product pages keep their existing flow; other pages lazy-load the picker.
  let mnGlobalCartGiftPickerPromise = null;

  const ensureGlobalCartGiftPicker = function () {
    let sectionHost = document.getElementById("shopify-section-global-cart-gift-picker");
    // Non-product pages have a dedicated cart picker. Never reuse a different
    // product/bundle drawer just because it also contains #mak-free-products.
    const current = sectionHost
      ? document.getElementById("mn-global-cart-gift-picker")?.querySelector("#mak-free-products")
      : document.querySelector(".mak__free_items.side-drawer #mak-free-products");
    if (current) return Promise.resolve(current.closest(".mak__free_items.side-drawer"));
    if (mnGlobalCartGiftPickerPromise) return mnGlobalCartGiftPickerPromise;

    const url = new URL(window.location.href);
    url.searchParams.set("section_id", "global-cart-gift-picker");
    mnGlobalCartGiftPickerPromise = fetch(url.toString(), {
      credentials: "same-origin", cache: "no-store",
      headers: { "Accept": "text/html" }
    }).then(function (response) {
      if (!response.ok) throw new Error("Failed to load free-gift selector");
      return response.text();
    }).then(function (html) {
      const page = new DOMParser().parseFromString(html, "text/html");
      const remoteHost = page.getElementById("shopify-section-global-cart-gift-picker");
      const source = remoteHost || page;
      const remotePicker = source.querySelector(".mak__free_items.side-drawer");
      if (!remotePicker || !remotePicker.querySelector("#mak-free-products")) {
        throw new Error("The free-gift selector is unavailable for the current cart");
      }
      // On pages where Shopify has not mounted the empty section wrapper yet,
      // create one only after a valid picker has been returned. This prevents
      // a click from silently navigating to the collection on those pages.
      if (!sectionHost) {
        sectionHost = document.createElement("div");
        sectionHost.id = "shopify-section-global-cart-gift-picker";
        document.body.appendChild(sectionHost);
      }
      // Product pages keep their own picker; only the global host is populated.
      sectionHost.innerHTML = remoteHost ? remoteHost.innerHTML : html;
      // Scripts inserted by innerHTML do not run. Only run the selector's
      // own inline scripts (never scripts inside product templates).
      Array.from(sectionHost.querySelectorAll("script")).forEach(function (oldScript) {
        if (oldScript.closest("template") || oldScript.src ||
            (oldScript.type && oldScript.type !== "text/javascript")) return;
        const newScript = document.createElement("script");
        newScript.textContent = oldScript.textContent;
        oldScript.replaceWith(newScript);
      });
      const picker = sectionHost.querySelector(".mak__free_items.side-drawer");
      if (!picker || !picker.querySelector("#mak-free-products")) {
        throw new Error("The free-gift selector could not be mounted");
      }
      return picker;
    }).catch(function (error) {
      mnGlobalCartGiftPickerPromise = null;
      throw error;
    });
    return mnGlobalCartGiftPickerPromise;
  };

  // Only headings belonging to the cart's picker are changed, and every
  // original is restored by makResetFreeGiftFlow when the picker closes.
  const setCartGiftPickerHeading = function (picker, step) {
    if (!picker) return;
    const text = step === 1
      ? 'Lägg till <strong>2 smycken</strong> och få ett <span>GRATIS</span>'
      : 'Lägg till <strong>1 smycke</strong> och få ett <span>GRATIS</span>';
    picker.querySelectorAll(
      '[data-mn-free-progress-heading], ' +
      '[data-mn-free-bottom-heading], ' +
      '.mak-drawer-gift-progress--step2 .mak-gift-progress-heading'
    ).forEach(function (heading) {
      if (!heading.hasAttribute('data-mn-cart-gift-original')) {
        heading.setAttribute('data-mn-cart-gift-original', heading.innerHTML);
      }
      heading.innerHTML = text;
    });
  };

  const updateCartGiftPickerLabels = function (picker) {
    if (!picker || !window.makCartGiftSelectionMode) return;
    const remaining = Math.max(1, (window.makCartGiftRequiredSelections || 1) - window.makGetSavedFreeGiftItems().length);
    const title = picker.querySelector('.mak-free-main-header h2');
    if (title) {
      if (!title.hasAttribute('data-mn-cart-original-title')) {
        title.setAttribute('data-mn-cart-original-title', title.textContent);
      }
      title.textContent = remaining === 1 ? 'Välj ett smycke' : 'Välj smycken';
    }
    const banner = picker.querySelector('.mn-free-one-plus-one-banner');
    if (banner && window.makCartGiftMode === 'bogo') {
      if (!banner.hasAttribute('data-mn-cart-original-banner')) {
        banner.setAttribute('data-mn-cart-original-banner', banner.textContent);
      }
      banner.textContent = window.makCartGiftStartCount >= 2
        ? 'KÖP 2 - FÅ 2 GRATIS SMYCKEN'
        : 'KÖP 1 - FÅ ETT GRATIS SMYCKE';
    }
  };

  const openCartGiftPicker = function (picker, startCount, requiredSelections, campaignMode) {
      // The bundle flow can leave an animated close pending. Reset only its
      // transient closing state; keep the existing picker and its contents.
      if (picker.__mnFreeGiftCloseTimer) {
        window.clearTimeout(picker.__mnFreeGiftCloseTimer);
        picker.__mnFreeGiftCloseTimer = null;
      }
      picker.classList.remove('mn-free-closing');
      picker.style.removeProperty('transform');
      picker.style.removeProperty('pointer-events');
      window.makResetFreeGiftFlow?.();
      window.makCartGiftSelectionMode = true;
      window.makCartGiftStartCount = startCount;
      window.makCartGiftRequiredSelections = requiredSelections;
      window.makCartGiftMode = campaignMode;
      window.makFreeGiftFlow = { step: campaignMode === 'three-for-two' && startCount === 2 ? 2 : 1, savedItems: [] };
      picker.classList.add('mak-cart-gift-selection');
      picker.classList.toggle('mak-cart-gift-bogo', campaignMode === 'bogo');
      picker.classList.toggle('mak-second-free-product-step', campaignMode === 'three-for-two' && startCount === 2);

      // Portal on both mobile and desktop so the selector slides above the
      // still-open cart, instead of being trapped in the product section.
      if (typeof makPortalFreeGiftDrawer === 'function') {
        makPortalFreeGiftDrawer(picker, true);
      }
      document.documentElement.classList.add('mn-cart-gift-flow');

      picker.style.display = 'block';
      picker.scrollTop = 0;
      picker.querySelector('.side-drawer_container')?.scrollTo(0, 0);
      picker.setAttribute('aria-hidden', 'false');
      window.makRenderFreeProgress?.(campaignMode === 'three-for-two' ? startCount : 1);
      if (campaignMode !== 'bogo') setCartGiftPickerHeading(picker, requiredSelections === 2 ? 1 : 2);
      updateCartGiftPickerLabels(picker);

      // Start from translateX(100%); next frame slides in right-to-left.
      void picker.offsetWidth;
      requestAnimationFrame(function () { picker.classList.add('open'); });
      document.querySelectorAll('.drawer__backdrop').forEach(function (backdrop) {
        backdrop.classList.add('active');
      });
      if (window.innerWidth < 768 && window.__mnFreeGiftPageScrollY == null) {
        window.__mnFreeGiftPageScrollY = window.scrollY;
      }
      document.body.style.overflow = 'hidden';
  };

  // One shared picker for legacy 2+1 and the 1+1 → 2+2 cart progression.
  document.addEventListener('click', function (event) {
    const buttonTarget = event.target.closest('#mak-cart-footer [data-mak-cart-gift-unlock], #mak-cart-footer [data-mak-cart-gift-choose], #mak-cart-footer [data-mak-cart-gift-bogo]');
    if (!buttonTarget) return;
    // Catch clicks on freshly AJAX-merged buttons before other bubble listeners.
    event.preventDefault();
    const isBogo = buttonTarget.hasAttribute('data-mak-cart-gift-bogo');
    const startCount = isBogo
      ? Number(buttonTarget.dataset.mnCartStartCount)
      : (buttonTarget.hasAttribute('data-mak-cart-gift-unlock') ? 1 : 2);
    const requiredSelections = isBogo
      ? Number(buttonTarget.dataset.mnCartRequired)
      : (startCount === 1 ? 2 : 1);
    const campaignMode = isBogo ? 'bogo' : 'three-for-two';
    const globalSection = document.getElementById('shopify-section-global-cart-gift-picker');
    const picker = globalSection
      ? document.getElementById('mn-global-cart-gift-picker')
      : document.querySelector('.mak__free_items.side-drawer');
    if (picker?.querySelector('#mak-free-products')) {
      event.preventDefault();
      openCartGiftPicker(picker, startCount, requiredSelections, campaignMode);
      return;
    }

    // Non-product pages may need to lazy-load the shared selector after
    // the first item is added via AJAX. Never follow the fallback link just
    // because Shopify hasn't mounted the global section wrapper yet.
    event.preventDefault();
    const button = buttonTarget;
    if (button.dataset.mnGiftPickerLoading === 'true') return;
    button.dataset.mnGiftPickerLoading = 'true';
    button.setAttribute('aria-busy', 'true');
    button.style.pointerEvents = 'none';
    ensureGlobalCartGiftPicker().then(function (loadedPicker) {
      // If the cart has changed in the meantime, don't start an outdated flow.
      if (!button.isConnected || !document.querySelector('#mak-cart-footer [data-mak-cart-gift-bogo], #mak-cart-footer [data-mak-cart-gift-unlock], #mak-cart-footer [data-mak-cart-gift-choose]')) return;
      openCartGiftPicker(loadedPicker, startCount, requiredSelections, campaignMode);
    }).catch(function (error) {
      console.error('Could not open free-gift selector:', error);
      window.alert('Kunde inte öppna smyckesväljaren. Försök igen.');
    }).finally(function () {
      delete button.dataset.mnGiftPickerLoading;
      button.removeAttribute('aria-busy');
      button.style.removeProperty('pointer-events');
    });
  }, true);

  // Close the active free-gift drawer through the shared lifecycle.
  function closeDrawer(event) {
    var source = event && event.currentTarget
      ? event.currentTarget
      : document.querySelector(".mak__free_items.side-drawer.open");

    window.makCloseFreeGiftDrawer(source, {
      immediate: false,
      resetFlow: true,
      unlockScroll: true
    });
  }

  if(!location.href.includes("/pages/upsell")){
    $(".side-drawer-header .side-pdp-close, .drawer__backdrop").on("click", closeDrawer);
  }

  // Open the shared customize drawer
  $(document).on("click", ".pdp_data-btn", function (e) {
    if (!window.mnSharedCustomizeDrawer) {
      return;
    }

    e.preventDefault();
    window.mnSharedCustomizeDrawer.openFromButton(this);
  });

  // Add current free product / open giftbox on product 2.
  // The second step is derived from both saved data and drawer state,
  // so the exact same product can safely be selected twice.
  $(document).on("click", ".mak__atc-upsell", function () {
    const currentFreeProductDrawer = this.closest(".mak__free_items-inner");

    if (!window.makValidateFreeProductDrawer(currentFreeProductDrawer)) {
      return;
    }

    if ((window.makCartGiftSelectionMode || window.makCartRecommendationMode) && currentFreeProductDrawer) {
      if (this.disabled || window.makCartGiftStepTransitioning) return;
      const selectedItem = window.makCollectFreeProductItemFromDrawer(currentFreeProductDrawer, this);
      if (!selectedItem?.id) return;

      // For promotions requiring two new jewels, stage the first selection
      // and add BOTH together only after the giftbox yes/no answer.
      if (window.makCartGiftSelectionMode && window.makCartGiftRequiredSelections === 2 &&
          !window.makGetSavedFreeGiftItems().length) {
        window.makCartGiftStepTransitioning = true;
        window.makFreeGiftFlow.savedItems = [selectedItem];
        window.makFreeGiftFlow.step = 2;
        window.makRenderFreeProgress?.(2);
        const cartPicker = currentFreeProductDrawer.closest('.mak__free_items.side-drawer');
        if (window.makCartGiftMode !== 'bogo') setCartGiftPickerHeading(cartPicker, 2);
        updateCartGiftPickerLabels(cartPicker);
        window.makGoToSecondFreeProduct(currentFreeProductDrawer);
        window.setTimeout(function () { window.makCartGiftStepTransitioning = false; }, 450);
        return;
      }

      // Cart started with TWO jewels, or jewel 2 has just been staged.
      // Defer all additions until the normal giftbox yes/no choice is finished.
      window.makCartPendingGiftItem = selectedItem;
      const giftbox = window.mnSharedCustomizeDrawer?.openGiftboxFromButton?.(this);
      if (!giftbox) {
        window.makCartPendingGiftItem = null;
        console.error("Could not open giftbox for the chosen free necklace.");
        return;
      }
      giftbox.querySelectorAll(".upsell-popup-data").forEach(function (offer) {
        offer.removeAttribute("data-add");
      });
      giftbox.querySelectorAll(".mainupsell").forEach(function (offer) {
        offer.style.display = "block";
      });
      giftbox.style.display = "block";
      giftbox.scrollTop = 0;
      requestAnimationFrame(function () {
        giftbox.classList.add("open");
        giftbox.style.overflowY = "auto";
      });
      return;
    }

    if (!location.href.includes("/pages/upsell")) {
      window.makFreeGiftFlow = window.makFreeGiftFlow || {
        step: 1,
        savedItems: []
      };

      const parentDrawer =
        document.querySelector(".mak__free_items.side-drawer.open") ||
        document.querySelector(".mak__free_items.side-drawer");

      const hasFirstProduct =
        Array.isArray(window.makFreeGiftFlow.savedItems) &&
        window.makFreeGiftFlow.savedItems.length > 0;

      const isSecondStep =
        window.makFreeGiftFlow.step === 2 ||
        hasFirstProduct ||
        !!parentDrawer?.classList.contains("mak-second-free-product-step");

      // Only the Main Product picker uses the editor's 1/2 selection count.
      // Cart gift selection/recommendations continue through their own flow above.
      const requiredSelections =
        parentDrawer?.getAttribute("data-mn-main-free-selections") === "1" ? 1 : 2;

      if (!isSecondStep) {
        const firstFreeProductItem = window.makCollectFreeProductItemFromDrawer(
          currentFreeProductDrawer,
          this
        );

        if (!firstFreeProductItem || !firstFreeProductItem.id) {
          return;
        }

        window.makFreeGiftFlow.savedItems = [firstFreeProductItem];
        window.makFreeGiftFlow.step = 2;
        window.makRenderFreeProgress?.(2);

        // In 1+1 mode, the first selected item IS the free jewelry.
        // Open the same giftbox immediately, without requesting another item.
        if (requiredSelections === 2) {
          window.makGoToSecondFreeProduct(currentFreeProductDrawer);
          return;
        }
      }

      window.makFreeGiftFlow.step = 2;
      window.makRenderFreeProgress?.(requiredSelections + 1);

      const upsellId = $(this).data("upsell-id");
      const sharedGiftbox =
        window.mnSharedCustomizeDrawer?.openGiftboxFromButton?.(this);

      const upsellDrawer = sharedGiftbox
        ? $(sharedGiftbox)
        : $(`#new-drawer-upsell-${upsellId}`);

      if (!upsellDrawer.length) {
        return;
      }

      upsellDrawer.css("display", "block");
      upsellDrawer.scrollTop(0);

      setTimeout(function () {
        upsellDrawer.addClass("open");
        upsellDrawer.css("overflow-y", "auto");
        upsellDrawer.scrollTop(0);
      }, 10);
    } else {
      console.log("Upsell page - drawer not opened");
      this.parentElement.querySelector(".upsell-decline-offer button").click();
    }
  });

  // Close only the inner drawer that owns the clicked button.
  $(document).on("click", ".side-drawer-header-new .side-pdp-close", function () {
    var drawer = this.closest(".mak__free_items-inner");
    var parentDrawer = this.closest(".mak__free_items.side-drawer");

    if (!drawer) {
      return;
    }

    if (window.makUnlockFreeGiftKeyboardPage) {
      window.makUnlockFreeGiftKeyboardPage();
    }
    window.__mnFreeGiftKeyboardDrawer = null;

    drawer.classList.remove("open");

    setTimeout(function () {
      drawer.style.display = "none";
      drawer.style.removeProperty("overflow");
      drawer.style.removeProperty("overflow-y");

      if (window.makResetPdpGalleryState) {
        window.makResetPdpGalleryState(drawer);
      }

      if (parentDrawer) {
        if (window.innerWidth < 768) {
          window.makUnlockFreeProductParentScroll(drawer, true);
        } else {
          parentDrawer.style.overflowY = "scroll";
        }
      }
    }, 300);
  });

  $(document).on("click", ".side-drawer-header-upsell .side-pdp-close-upsell", function () {
    var drawer = this.closest(".mak__upsell-inner");
    var productDrawer = this.closest(".mak__free_items-inner");

    if (!drawer) {
      return;
    }

    drawer.classList.remove("open");

    setTimeout(function () {
      drawer.style.display = "none";
      drawer.style.removeProperty("overflow");
      drawer.style.removeProperty("overflow-y");

      if (productDrawer) {
        productDrawer.style.overflowY = "scroll";
      }
    }, 300);
  });
  // Initialize sliders
  $(".product-main-slider").slick({
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    speed: 300,
    dots: false,
    adaptiveHeight: true,
    autoplay: false, // Enable autoplay
    autoplaySpeed: 1500, // Delay between slides (in ms)
    prevArrow:
      "<img class='prev ars-prv slick-prev' src='https://cdn.shopify.com/s/files/1/0845/9458/9983/files/Group_1479.png?v=1712181283'>",
    nextArrow:
      "<img class='next ars-nxt slick-next' src='https://cdn.shopify.com/s/files/1/0845/9458/9983/files/Group_1478.png?v=1712181283'>",
    asNavFor: ".product-nav-slider",
  });


  $(".product-nav-slider").slick({
    slidesToShow: 5.5,
    slidesToScroll: 1,
    asNavFor: ".product-main-slider",
    dots: false,
    infinite: false,
    vertical: false,
    centerMode: false,
    arrows: false,
    focusOnSelect: true,
  });

  // Functions to manage body scroll
  function disableBodyScroll() {
    if (window.innerWidth < 768 && window.__mnFreeGiftPageScrollY == null) {
      window.__mnFreeGiftPageScrollY = window.scrollY;
    }
    $("body").css("overflow", "hidden");
  }

  function enableBodyScroll() {
    $("body").css("overflow", "");
  }
});

// Cart-only free gift: finish through the existing giftbox (yes/no) before
// adding the already-customized third item. Intercept in capture phase so the
// normal two-product upsell handlers cannot add a duplicate paid product.
(function () {
  if (window.__mnCartGiftboxFinishBound) return;
  window.__mnCartGiftboxFinishBound = true;

  // The theme raises an already-open cart to z-index:1000005 whenever a
  // giftbox opens. In the cart-gift flow that would place the cart in FRONT
  // of the shared customize drawer (z-index:1000001), exposing the cart
  // during the giftbox slide-in. Keep all three drawers stacked correctly:
  // cart < customize < giftbox backdrop < giftbox. Only for this flow.
  var style = document.createElement("style");
  style.id = "mn-cart-giftbox-layer";
  style.textContent =
    'html.mn-cart-gift-flow .mn-builder-drawer.is-open{z-index:1000008!important}' +
    'html.mn-cart-gift-flow .mn-builder-giftbox-backdrop{z-index:1000010!important}' +
    'html.mn-cart-gift-flow .mak__upsell-inner.side-drawer-upsell[data-mn-builder-giftbox]{z-index:1000011!important}' +
    '@media(max-width:768px){html.mn-cart-gift-flow .mn-builder-giftbox-backdrop{background:rgba(0,0,0,.12)!important}}';
  document.head.appendChild(style);

  document.addEventListener("click", function (event) {
    if (!(window.makCartGiftSelectionMode || window.makCartRecommendationMode) || !window.makCartPendingGiftItem) return;
    var button = event.target.closest(
      ".mak__upsell-inner[data-mn-builder-giftbox] .mak__atc-final, " +
      ".mak__upsell-inner[data-mn-builder-giftbox] .mak__atc-upsell-final"
    );
    if (!button) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    if (button.disabled) return;

    var giftbox = button.closest(".mak__upsell-inner[data-mn-builder-giftbox]");
    var panel = button.closest(".upsell-popup-data");
    var wantsGiftbox = button.classList.contains("mak__atc-upsell-final");
    if (panel) panel.setAttribute("data-add", wantsGiftbox ? "true" : "false");

    // Add all staged choices together (e.g. 2→4 in BOGO mode).
    // Cart recommendations continue to add only their selected recommendation.
    var items = window.makCartGiftSelectionMode && window.makCartGiftRequiredSelections === 2
      ? window.makGetSavedFreeGiftItems()
      : [];
    items.push(window.makCartPendingGiftItem);
    giftbox.querySelectorAll('.upsell-popup-data[data-add="true"]').forEach(function (offer) {
      var id = Number(offer.getAttribute("data-id"));
      if (!id) return;
      var quantityInput = offer.querySelector(".mak-quantity-input");
      var qty = quantityInput ? parseInt(quantityInput.value, 10) : 1;
      items.push({ id: id, quantity: Math.max(1, Number.isFinite(qty) ? qty : 1) });
    });

    button.disabled = true;
    button.classList.add("is-loading");
    fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Accept": "application/json" },
      body: JSON.stringify({ items: items })
    }).then(function (response) {
      if (!response.ok) throw new Error("Could not add the selected free jewelry");

      // Wait until Symmetry has merged the new cart HTML before uncovering it.
      // This also updates the mini-cart count and the 3-for-2 banner.
      return new Promise(function (resolve) {
        var form = document.getElementById("mak-cart-form");
        if (!form) {
          document.dispatchEvent(new CustomEvent("on:cart:change", { bubbles: true }));
          resolve();
          return;
        }
        var finished = false;
        var timeout;
        function done() {
          if (finished) return;
          finished = true;
          clearTimeout(timeout);
          form.removeEventListener("on:cart:after-merge", done);
          resolve();
        }
        form.addEventListener("on:cart:after-merge", done);
        timeout = setTimeout(function () {
          // Fallback if another cart listener swallowed the refresh event.
          if (typeof form.refresh === "function") {
            form.refresh({ suppressDrawerOpen: true }).then(done, done);
          } else {
            done();
          }
        }, 6000);
        document.dispatchEvent(new CustomEvent("on:cart:change", { bubbles: true }));
      });
    }).then(function () {
      var wasRecommendation = !!window.makCartRecommendationMode;
      window.mnSharedCustomizeDrawer?.close?.({ immediate: true });
      // The free-gift picker is untouched for paid cart recommendations.
      if (wasRecommendation) {
        window.makCartRecommendationMode = false;
        window.makCartPendingGiftItem = null;
        document.documentElement.classList.remove("mn-cart-gift-flow");
      }
      var picker = wasRecommendation ? null : document.querySelector(".mak__free_items.mak-cart-gift-selection");
      if (picker && window.makCloseFreeGiftDrawer) {
        window.makCloseFreeGiftDrawer(picker, {
          immediate: true, resetFlow: true,
          unlockScroll: false, returnToCart: false
        });
      }
      // The cart stays open behind the giftbox, so its normal open-event scroll
      // reset never runs. Return to the top after either giftbox choice.
      var cartDrawer = document.getElementById("mak-cart-drawer");
      if (cartDrawer) {
        var resetCartScroll = function () {
          if (typeof cartDrawer.resetScrollToTop === "function") {
            cartDrawer.resetScrollToTop();
          } else {
            var content = cartDrawer.querySelector("#mak-cart-content");
            if (content) content.scrollTop = 0;
            cartDrawer.scrollTop = 0;
          }
        };
        resetCartScroll();
        requestAnimationFrame(resetCartScroll);
      }
      button.disabled = false;
      button.classList.remove("is-loading");
    }).catch(function (error) {
      console.error("Cart giftbox add failed:", error);
      button.disabled = false;
      button.classList.remove("is-loading");
      window.alert("Det gick inte att lägga till smycket. Försök igen.");
    });
  }, true);
})();

$(document).on("change", ".mak_variants_radio", function () {
  if ($(this).is(":checked")) {
    var selectedValue = $(this).next('label').find(".js-value").text();
    var productId = $(this).closest(".mak_option-slector").data("product-id");

    $(this).closest('.option-selector__btns').prev('.label').find(".mak_slected-variant").text(selectedValue);
  }
});
$(document).on("click", ".mak__atc-final", function (e) {
  var finalbtn = $(this);

  finalbtn.closest(".upsell-popup-data").attr("data-add", false);
  var upsellIds = finalbtn.closest(".side-drawer_inner-container-upsell").find(".upsell-popup-data");
  
  if (finalbtn.closest(".main-product-gift-product").length) {
    if (!finalbtn.closest(".cart-drawer-upsell.disabled-main-product").length) {
      $(".buy-buttons-row .add-to-cart").trigger("click");
    }
    document.querySelector('.main-product-gift-product').classList.remove("open");
    return;
  }

  window.isMainProductAdded = false;
  const $button = $(this);
  const localContainer = $button.closest(".mak__new__pdp-data");
  const container = localContainer.length
    ? localContainer
    : $("[data-mn-shared-customize-drawer] .mak__new__pdp-data").first();
  const productId = $button.data("product-id");
  const defaultVariantId = $button.attr("default-variant-id");
  const selectedOption = container
    .find(
      '.mak__line-items[data-product-id="' +
      productId +
      '"] #valj-justerbar-kedjelangd'
    )
    .val();

  var checkedVariant = container.find(
    ".mak__variants-main .mak_variants_radio:checked"
  );

  var variantId = 0;

  console.log(container.find(".mak__variants-main"))

  // Determine the variant ID to use
  if (checkedVariant.length > 1) {
    var master = container.find(".mak__variants-main select[name='id'] option");

    var selected = checkedVariant;
    var variantname = '';

    for (let i = 0; i < selected.length; i++) {
      const element = selected[i].value;
      if (i == selected.length - 1) {
        variantname = variantname + element;
      }
      else {
        variantname = variantname + element + ' / ';
      }
    }


    for (let j = 0; j < master.length; j++) {
      const element = master[j].getAttribute("data-name");
      if (element == variantname) {
        variantId = master[j].getAttribute("value");
      }
    }

    console.log("Variant ID:", variantId);

  } else {

    variantId = checkedVariant.length
      ? checkedVariant.data("variant-id")
      : defaultVariantId;

    console.log("Variant ID:", variantId);
    console.log("Product ID:", productId);
  }



  const productWrapper = container.find(".mak-input-wrapper");
  const selectedFontName = container.find(".mak-input-wrapper").find("#fontSelected").val()
  console.log(selectedFontName)
  const selectedFont = productWrapper
    .find("#maktextInput-val, #your-name")
    .val();

  console.log(selectedFont)

  console.log("Selected Font:", selectedFont);
  console.log("AKJ PRODUCT ADDED");
  if (selectedOption) {
    console.log("Selected Dropdown Option:", selectedOption);
  }

  var properties = {};
  
  var productNames = productWrapper.find(".mak-iec-input-wrapper.active")
  console.log(productNames.length)
  if (productNames.length > 1) {
    for (let index = 0; index < productNames.length; index++) {
      const selectedFont = $(productNames[index]).find("#maktextInput-val, #your-name").val();
      if (selectedFont) {
        properties[window.language_name + " " + (index + 1)] = selectedFont;
      }
    }
  } else {
    if (selectedFont) {
      properties[window.language_name] = selectedFont;
    }
  }

  if (selectedFontName) {
    properties["Typsnitt"] = selectedFontName;

  }



  if (selectedOption) {
    properties["Kedjelängd"] = selectedOption;
  }


  var upsellItem = [];

  for (let i = 0; i < upsellIds.length; i++) {
    const id = parseInt($(upsellIds[i]).attr("data-id"));
    var isadd = $(upsellIds[i]).attr("data-add");
    if (isadd == 'true') { 
      upsellItem.push({
        id: id,
        quantity: 1
      })
    }

    $(upsellIds[i]).removeAttr("data-add");
  }

  if (window.makAppendSavedFreeGiftItems) {
    window.makAppendSavedFreeGiftItems(upsellItem);
  }

  // 1+1: den sparade produkten ovan är samma smycke som variantId.
  // Lägg inte till en extra kopia från den öppna anpassningsdrawern.
  if (!window.makIsMainProductOnePlusOneFinish()) {
    upsellItem.push({
      id: variantId,
      quantity: 1,
      properties: window.makCombineMultiNameProperties(properties)
    });
  }

  


  // Add loading class to the button and disable it
  $button.addClass("is-loading").prop("disabled", true);
  

  $.ajax({
    url: "/cart/add.js",
    type: "POST",
    data: { items: upsellItem }, // Properly encode data for the request
    dataType: "json",
    success: function () {
      // Close the free-gift drawer exactly when Symmetry starts opening the cart.
      if (window.makPrepareFreeGiftCartHandoff) {
        window.makPrepareFreeGiftCartHandoff(finalbtn[0]);
      }

      $(".buy-buttons-row .add-to-cart").trigger("click");
      window.isMainProductAdded = true;

      // Add success class after successful addition to cart
      $button.removeClass("is-loading").addClass("is-success");
      document.querySelector('.main-product-gift-product')
      ? document.querySelector('.main-product-gift-product').classList.remove("open")
      : "";

      // Remove the success class after 2 seconds
      setTimeout(() => {
        $button.removeClass("is-success").prop("disabled", false);
      }, 2000);

      document.querySelectorAll("#textInput").forEach(function (textInput) {
        textInput.value = "";
        textInput.nextElementSibling.value = "";
      })

      document.querySelectorAll("#maktextInput").forEach(function (textInput) {
        textInput.value = "";
        textInput.nextElementSibling.value = "";
      })

      if(location.href.includes("/pages/upsell")){
        console.log('AKJ OPEN CART HERE');
        location.assign('/checkout');
        // document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
        // setTimeout(() => {
        //   document.querySelector('.mak__free_items.side-drawer.open').classList.remove('open');
        //   document.querySelector('.drawer__backdrop').style.display = 'none';
        //   document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
        // }, 1000);
      }

    },
    error: function (xhr, status, error) {
      console.error("Error adding item to cart:", error);
      $button.removeClass("is-loading").prop("disabled", false);
    },
  });
});

// Enhanced Quantity Selector with Validation

$(document).on("click", ".mak-increase-quantity", function () {
  const $input = $(this).siblings(".mak-quantity-input");
  let value = parseInt($input.val()) || 1;
  $input.val(value + 1).trigger("change");
});

$(document).on("click", ".mak-decrease-quantity", function () {
  const $input = $(this).siblings(".mak-quantity-input");
  let value = parseInt($input.val()) || 1;
  if (value > 1) {
    $input.val(value - 1).trigger("change");
  }
});

// Input validation
$(document).on("change input", ".mak-quantity-input", function () {
  let value = parseInt($(this).val());
  if (isNaN(value) || value < 1) {
    $(this).val(1);
  }
});
// Log selected chain length on change
$(document).on("change", "#valj-justerbar-kedjelangd", function () {
  const selectedValue = $(this).val();
  console.log("Selected Kejdelängd:", selectedValue);
});

window.isMainProductAdded = true;

// Main Add to Cart with Upsell and Dynamic Quantity
$(document).on("click", ".mak__atc-upsell-final", function (e) {
  var upsellbtn = $(this);
  upsellbtn.closest(".upsell-popup-data").attr("data-add", true);

  var upsellIds = upsellbtn.closest(".side-drawer_inner-container-upsell").find(".upsell-popup-data");

  window.isMainProductAdded = false;
  e.preventDefault();
  e.stopPropagation();
  var isMainProductToBeAdded = true;
 
  if(document.querySelector('.cart-drawer-upsell.disabled-main-product')){
    isMainProductToBeAdded = false;
  }

  const $button = $(this);
  const localContainer = $button.closest(".mak__new__pdp-data");
  const container = localContainer.length
    ? localContainer
    : $("[data-mn-shared-customize-drawer] .mak__new__pdp-data").first();

  const productId = $button.data("product-id");
  const upsellVariantId = $button.data("upsell-variant-id");

  // Get upsell quantity
  const upsellContainer = $button.closest(".mak_upsell-container");
  const selectedQuantity =
    parseInt(upsellContainer.find(".mak-quantity-input").val()) || 1;

  // Get selected dropdown (chain length)
  const selectedOption = container.find(`#valj-justerbar-kedjelangd`).val();
  if (selectedOption) {
    console.log("Selected Dropdown Option:", selectedOption);
  }

  // Get font input
  const productWrapper = container.find(".mak-input-wrapper.drawer-product-font");
  const selectedFont = productWrapper
    .find("#maktextInput-val, #your-name")
    .val();
  console.log("Selected Font:", selectedFont);


  var selectedFontName = container.find(".mak-input-wrapper.drawer-product-font").find("#fontSelected").val()



  // Get selected or default variant ID
  const defaultVariantId = $button.attr("default-variant-id");
  const checkedVariant = container.find(
    ".mak__variants-main .mak_variants_radio:checked"
  );

  var variantId = 0;


  if (checkedVariant.length > 1) {
    var master = container.find(".mak__variants-main select[name='id'] option");

    var selected = checkedVariant;
    var variantname = '';

    for (let i = 0; i < selected.length; i++) {
      const element = selected[i].value;
      if (i == selected.length - 1) {
        variantname = variantname + element;
      }
      else {
        variantname = variantname + element + ' / ';
      }
    }


    for (let j = 0; j < master.length; j++) {
      const element = master[j].getAttribute("data-name");
      if (element == variantname) {
        variantId = master[j].getAttribute("value");
      }
    }

    console.log("Variant ID:", variantId);

  } else {

    variantId = checkedVariant.length
      ? checkedVariant.data("variant-id")
      : defaultVariantId;

    console.log("Variant ID:", variantId);
    console.log("Product ID:", productId);
  }

  // Prepare main product data with properties
  

  var productNames = productWrapper.find(".mak-iec-input-wrapper.active")
  console.log(productNames.length);
  var properties = {};
  if (productNames.length > 1) {
    for (let index = 0; index < productNames.length; index++) {
      const selectedFont = $(productNames[index]).find("#maktextInput-val,#your-name").val();
      if (selectedFont) {
        properties[window.language_name + " " + (index + 1)] = selectedFont;
      }
    }
  } else {
    if (selectedFont) {
      properties[window.language_name] = selectedFont;
    }
  }


  if (selectedFontName) {
    properties["Typsnitt"] = selectedFontName;
  }

  if (selectedOption) {
    properties["Kedjelängd"] = selectedOption;
  }

  console.log(mainItem)

  var mainItem = [{
    id: variantId,
    quantity: 1,
    properties: window.makCombineMultiNameProperties(properties),
  }];

  if (window.makIsMainProductOnePlusOneFinish()) {
    // 1+1: skicka endast det redan sparade gratissmycket.
    // Vald presentask läggs separat till i upsellItem.
    mainItem = window.makGetSavedFreeGiftItems();
  } else if (window.makPrependSavedFreeGiftItems) {
    // 2+1: behåll båda valda smyckena.
    mainItem = window.makPrependSavedFreeGiftItems(mainItem);
  }


  // Prepare upsell product data
  // const upsellItem = {
  //   id: upsellVariantId,
  //   quantity: selectedQuantity,
  // };

  var upsellItem = [];

  for (let i = 0; i < upsellIds.length; i++) {
    const id = parseInt($(upsellIds[i]).attr("data-id"));
    var isadd = $(upsellIds[i]).attr("data-add");
    if (isadd == 'true') { 
      upsellItem.push({
        id: id,
        quantity: selectedQuantity
      })
    }

    $(upsellIds[i]).removeAttr("data-add");
  }

  

  console.log(upsellItem, upsellIds)

  var cartItems = [mainItem, upsellItem];

  if (upsellbtn.closest(".main-product-gift-product").length > 0) {
    cartItems = [upsellItem];
  }

  
  console.log("Cart items to be added:", JSON.stringify(cartItems, null, 2));

  $button.addClass("is-loading").prop("disabled", true);

  // Add both items to cart via AJAX
  let promiseChain = Promise.resolve();

  cartItems.forEach((item, index) => {
    promiseChain = promiseChain.then(() => {
      return $.ajax({
        url: "/cart/add.js",
        method: "POST",
        data: { items: item },
        dataType: "json",
      })
        .then((response) => {
          console.log(
            `Successfully added ${index === 0 ? "main" : "upsell"} product:`,
            response
          );

          return response;
        })
        .catch((error) => {
          console.error(
            `Error adding ${index === 0 ? "main" : "upsell"} product:`,
            error
          );
          return Promise.resolve(); // Continue even if one fails
        });
    });
  });

  // Final UI updates
  promiseChain
    .then(() => {
      return $.getJSON("/cart.js").then((cart) => {
        console.log("Current cart contents:", cart);

        const upsellInCart = cart.items.find(
          (item) => item.id === upsellVariantId
        );
        if (upsellInCart) {
          console.log(
            `Upsell product in cart. Expected: ${selectedQuantity}, Actual: ${upsellInCart.quantity}`
          );
        }

        document.querySelector('.main-product-gift-product') ? document.querySelector('.main-product-gift-product').classList.remove("open"): "";
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
          bubbles: true 
        }));
        
        window.isMainProductAdded = true;

        if (isMainProductToBeAdded) {
          // Close the free-gift drawer through the cart drawer lifecycle.
          if (window.makPrepareFreeGiftCartHandoff) {
            window.makPrepareFreeGiftCartHandoff(upsellbtn[0]);
          }

          $(".buy-buttons-row .add-to-cart").trigger("click");  
        }
        
        $button.removeClass("is-loading").addClass("is-success");

        setTimeout(() => {
          $button.removeClass("is-success").prop("disabled", false);



          document.querySelectorAll("#textInput").forEach(function (textInput) {
            textInput.value = "";
            textInput.nextElementSibling.value = "";
          })

          document.querySelectorAll("#maktextInput").forEach(function (textInput) {
            textInput.value = "";
            textInput.nextElementSibling.value = "";
          })

        }, 2000);
      });
    })
    .catch((error) => {
      console.error("Cart addition error:", error);
      $button.removeClass("is-loading").prop("disabled", false);
    });


});

$(document).ready(function () {
  const originalFontResetHTML = $("#mak_font-reset").html();
  $(".your-name-input").on("input", function () {
    let inputValue = $(this).val();
    $("#hidden-name-new-field").val(inputValue); // Sync hidden input
  });

  // Prevent form submission if name field is empty
  $(".js-product-form").on("submit", function (e) {
    // let userInput = $(".your-name-input").val().trim();

    $("#mak_font-reset").html(originalFontResetHTML);
    $(".side-drawer-new").css("overflow-y", "auto");
  });
});

$(document).ready(function () {
  // Store the original HTML of the font selector for reset
  let savedFontSelectorHTML = $(".font-selector-normal").html();

  // Handle input typing and update hidden field with formatted text
  $("#textInput").on("input", function () {
    let enteredName = $(this).val();
    let selectedFont =
      $(".button-wrapper-brd.selected .btn-i").data("name") || "";
    $("#hidden-name-new-field").val(
      enteredName
    );
    $(this).closest(".mak_option-slector").find("#fontSelected").val(selectedFont);
    this.closest(".product-form").querySelector("#hidden-name-new-font")
      ? this.closest(".product-form").querySelector("#hidden-name-new-font").value = selectedFont
      : ""
  });

  // $(this).closest(".mak_option-slector").find("#fontSelected").val(selectedFont);

  // Detect when a new font is selected (when 'selected' class is added)
  $(".font-selector-normal .button-wrapper-brd").on("click", function () {
    // Remove 'selected' class from other elements
    $(".font-selector-normal .button-wrapper-brd").removeClass("selected");

    // Add 'selected' class to the clicked element
    $(this).addClass("selected");

    // Get the selected font name
    let selectedFont = $(this).find(".btn-i").data("name") || "";
    let enteredName = $("#textInput").val();
    $("#hidden-name-new-field").val(
      enteredName
    );
    $(this).closest(".mak_option-slector").find("#fontSelected").val(selectedFont);
    this.closest(".product-form").querySelector("#hidden-name-new-font")
      ? this.closest(".product-form").querySelector("#hidden-name-new-font").value = selectedFont
      : ""
  });

  // On form submission
  $(".js-product-form").on("submit", function (e) {
    let currentNameInput = $("#maktextInput").val().trim();

    // Reset font selector and scroll state
    $(".font-selector-normal").html(savedFontSelectorHTML);
    $(".side-drawer-new").css("overflow-y", "auto");

    // Optional: re-sync formatted value before submit (in case last click didn't trigger keyup)
    let selectedFont =
      $(".button-wrapper-brd.selected .btn-i").data("name") || "";
    $("#hidden-name-new-field").val(
      currentNameInput
    );

    $(this).find("#fontSelected").val(selectedFont);
    this.closest(".product-form").querySelector("#hidden-name-new-font")
      ? this.closest(".product-form").querySelector("#hidden-name-new-font").value = selectedFont
      : ""
  });
});

$(document).ready(function () {
  // Update hidden input whenever dropdown selection changes
  $("#valj-justerbar-kedjelangd-new").on("change", function () {
    let selectedValue = $(this).val();
    $("#hidden-length-field").val(selectedValue);
  });

  // Set default value on page load (if needed)
  let defaultValue = $("#valj-justerbar-kedjelangd-new").val();
  $("#hidden-length-field").val(defaultValue);
});



document.addEventListener('DOMContentLoaded', function () {
  const images = document.querySelectorAll('img');
  images.forEach(function (img) {
    img.setAttribute('loading', 'lazy');
  });
});

window.offsetvalue = screen.width > 768 ? 64 : 40;

let differentSlider = null;

function initDifferentSlider() {
  const sliderElement = document.querySelector("#mak-cart-upsell .different_slider");

  if (!sliderElement || typeof Swiper === "undefined") {
    return;
  }

  if (sliderElement.swiper && !sliderElement.swiper.destroyed) {
    sliderElement.swiper.destroy(true, true);
  }

  // Keep image clicks from moving cart recommendations to the first visible slot.
  // Swiper's accessibility focus handler otherwise calls slideTo() on the image link.
  if (!sliderElement.dataset.mnCartImageFocusGuard) {
    sliderElement.addEventListener("focus", function (event) {
      if (event.target.closest(".swiper-slide .image-cont a.product-link")) {
        event.stopImmediatePropagation();
      }
    }, true);
    sliderElement.dataset.mnCartImageFocusGuard = "1";
  }

  differentSlider = new Swiper(sliderElement, {
    slidesPerView: 2.85,
    slidesOffsetAfter: window.offsetvalue,
    spaceBetween: 4,
    loop: false,
    navigation: {
      nextEl: "#mak-cart-drawer .slider-nav__btn.has-ltr-icon[name='next']",
      prevEl: "#mak-cart-drawer .slider-nav__btn.has-ltr-icon[name='prev']",
    }
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initDifferentSlider);
} else {
  initDifferentSlider();
}

document.addEventListener("on:cart:after-merge", function () {
  initDifferentSlider();
});

function observeCartChanges() {
  const cartObserver = new PerformanceObserver((list) => {
    list.getEntries().forEach((entry) => {
      const isValidRequestType = ['xmlhttprequest', 'fetch'].includes(entry.initiatorType);
      const isCartChangeRequest = /\/cart\//.test(entry.name);
      if (isValidRequestType && isCartChangeRequest) {


        fetch('/').then(function (response) {
          // The API call was successful!
          return response.text();
        }).then(function (html) {
          // Convert the HTML string into a document object
          var parser = new DOMParser();
          var doc = parser.parseFromString(html, 'text/html');
          document.querySelector('.cart-drawer__content-item').innerHTML = doc.querySelector(".cart-drawer__content-item").innerHTML;
          document.querySelector("cc-cart-cross-sell .product-info-label").innerHTML = doc.querySelector("cc-cart-cross-sell .product-info-label").innerHTML;
          document.querySelectorAll("cc-cart-cross-sell .product-price.product-price--block").forEach(function (cc_cart, index) {
            cc_cart.innerHTML = doc.querySelectorAll("cc-cart-cross-sell .product-price.product-price--block")[index].innerHTML;
          })

          document.querySelectorAll(".different_slider .custom-label").forEach(function (label, index) {
            label.innerHTML = doc.querySelectorAll(".different_slider .custom-label")[index].innerHTML;
          })


          // $(".cart-item-upsells .slider .slider__grid").slick({
          //   infinite: false,
          //   slidesToShow: 2
          // })
        }).catch(function (err) {
          // There was an error
          console.warn('Something went wrong.', err);
        });



        if (window.isMainProductAdded) {
          document.querySelectorAll('#your-name').forEach(function (a) {
            a.value = ""
          });
          $('#maktextInput').val('');
          $('#makmyHeading').html('');
          $('#textInput').val('');
          $('#myHeading').html('');
          $(".fontPreview").removeClass("partial-width");
          $(".input-field").removeClass("partial-width");
          $(".fontPreview").removeClass("mak-partial-width");
          $(".input-field").removeClass("mak-partial-width");


          document.querySelectorAll("#textInput").forEach(function (textInput) {
            textInput.value = "";
            textInput.nextElementSibling.value = "";
          })

          document.querySelectorAll("#maktextInput").forEach(function (textInput) {
            textInput.value = "";
            textInput.nextElementSibling.value = "";
          })




          $("#textInput").on("input", function () {
            let enteredName = $(this).val();
            let selectedFont =
              $(".button-wrapper-brd.selected .btn-i").data("name") || "";
            $("#hidden-name-new-field").val(
              enteredName
            );
            $(this).closest(".mak_option-slector").find("#fontSelected").val(selectedFont);
            this.closest(".product-form").querySelector("#hidden-name-new-font")
              ? this.closest(".product-form").querySelector("#hidden-name-new-font").value = selectedFont
              : ""
          });

          $(".your-name-input").on("input", function () {
            let inputValue = $(this).val();
            $("#hidden-name-new-field").val(inputValue); // Sync hidden input
          });

          if (document.querySelectorAll('.mak-font-selector-normal').length) {
            MainProductData2();
          }

          $(".sticky-content-container").removeClass("no-sticky");
        }
      }
    });
  });
  cartObserver.observe({ entryTypes: ["resource"] });
}

observeCartChanges();


function changeSelectName(select, i) {
  select.forEach(function (option, index) {
    if (i <= index) {
      option.classList.remove("active");
      option.style.display = 'none';
    } else {
      option.classList.add("active");
      option.style.display = 'block';
    }
  })
}


function AddAnotherName(btn) {
  var index = parseInt(btn.parentElement.getAttribute("data-index"));

  var length = btn.closest(".option-selector__btns").querySelectorAll(".variant_old input").length

  if (btn.closest(".option-selector__btns").querySelector(".variant_old input:checked").nextElementSibling.nextElementSibling) {
    btn.closest(".option-selector__btns").querySelector(".variant_old input:checked").nextElementSibling.nextElementSibling.click();
    index++;
    btn.parentElement.setAttribute("data-index", index);
    changeSelectName(btn.closest(".option-selector__btns").querySelectorAll(".mak-iec-input-wrapper"), index);
    if (index == length) {
      btn.style.display = 'none';
      btn.nextElementSibling.style.display = 'block';
    }

    if (index > 1 && index != length) {
      btn.style.display = 'block';
      btn.nextElementSibling.style.display = 'block';
    }
  }
}

function RemoveName(btn) {
  var index = parseInt(btn.parentElement.getAttribute("data-index"));

  if (btn.closest(".option-selector__btns").querySelector(".variant_old input:checked").previousElementSibling.previousElementSibling) {
    btn.closest(".option-selector__btns").querySelector(".variant_old input:checked").previousElementSibling.previousElementSibling.click();
    index--;
    btn.parentElement.setAttribute("data-index", index);
    changeSelectName(btn.closest(".option-selector__btns").querySelectorAll(".mak-iec-input-wrapper"), index);

    if (index == 1) {
      btn.style.display = 'none';
      btn.previousElementSibling.style.display = 'block';
    }

    if (index > 1) {
      btn.style.display = 'block';
      btn.previousElementSibling.style.display = 'block';
    }
  }
}


var sliderswiper2 = new Swiper(".slider-swiper-2", {
  slidesPerView: 4,
  spaceBetween: 8,
  loop: false,
  navigation: {
    nextEl: ".section-featured-collection .slider-nav__btn.has-ltr-icon[name='next']",
    prevEl: ".section-featured-collection .slider-nav__btn.has-ltr-icon[name='prev']",
  },
  breakpoints: {
    0: {
      slidesPerView: 2.2,
      spaceBetween: 4,
    },
    768: {
      slidesPerView: 2.2,
      spaceBetween: 4,
    },
    1024: {
      slidesPerView: 4,
      spaceBetween: 8,
    }
  }
});


function textLimit() {

  var inputnewFields = document.querySelectorAll('#maktextInput'); // Select all input fields
  // var charCounts = document.querySelectorAll('#mak_font-limit'); // Select all character count elements

  if (inputnewFields.length > 0) {
    inputnewFields.forEach(function (inputField, index) {
      var maxLength = parseInt(inputField.getAttribute('maxlength'), 10) || 6; // Get max length safely
      var charCount = inputField.closest('.mak-iec-inner-text-wrapper').querySelector('#mak_font-limit'); // Get the matching character count element

      if (charCount) {
        charCount.textContent = '0/' + maxLength; // Initialize count display

        // Update character count when user types
        inputField.addEventListener('input', function () {
          charCount.textContent = inputField.value.length + '/' + maxLength;
        });
      }
    });
  } else {
    console.warn('No your-name or mak_font-limit elements found! Check if Liquid is rendering them correctly.');
  }

}


textLimit()

function clickOnAddToCartButton(e, image) {
  e.preventDefault();
  if (image.closest("#mak-cart-upsell")) e.stopPropagation();
  image.closest(".product-block").querySelector(".product-block__detail .btn").click();
}

function clickOnAddToCartButtonDrawer(e, image) {
  e.preventDefault();
  image.closest(".mak_product-item").querySelector(".free_pdp-btn .btn").click();
}

function updateGalleryImage(input) {
  var id = input.getAttribute("data-image-id");
  var galleryImages = input.closest(".mak__new__pdp-data").querySelectorAll(".main-pdp");
  var gallery = input.closest(".mak__new__pdp-data").querySelector(".product-main-slider");
  galleryImages.forEach(function (galleryImage, index) {
    var imageid = galleryImage.getAttribute("data-image-id");
    if (imageid == id) {
      $(gallery).slick('slickGoTo', index);
    }
  })
}

// Cart Recommendation: open the existing shared customize drawer immediately.
// No legacy quick-view popup, product-page fetch, or duplicate gallery scripts.
(function () {
  function openRecommendation(button) {
    if (!button || !button.closest('#mak-cart-upsell')) return false;
    var productId = String(button.getAttribute('data-id') || '');
    if (!/^\d+$/.test(productId)) return false;
    var template = document.querySelector(
      '#mak-cart-drawer template[data-mn-customize-template][data-mn-context="cart-recommendation"][data-pdp-id="' + productId + '"]'
    );
    if (!template || !window.mnSharedCustomizeDrawer?.openFromButton) {
      // Unconfigured automatic recommendations have no prepared template.
      // The ordinary product page is a safe fallback, not the removed quick view.
      var productUrl = button.getAttribute('data-url');
      if (productUrl) window.location.assign(productUrl);
      return false;
    }
    if (window.makCartRecommendationMode) return true;

    var trigger = document.createElement('button');
    trigger.dataset.pdpId = productId;
    trigger.dataset.mnContext = 'cart-recommendation';
    window.makCartPendingGiftItem = null;
    window.makCartRecommendationMode = true;
    document.documentElement.classList.add('mn-cart-gift-flow');
    try {
      if (!window.mnSharedCustomizeDrawer.openFromButton(trigger)) {
        window.makCartRecommendationMode = false;
        document.documentElement.classList.remove('mn-cart-gift-flow');
        return false;
      }
    } catch (error) {
      window.makCartRecommendationMode = false;
      document.documentElement.classList.remove('mn-cart-gift-flow');
      console.error('Cart Recommendation shared drawer failed:', error);
      return false;
    }
    return true;
  }
  window.mnOpenCartRecommendation = openRecommendation;
})();

// Three-step 1+1 → 2+2: replay the same drawer-open fill animation as 2+1.
(function () {
  document.addEventListener("on:cart-drawer:before-open", () => {
    const offer = document.querySelector("#mak-cart-footer .mn-cart-bogo-offer");
    if (!offer || offer.classList.contains("mak-progress-ajax")) return;
    offer.classList.remove("mak-progress-animate");
    void offer.offsetWidth;
    offer.classList.add("mak-progress-animate");
  });
  document.addEventListener("on:cart-drawer:before-close", () => {
    const offer = document.querySelector("#mak-cart-footer .mn-cart-bogo-offer");
    if (offer) offer.classList.remove("mak-progress-animate");
  });
})();
